import { useTheme } from "../ThemeContext";
import { useState, useEffect, useRef, useCallback } from "react";
import { fmt, STATUS, DONE_STATUS, valorReal } from "../theme";
import T from "../i18n";
import { post, get, put } from "../api";

// ─────────────────────────────────────────────────────────────
// helpers
// ─────────────────────────────────────────────────────────────
const DIAS_SEMANA = ["dom","seg","ter","qua","qui","sex","sab"];

/* Helper — extrai adicionais de um item do pedido
   Formato real da API: string "Leite Condensado (+R$ 0,00), Ovomaltine Em Pó (+R$ 0,00)" */
function getAdicionais(item) {
  if (!item) return [];
  let ads = item.adicionais || item.extras || "";

  // Se já for array (futuro), retorna direto
  if (Array.isArray(ads)) return ads;

  // Se for string formatada → parsear
  if (typeof ads === "string" && ads.trim()) {
    // Tenta JSON primeiro (caso futuro)
    if (ads.trim().startsWith("[")) { try { return JSON.parse(ads); } catch {} }

    // Parsear formato: "Nome (+R$ X,XX), Nome (+R$ X,XX)"
    // Separa por "), " e adiciona ")" de volta
    const partes = ads.split(/\),\s*/);
    return partes.map(function(p) {
      p = p.trim();
      if (!p) return null;
      if (!p.endsWith(")")) p += ")";
      // Extrair nome e preço: "Leite Condensado (+R$ 0,00)"
      var m = p.match(/^(.+?)\s*\(\+?R\$\s*([\d.,]+)\)$/);
      if (m) {
        var preco = parseFloat(m[2].replace(",", ".")) || 0;
        return { nome: m[1].trim(), preco: preco };
      }
      // Fallback: só o nome sem preço
      return { nome: p.replace(/\(.*\)/, "").trim() || p, preco: 0 };
    }).filter(Boolean);
  }

  return [];
}
/* Helper — observação limpa do item (ignora quando é igual aos adicionais) */
function getObsItem(item) {
  if (!item) return "";
  var obs = item.observacao_item || "";
  // Se observacao do item é igual ao campo adicionais, não duplicar
  if (!obs && item.observacao && item.adicionais && item.observacao !== item.adicionais) {
    // Tem observação mas é diferente dos adicionais = obs real do cliente
    return "";
  }
  return obs;
}

function isLojaOnlineAgora(loja) {
  if (!loja) return false;
  const agora = new Date();
  const diaSemana = DIAS_SEMANA[agora.getDay()];
  const dias = (loja.dias_funcionamento || "seg,ter,qua,qui,sex,sab,dom").split(",").map(d => d.trim());
  if (!dias.includes(diaSemana)) return false;
  const horaAtual = agora.getHours() * 60 + agora.getMinutes();
  const [ah, am] = (loja.horario_abre || "08:00").split(":").map(Number);
  const [fh, fm] = (loja.horario_fecha || "23:00").split(":").map(Number);
  return horaAtual >= ah * 60 + am && horaAtual < fh * 60 + fm;
}

function minutosDesde(ts) {
  if (!ts) return 0;
  return Math.floor((Date.now() - parseUTC(ts).getTime()) / 60000);
}

// MySQL retorna datetime sem timezone (ex: "2026-05-12 02:12:42").
// O servidor grava em UTC, mas o navegador interpreta como hora LOCAL.
// parseUTC() força a leitura como UTC, devolvendo Date convertido pro fuso do navegador.
function parseUTC(ts) {
  if (!ts) return new Date(NaN);
  if (ts instanceof Date) return ts;
  const s = String(ts);
  // Se já tem timezone (Z ou +/-HH:MM), deixa como está
  if (/Z$|[+-]\d{2}:?\d{2}$/.test(s)) return new Date(s);
  // Formato "YYYY-MM-DD HH:MM:SS" → trata como UTC
  return new Date(s.replace(" ", "T") + "Z");
}
function fmtLocalDateTime(ts) {
  if (!ts) return "";
  return parseUTC(ts).toLocaleString("pt-BR");
}
function fmtLocalTime(ts) {
  if (!ts) return "";
  return parseUTC(ts).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}
function timerCor(min) {
  if (min >= 6) return "#E8201A";
  if (min >= 3) return "#FACC15";
  return "#22C55E";
}
function timerPct(min, max = 10) {
  return Math.min(100, (min / max) * 100);
}

const PROGRESS_STEPS = [
  { key: "novo",                label: "Novo",        icon: "🔔" },
  { key: "aceito",              label: "Aceito",      icon: "✓" },
  { key: "buscando_entregador", label: "Entregador",  icon: "📦" },
  { key: "em_entrega",          label: "Em rota",     icon: "🛵" },
  { key: "entregue",            label: "Entregue",    icon: "✅" },
];
function progressIndex(status) {
  if (status === "pronto") return 2; // pronto = mesma fase que buscando_entregador
  if (status === "preparando") return 1; // preparando = mesma fase que aceito
  const idx = PROGRESS_STEPS.findIndex(s => s.key === status);
  return idx >= 0 ? idx : 0;
}

export default function Pedidos({ orders, onAdvance, onReject, onRefresh, lang, lojaId: _lojaIdProp, goTo }) {
  const lojaId = _lojaIdProp || (() => {
    try {
      for (const k of ["mixup_loja","loja","user","lojista","auth","session"]) {
        const raw = localStorage.getItem(k); if (!raw) continue;
        const u = JSON.parse(raw);
        const id = u?.loja_id || u?.id || u?.loja?.id;
        if (id) return id;
      }
    } catch {} return null;
  })();
  const { theme: C } = useTheme();
  const _ = (k) => T(lang, k);

  // ── estados ──
  const [selected, setSelected]         = useState(null);
  const [toast, setToast]               = useState(null);
  const [busca, setBusca]               = useState("");

  // ── loja / online ──
  const [loja, setLoja]                 = useState(null);
  const [lojaOnline, setLojaOnline]     = useState(false);
  const [togglingOnline, setTogglingOnline] = useState(false);

  // ── pausa temporária ──
  // pausaInfo = { expira: Date, minutosOriginal: 30|60|120 } | null
  const [pausaInfo, setPausaInfo]       = useState(null);
  const [showPausaMenu, setShowPausaMenu] = useState(false);

  // ── modo forçado (intervenção sem tempo definido — vale até próximo evento de horário) ──
  // forcadoInfo = { tipo: 'online'|'offline', proximoEvento: Date | null } | null
  const [forcadoInfo, setForcadoInfo]   = useState(null);

  // ── saldo ──
  const [saldo, setSaldo]               = useState(null);
  const [showSaldo, setShowSaldo]       = useState(false);
  const [recargaValor, setRecargaValor] = useState("");
  const [recargaLoading, setRecargaLoading] = useState(false);
  const [recargaPix, setRecargaPix]     = useState(null);
  const [recargaPolling, setRecargaPolling] = useState(false);
  const recargaPollRef = useRef(null);

  // ── comissão ──
  const [showComissao, setShowComissao] = useState(false);
  const [comPagLoading, setComPagLoading] = useState(false);
  const [comPix, setComPix] = useState(null);
  const [comPolling, setComPolling] = useState(false);
  const comPollRef = useRef(null);

  // ── vendas ──
  const [showVendas, setShowVendas]     = useState(false);

  // ── Som ──
  const somRef = useRef(null);
  const somIntervalRef = useRef(null);
  const pararSom = () => {
    if (somIntervalRef.current) { clearInterval(somIntervalRef.current); somIntervalRef.current = null; }
    if (somRef.current) { somRef.current.pause(); somRef.current.currentTime = 0; }
  };
  const tocarSom = () => {
    if (!somRef.current) return;
    somRef.current.currentTime = 0;
    somRef.current.play().catch(() => {});
    if (somIntervalRef.current) clearInterval(somIntervalRef.current);
    somIntervalRef.current = setInterval(() => {
      if (somRef.current) { somRef.current.currentTime = 0; somRef.current.play().catch(() => {}); }
    }, 4000);
  };

  // ── auto-aceite ──
  const [autoAceite, setAutoAceite] = useState(() => localStorage.getItem("mixup_auto_aceite") === "1");

  // ── Impressão ──
  const [silentPrint, setSilentPrint] = useState(() => localStorage.getItem("mixup_silent_print") === "1");
  const [showPrintCfg, setShowPrintCfg] = useState(false);
  const silentIframeRef = useRef(null);
  const silentIframe2Ref = useRef(null);

  // Configuração de impressoras (salva em localStorage como JSON)
  const [printCfg, setPrintCfg] = useState(() => {
    try { return JSON.parse(localStorage.getItem("mixup_print_cfg")) || {}; } catch { return {}; }
  });
  const savePrintCfg = (cfg) => { setPrintCfg(cfg); localStorage.setItem("mixup_print_cfg", JSON.stringify(cfg)); };
  // printCfg = { caixaNome, cozinhaNome, cozinhaIP, cozinhaPorta:"9100", agentUrl:"http://localhost:9111" }
  // (cozinhaMode foi MIGRADO para o banco — loja.impressao_modo. vias_impressao também vem do banco.)

  // ── Chat ──
  const [showChat, setShowChat]         = useState(false);
  const [chatPedido, setChatPedido]     = useState(null);
  const [showChatEnt, setShowChatEnt]   = useState(false);
  const [chatEntPedido, setChatEntPedido] = useState(null);
  const [chatEntConversa, setChatEntConversa] = useState(null);
  const [chatEntMsgs, setChatEntMsgs]   = useState([]);
  const [chatEntTexto, setChatEntTexto] = useState("");
  const [chatEntLoading, setChatEntLoading] = useState(false);
  const [chatConversa, setChatConversa] = useState(null);
  const [chatMsgs, setChatMsgs]         = useState([]);
  const [chatTexto, setChatTexto]       = useState("");
  const [chatLoading, setChatLoading]   = useState(false);
  const [chatNaoLidos, setChatNaoLidos] = useState({});
  const [chatNaoLidosEnt, setChatNaoLidosEnt] = useState({});
  const chatScrollRef                   = useRef(null);
  const chatEntScrollRef                = useRef(null);
  const chatEntConversaRef              = useRef(null);
  const chatEntUltimaRef                = useRef(null);
  const chatEntPollRef                  = useRef(null);
  const chatUltimaRef                   = useRef(null);
  const chatConversaRef                 = useRef(null);
  const chatPollRef                     = useRef(null);

  // Detectar novos pedidos
  const prevOrdersRef = useRef([]);

  // ── NOTIFICAÇÃO VISUAL DE PEDIDO NOVO (Electron PDV) ──
  // Dispara notificação Windows nativa + pisca ícone na taskbar quando chega
  // pedido e o programa está minimizado / atrás de outras janelas.
  // No navegador comum (sem Electron) usa Notification API web.
  // Não interfere no som que já é tocado por tocarSom().
  const notificarPedidoNovo = (qtdNovos) => {
    try {
      const isElectronPDV = typeof window !== "undefined" && !!window.__ELECTRON_PDV__;
      const titulo = qtdNovos > 1
        ? `${qtdNovos} pedidos novos!`
        : "Novo pedido recebido!";
      const corpo = "Clique para abrir o MixUp PDV";

      // 1. Pisca taskbar (apenas Electron — IPC handler em main.js)
      if (isElectronPDV && window.pdvElectron?.app?.flashTaskbar) {
        window.pdvElectron.app.flashTaskbar().catch(() => {});
      }

      // 2. Notificação Windows nativa (funciona no Electron e no Chrome moderno)
      if (typeof Notification !== "undefined" && Notification.permission === "granted") {
        const n = new Notification(titulo, {
          body: corpo,
          icon: "/mixup-lojista/logo192.png",
          tag: "pedido-novo",          // substitui notificação anterior em vez de empilhar
          requireInteraction: false,
          silent: true                 // o som já é tocado pelo tocarSom() — evita duplicar
        });
        n.onclick = () => {
          try {
            if (isElectronPDV && window.pdvElectron?.app?.showWindow) {
              window.pdvElectron.app.showWindow();
            } else {
              window.focus();
            }
            n.close();
          } catch (e) {}
        };
      }
    } catch (e) {
      // Notificação falhou — não impacta nada (som e impressão continuam OK)
    }
  };

  // Pedir permissão de notificação 1x ao montar (Electron já vem como granted)
  useEffect(() => {
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  useEffect(() => {
    const prevIds = new Set(prevOrdersRef.current.map(o => o.id));
    const novos = orders.filter(o => o.status === "novo" && !prevIds.has(o.id));
    if (novos.length > 0) {
      tocarSom();
      notificarPedidoNovo(novos.length);
      if (autoAceite) {
        // Auto-aceite ON → aceita sozinho
        // Print ON também → aceita E imprime
        // Print OFF → só aceita, sem imprimir
        novos.forEach(o => {
          setTimeout(() => {
            onAdvance(o.id);
            if (silentPrint) imprimirPedido(o);
          }, 800);
        });
      }
    }
    if (!orders.some(o => o.status === "novo")) pararSom();
    prevOrdersRef.current = orders;
  }, [orders]);

  const toggleAutoAceite = () => {
    const novo = !autoAceite;
    setAutoAceite(novo);
    localStorage.setItem("mixup_auto_aceite", novo ? "1" : "0");
    notify(novo ? "Auto-aceite ativado" : "Auto-aceite desativado", novo);
  };

  // ── Chat functions ──
  const abrirChat = async (pedido) => {
    markInteracting(); setChatPedido(pedido); setChatMsgs([]);
    chatUltimaRef.current = null; chatConversaRef.current = null;
    setShowChat(true); setChatLoading(true);
    try {
      const r = await fetch(`https://mixupbr.com.br/api/chat/?acao=conversa`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ participante_a_tipo:"loja", participante_a_id:lojaId, participante_b_tipo:"cliente", participante_b_id:pedido.cliente_id, pedido_id:pedido.id }),
      });
      const d = await r.json();
      if (d.ok) {
        setChatConversa(d.conversa); chatConversaRef.current = d.conversa;
        const r2 = await fetch(`https://mixupbr.com.br/api/chat/?acao=mensagens&conversa_id=${d.conversa.id}&marcar_lido=loja:${lojaId}`);
        const d2 = await r2.json();
        if (d2.ok) { setChatMsgs(d2.mensagens || []); if (d2.mensagens?.length) chatUltimaRef.current = d2.mensagens[d2.mensagens.length-1].criado_em; }
      }
    } catch {} setChatLoading(false);
    clearInterval(chatPollRef.current);
    chatPollRef.current = setInterval(async () => {
      if (!chatConversaRef.current?.id) return;
      try {
        const r = await fetch(`https://mixupbr.com.br/api/chat/?acao=mensagens&conversa_id=${chatConversaRef.current.id}&marcar_lido=loja:${lojaId}${chatUltimaRef.current ? "&desde="+encodeURIComponent(chatUltimaRef.current) : ""}`);
        const d = await r.json();
        if (d.ok && d.mensagens?.length > 0) {
          setChatMsgs(prev => { const ids = new Set(prev.map(m=>String(m.id))); const n = d.mensagens.filter(m=>!ids.has(String(m.id))); return n.length ? [...prev,...n] : prev; });
          chatUltimaRef.current = d.mensagens[d.mensagens.length-1].criado_em;
          setTimeout(() => chatScrollRef.current?.scrollTo({ top:99999, behavior:"smooth" }), 80);
        }
      } catch {}
    }, 3000);
  };
  const fecharChat = () => { setShowChat(false); clearInterval(chatPollRef.current); chatConversaRef.current = null; chatUltimaRef.current = null; };

  const abrirChatEnt = async (pedido) => {
    setChatEntPedido(pedido); setChatEntMsgs([]); chatEntConversaRef.current = null; chatEntUltimaRef.current = null;
    setShowChatEnt(true); setChatEntLoading(true);
    try {
      const r = await fetch(`https://mixupbr.com.br/api/chat/?acao=conversa`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ participante_a_tipo:"loja", participante_a_id:lojaId, participante_b_tipo:"entregador", participante_b_id:pedido.entregador_id, pedido_id:pedido.id }),
      });
      const d = await r.json();
      if (d.ok) {
        setChatEntConversa(d.conversa); chatEntConversaRef.current = d.conversa;
        const r2 = await fetch(`https://mixupbr.com.br/api/chat/?acao=mensagens&conversa_id=${d.conversa.id}&marcar_lido=loja:${lojaId}`);
        const d2 = await r2.json();
        if (d2.ok) { setChatEntMsgs(d2.mensagens || []); if (d2.mensagens?.length) chatEntUltimaRef.current = d2.mensagens[d2.mensagens.length-1].criado_em; }
      }
    } catch {} setChatEntLoading(false);
    clearInterval(chatEntPollRef.current);
    chatEntPollRef.current = setInterval(async () => {
      if (!chatEntConversaRef.current?.id) return;
      try {
        const desde = chatEntUltimaRef.current ? "&desde="+encodeURIComponent(chatEntUltimaRef.current) : "";
        const r = await fetch(`https://mixupbr.com.br/api/chat/?acao=mensagens&conversa_id=${chatEntConversaRef.current.id}&marcar_lido=loja:${lojaId}${desde}`);
        const d = await r.json();
        if (d.ok && d.mensagens?.length > 0) {
          setChatEntMsgs(prev => { const ids = new Set(prev.map(m=>String(m.id))); const n = d.mensagens.filter(m=>!ids.has(String(m.id))); return n.length ? [...prev,...n] : prev; });
          chatEntUltimaRef.current = d.mensagens[d.mensagens.length-1].criado_em;
          setTimeout(() => chatEntScrollRef.current?.scrollTo({ top:99999, behavior:"smooth" }), 80);
        }
      } catch {}
    }, 3000);
  };
  const fecharChatEnt = () => { setShowChatEnt(false); clearInterval(chatEntPollRef.current); chatEntConversaRef.current = null; chatEntUltimaRef.current = null; };

  const enviarMsgChat = async () => {
    const txt = chatTexto.trim(); if (!txt || !chatConversaRef.current) return;
    const tempId = "tmp_"+Date.now(); setChatTexto("");
    setChatMsgs(prev => [...prev, { id:tempId, remetente_tipo:"loja", remetente_id:lojaId, remetente_nome:"Loja", tipo:"texto", mensagem:txt, criado_em:new Date().toISOString(), lido:0 }]);
    setTimeout(() => chatScrollRef.current?.scrollTo({ top:99999, behavior:"smooth" }), 80);
    try {
      const res = await fetch(`https://mixupbr.com.br/api/chat/?acao=enviar`, { method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ conversa_id:chatConversaRef.current.id, remetente_tipo:"loja", remetente_id:lojaId, remetente_nome:"Loja", tipo:"texto", mensagem:txt }) });
      const d = await res.json();
      if (d.ok && d.mensagem) { setChatMsgs(prev => prev.map(m => m.id===tempId ? d.mensagem : m)); chatUltimaRef.current = d.mensagem.criado_em; }
    } catch {}
  };
  const enviarMsgChatEnt = async () => {
    const txt = chatEntTexto.trim(); if (!txt || !chatEntConversaRef.current) return;
    const tempId = "tmp_"+Date.now(); setChatEntTexto("");
    setChatEntMsgs(prev => [...prev, { id:tempId, remetente_tipo:"loja", remetente_id:lojaId, remetente_nome:"Loja", tipo:"texto", mensagem:txt, criado_em:new Date().toISOString(), lido:0 }]);
    setTimeout(() => chatEntScrollRef.current?.scrollTo({ top:99999, behavior:"smooth" }), 80);
    try {
      const res = await fetch(`https://mixupbr.com.br/api/chat/?acao=enviar`, { method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ conversa_id:chatEntConversaRef.current.id, remetente_tipo:"loja", remetente_id:lojaId, remetente_nome:"Loja", tipo:"texto", mensagem:txt }) });
      const d = await res.json();
      if (d.ok && d.mensagem) { setChatEntMsgs(prev => prev.map(m => m.id===tempId ? d.mensagem : m)); chatEntUltimaRef.current = d.mensagem.criado_em; }
    } catch {}
  };

  // Polling não lidos
  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const r = await fetch(`https://mixupbr.com.br/api/chat/?tipo=loja&id=${lojaId}`);
        const d = await r.json();
        if (d.ok) {
          const mapCli = {}, mapEnt = {};
          (d.conversas||[]).forEach(c => {
            if (c.pedido_id && c.nao_lidos > 0) {
              const outro = c.participante_a_tipo === "loja" ? c.participante_b_tipo : c.participante_a_tipo;
              if (outro === "entregador") mapEnt[c.pedido_id] = (mapEnt[c.pedido_id]||0) + parseInt(c.nao_lidos);
              else mapCli[c.pedido_id] = (mapCli[c.pedido_id]||0) + parseInt(c.nao_lidos);
            }
          });
          setChatNaoLidos(mapCli); setChatNaoLidosEnt(mapEnt);
        }
      } catch {}
    }, 5000);
    return () => clearInterval(t);
  }, [lojaId]);

  // ── Impressão ──
  // CSS térmica — papel físico 80mm, área imprimível 72mm
  // @page 80mm = navegador vê o papel "real" e não escala
  // body 72mm = conteúdo cabe na área imprimível da Elgin i8
  // Tudo em PRETO sólido (#000) e BOLD (font-weight:700) — térmicas precisam de contraste alto
  const CSS_TERMICA = `@page{margin:0;size:80mm auto}*{margin:0;padding:0;box-sizing:border-box}html{width:80mm}body{font-family:'Courier New',Courier,monospace;font-size:11px;color:#000;background:#fff;padding:0;margin:0;line-height:1.3;font-weight:700;width:72mm;max-width:72mm}.wrap{padding:2px 3px}.center{text-align:center}.logo{font-size:18px;font-weight:900;letter-spacing:1px}.lojaNome{font-size:11px;font-weight:900;margin-top:1px;letter-spacing:.3px}.sub{font-size:9px;color:#000;margin-bottom:4px;font-weight:700}.sep{border:none;border-top:1px dashed #000;margin:5px 0}.sep2{border-top:2px solid #000;margin:5px 0}.lbl{font-size:9px;color:#000;font-weight:900;margin-top:3px;text-transform:uppercase}.val{font-size:11px;font-weight:900;margin-bottom:2px}.big{font-size:14px;font-weight:900}table{width:100%;border-collapse:collapse}td{padding:1px 0;vertical-align:top;font-size:11px;font-weight:700}.r{text-align:right;white-space:nowrap;font-weight:900}.adi{padding-left:8px;font-size:10px;color:#000;font-weight:700}.obs{font-style:italic;color:#000;font-weight:700}.tot td{font-size:13px;font-weight:900;padding-top:3px}.badge{display:inline-block;padding:1px 5px;border:2px solid #000;border-radius:3px;font-size:9px;font-weight:900;margin:2px 2px 2px 0}@media print{body{padding:0;margin:0;width:72mm}html{width:80mm}}`;

  // ═══ Geradores de TEXTO PURO (modo Agent — PowerShell Out-Printer) ═══
  const LARGURA_TXT = 42;
  const linhaTxt = (esq, dir = "") => {
    const livre = LARGURA_TXT - esq.length - dir.length;
    return esq + (livre > 0 ? " ".repeat(livre) : " ") + dir;
  };
  const sepTxt = "-".repeat(LARGURA_TXT);
  const sepFortTxt = "=".repeat(LARGURA_TXT);
  const centerTxt = (s) => {
    if (s.length >= LARGURA_TXT) return s;
    const pad = Math.floor((LARGURA_TXT - s.length) / 2);
    return " ".repeat(pad) + s;
  };

  // Texto do CAIXA — nota completa
  const gerarTextoCaixa = (p) => {
    const clienteNome = p.cliente_nome || (p.observacao?.match(/Cliente: ([^•]+)/)?.[1]?.trim() || "—");
    const clienteTel  = p.cliente_telefone || (p.observacao?.match(/Tel: ([^•\n]+)/)?.[1]?.trim() || "—");
    const obsLimpa = (p.observacao || "").replace(/\[RETIRADA NA LOJA\]/g, "").replace(/\[PAGO ONLINE\]/g, "").replace(/Cliente:[^•]+/g, "").replace(/Tel:[^•\n]+/g, "").replace(/•/g, "").trim();
    const cashback = Number(p.cashback_usado) || 0;

    const linhas = [];
    linhas.push(centerTxt("MixUp"));
    if (loja?.nome) linhas.push(centerTxt(loja.nome));
    linhas.push(centerTxt(new Date().toLocaleString("pt-BR")));
    linhas.push(sepFortTxt);
    linhas.push(`PEDIDO #${p.codigo}`);
    linhas.push(sepTxt);
    linhas.push(`CLIENTE: ${clienteNome}`);
    linhas.push(`TELEFONE: ${clienteTel}`);
    linhas.push(`ENDERECO:`);
    linhas.push(`  ${p.endereco_entrega || "—"}`);
    if (p.observacao?.includes("[RETIRADA NA LOJA]")) linhas.push(">>> RETIRADA NA LOJA <<<");
    if (p.observacao?.includes("[PAGO ONLINE]")) linhas.push(">>> PAGO ONLINE <<<");
    if (obsLimpa) { linhas.push(`OBS: ${obsLimpa}`); }
    linhas.push(sepFortTxt);
    linhas.push("ITENS:");
    (p.itens || []).forEach(it => {
      const qtd = Number(it.quantidade || 1);
      const nome = it.nome || "";
      const preco = `R$ ${Number(it.preco_unitario || it.preco || 0).toFixed(2).replace(".", ",")}`;
      linhas.push(linhaTxt(`${qtd}x ${nome}`.slice(0, LARGURA_TXT - preco.length - 1), preco));
      // adicionais (parseia JSON string ou array)
      getAdicionais(it).forEach(ad => {
        const adNome = ad.nome || "";
        const adPreco = Number(ad.preco || ad.valor || 0);
        const adStr = adPreco > 0 ? `+R$ ${adPreco.toFixed(2).replace(".", ",")}` : "";
        linhas.push(linhaTxt(`  + ${adNome}`, adStr));
      });
      const obsIt = getObsItem(it);
      if (obsIt) linhas.push(`  obs: ${obsIt}`);
    });
    linhas.push(sepFortTxt);
    linhas.push(linhaTxt("Subtotal", `R$ ${Number(p.subtotal || 0).toFixed(2).replace(".", ",")}`));
    const tx = Number(p.taxa_entrega || 0);
    linhas.push(linhaTxt("Entrega", tx > 0 ? `R$ ${tx.toFixed(2).replace(".", ",")}` : "GRATIS"));
    const txServ = Number(p.taxa_servico || 0);
    if (txServ > 0) linhas.push(linhaTxt("Taxa servico", `R$ ${txServ.toFixed(2).replace(".", ",")}`));
    if (cashback > 0) linhas.push(linhaTxt("Cashback", `- R$ ${cashback.toFixed(2).replace(".", ",")}`));
    linhas.push(sepTxt);
    linhas.push(linhaTxt("TOTAL", `R$ ${Number(p.total || 0).toFixed(2).replace(".", ",")}`));
    linhas.push(sepFortTxt);
    linhas.push(`PAGAMENTO: ${(p.forma_pagamento || "—").toUpperCase()}`);
    if (Number(p.troco_para || 0) > 0) linhas.push(`TROCO PARA: R$ ${Number(p.troco_para).toFixed(2).replace(".", ",")}`);
    linhas.push(sepTxt);
    linhas.push(centerTxt("Obrigado pela preferencia!"));
    return linhas.join("\n");
  };

  // Texto da COZINHA — só itens
  const gerarTextoCozinha = (p) => {
    const clienteNome = p.cliente_nome || (p.observacao?.match(/Cliente: ([^•]+)/)?.[1]?.trim() || "—");
    const obsLimpa = (p.observacao || "").replace(/\[RETIRADA NA LOJA\]/g, "").replace(/\[PAGO ONLINE\]/g, "").replace(/Cliente:[^•]+/g, "").replace(/Tel:[^•\n]+/g, "").replace(/•/g, "").trim();

    const linhas = [];
    if (loja?.nome) linhas.push(centerTxt(loja.nome));
    linhas.push(sepFortTxt);
    linhas.push(centerTxt(">>> COZINHA <<<"));
    linhas.push(sepFortTxt);
    linhas.push(`#${p.codigo}   ${new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`);
    linhas.push(`Cliente: ${clienteNome}`);
    if (p.observacao?.includes("[RETIRADA NA LOJA]")) linhas.push(">>> RETIRADA NA LOJA <<<");
    linhas.push(sepFortTxt);
    (p.itens || []).forEach(it => {
      const qtd = Number(it.quantidade || 1);
      linhas.push(`${qtd}x ${it.nome || ""}`.toUpperCase());
      getAdicionais(it).forEach(ad => linhas.push(`  + ${ad.nome || ""}`));
      const obsIt = getObsItem(it);
      if (obsIt) linhas.push(`  >> ${obsIt}`);
    });
    linhas.push(sepFortTxt);
    if (obsLimpa) linhas.push(`OBS GERAL: ${obsLimpa}`);
    const totalItens = (p.itens || []).reduce((s, i) => s + Number(i.quantidade || 1), 0);
    linhas.push(centerTxt(`${totalItens} item(ns)`));
    return linhas.join("\n");
  };

  const gerarItensHtml = (itens) => (itens||[]).map(it => {
    let html = `<tr><td><b>${it.quantidade}x ${it.nome}</b></td><td class="r">${fmt(it.preco_total||0)}</td></tr>`;
    const ads = getAdicionais(it);
    if (ads.length > 0) ads.forEach(ad => {
      const adPreco = Number(ad.preco || ad.valor || 0);
      html += `<tr><td class="adi">  + ${ad.nome || "Adicional"}${adPreco > 0 ? " (R$ "+adPreco.toFixed(2).replace(".",",")+")" : ""}</td><td></td></tr>`;
    });
    const obsIt = getObsItem(it);
    if (obsIt) html += `<tr><td class="adi obs">  * ${obsIt}</td><td></td></tr>`;
    return html;
  }).join("");

  /* ═══ NOTA DO CAIXA — completa ═══ */
  const gerarHtmlCaixa = (p) => {
    if (!p) return "";
    const clienteNome = p.cliente_nome || p.observacao?.match(/Cliente: ([^•]+)/)?.[1]?.trim() || `#${p.cliente_id}`;
    const clienteTel = p.telefone_entrega || p.observacao?.match(/Tel: ([^•]+)/)?.[1]?.trim() || "—";
    const obs = p.observacao?.includes("[ENTREGA AVULSA]") ? p.observacao.replace("[ENTREGA AVULSA] ","") : (p.observacao || "");
    const obsLimpa = obs && !obs.includes("Cliente:") ? obs : "";
    const cashback = Number(p.cashback_usado||0);
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>#${p.codigo}</title><style>${CSS_TERMICA}</style></head><body>
<div class="wrap">
<div class="center"><div class="logo">MixUp</div>${loja?.nome ? `<div class="lojaNome">${loja.nome}</div>` : ""}<div class="sub">${new Date().toLocaleString("pt-BR")}</div></div>
<hr class="sep2">
<div class="lbl">PEDIDO</div><div class="val big">#${p.codigo}</div>
<hr class="sep">
<div class="lbl">CLIENTE</div><div class="val">${clienteNome}</div>
<div class="lbl">TELEFONE</div><div class="val">${clienteTel}</div>
<div class="lbl">ENDEREÇO</div><div class="val">${p.endereco_entrega || "—"}</div>
${p.observacao?.includes("[RETIRADA NA LOJA]") ? '<div class="badge">RETIRADA NA LOJA</div>' : ""}
${p.observacao?.includes("[PAGO ONLINE]") ? '<div class="badge">PAGO ONLINE</div>' : ""}
${obsLimpa ? `<div class="lbl">OBS</div><div class="val">${obsLimpa}</div>` : ""}
<hr class="sep2">
<div class="lbl" style="margin-bottom:4px">ITENS</div>
<table>${gerarItensHtml(p.itens)}</table>
<hr class="sep2">
<table>
  <tr><td>Subtotal</td><td class="r">${fmt(p.subtotal)}</td></tr>
  <tr><td>Entrega</td><td class="r">${Number(p.taxa_entrega||0) > 0 ? fmt(p.taxa_entrega) : "GRÁTIS"}</td></tr>
  ${Number(p.taxa_servico||0) > 0 ? `<tr><td>Taxa de serviço</td><td class="r">${fmt(p.taxa_servico)}</td></tr>` : ""}
  ${cashback > 0 ? `<tr><td>Cashback</td><td class="r">- ${fmt(p.cashback_usado)}</td></tr>` : ""}
  <tr class="tot"><td>TOTAL</td><td class="r">${fmt(p.total)}</td></tr>
</table>
<hr class="sep2">
<div class="lbl">PAGAMENTO</div><div class="val big">${(p.forma_pagamento||"—").toUpperCase()}</div>
${Number(p.troco_para||0) > 0 ? `<div class="lbl">TROCO PARA</div><div class="val">R$ ${Number(p.troco_para).toFixed(2).replace(".",",")}</div>` : ""}
<hr class="sep">
<div class="center sub" style="margin-top:6px">Obrigado pela preferência!</div>
</div>
</body></html>`;
  };

  /* ═══ NOTA DA COZINHA — só itens, fonte grande, sem valores ═══ */
  const gerarHtmlCozinha = (p) => {
    if (!p) return "";
    const clienteNome = p.cliente_nome || p.observacao?.match(/Cliente: ([^•]+)/)?.[1]?.trim() || `#${p.cliente_id}`;
    const obsLimpa = p.observacao && !p.observacao.includes("Cliente:") && !p.observacao.includes("[ENTREGA AVULSA]") ? p.observacao : "";
    const itensHtml = (p.itens||[]).map(it => {
      let html = `<tr><td style="font-size:16px;font-weight:900;padding:6px 0;border-bottom:1px dashed #aaa">${it.quantidade}x ${it.nome}</td></tr>`;
      const ads = getAdicionais(it);
      if (ads.length > 0) ads.forEach(ad => {
        html += `<tr><td style="padding-left:16px;font-size:13px;font-weight:600;color:#333">  → ${ad.nome || "Adicional"}</td></tr>`;
      });
      const obsIt = getObsItem(it);
      if (obsIt) html += `<tr><td style="padding-left:16px;font-size:12px;font-style:italic;color:#555">  * ${obsIt}</td></tr>`;
      return html;
    }).join("");
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>COZ #${p.codigo}</title>
<style>@page{margin:0;size:80mm auto}*{margin:0;padding:0;box-sizing:border-box}html{width:80mm}body{font-family:'Courier New',Courier,monospace;font-size:13px;color:#000;background:#fff;padding:0;margin:0;font-weight:700;width:72mm;max-width:72mm}.wrap{padding:2px 3px}table{width:100%;border-collapse:collapse}@media print{body{padding:0;margin:0;width:72mm}html{width:80mm}}</style></head><body>
<div class="wrap">
${loja?.nome ? `<div style="text-align:center;font-size:11px;font-weight:900;margin-bottom:3px">${loja.nome}</div>` : ""}
<div style="text-align:center;font-size:20px;font-weight:900;border:3px solid #000;padding:6px;margin-bottom:5px">★ COZINHA ★</div>
<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:3px">
  <div style="font-size:22px;font-weight:900">#${p.codigo}</div>
  <div style="font-size:10px;color:#000;font-weight:900">${new Date().toLocaleTimeString("pt-BR",{hour:"2-digit",minute:"2-digit"})}</div>
</div>
<div style="font-size:12px;font-weight:900;margin-bottom:2px">${clienteNome}</div>
${p.observacao?.includes("[RETIRADA NA LOJA]") ? '<div style="font-size:13px;font-weight:900;border:2px solid #000;text-align:center;padding:3px;margin:3px 0">⚠ RETIRADA NA LOJA</div>' : ""}
<hr style="border:none;border-top:2px solid #000;margin:5px 0">
<table>${itensHtml}</table>
<hr style="border:none;border-top:2px solid #000;margin:5px 0">
${obsLimpa ? `<div style="font-size:11px;font-weight:900;margin-bottom:3px">OBS: ${obsLimpa}</div><hr style="border:none;border-top:1px dashed #000;margin:3px 0">` : ""}
<div style="text-align:center;font-size:9px;color:#000;font-weight:900">Pedido ${(p.itens||[]).reduce((s,i)=>s+Number(i.quantidade||1),0)} item(ns)</div>
</div>
</body></html>`;
  };

  /* ═══ Funções de impressão por método ═══ */
  // Imprime via iframe oculto (CAIXA — usa impressora padrão do navegador/Chrome)
  // Para silencioso 100%: usuário precisa configurar Chrome com --kiosk-printing OU desativar prompt no chrome://flags
  // Repete N vezes via setTimeout (vias_impressao da loja)
  const imprimirViaIframe = (html, iframeRef, vias = 1) => {
    const total = Math.max(1, Math.min(5, parseInt(vias) || 1));
    const fazerUmaVia = () => {
      try {
        let iframe = iframeRef.current;
        if (!iframe) {
          iframe = document.createElement("iframe");
          iframe.style.cssText = "position:fixed;top:-9999px;left:-9999px;width:80mm;height:0;border:none;visibility:hidden";
          document.body.appendChild(iframe);
          iframeRef.current = iframe;
        }
        const doc = iframe.contentDocument || iframe.contentWindow.document;
        doc.open(); doc.write(html); doc.close();
        setTimeout(() => { try { iframe.contentWindow.print(); } catch(e) { console.error("Print err:", e); } }, 300);
      } catch(e) {
        const w = window.open("","_blank","width=320,height=600");
        w.document.write(html); w.document.close();
        setTimeout(() => { w.print(); w.close(); }, 400);
      }
    };
    for (let i = 0; i < total; i++) {
      setTimeout(fazerUmaVia, i * 1200); // 1.2s entre vias
    }
  };

  const imprimirViaPopup = (html, vias = 1) => {
    const total = Math.max(1, Math.min(5, parseInt(vias) || 1));
    for (let i = 0; i < total; i++) {
      setTimeout(() => {
        const w = window.open("","_blank","width=320,height=600");
        if (w) { w.document.write(html); w.document.close(); setTimeout(() => { w.print(); w.close(); }, 400); }
      }, i * 1500);
    }
  };

  // Modo Rede — TCP socket porta 9100 via API PHP (faz N vias server-side)
  const imprimirViaRede = async (html, ip, porta, vias = 1, pedidoId = 0) => {
    try {
      const r = await fetch("https://mixupbr.com.br/api/impressora/", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ip, porta: porta || "9100", html,
          loja_id: lojaId || 1,
          pedido_id: pedidoId || 0,
          vias: vias || 1,
          tipo: "cozinha"
        })
      });
      const d = await r.json();
      if (!d.ok) { notify("Erro impressora rede: " + (d.erro || ""), false); return false; }
      return true;
    } catch(e) { notify("Falha conexão impressora rede", false); return false; }
  };

  // Modo PDV Desktop — servidor :3456 com impressão multi-destino
  // tipo: "recibo" (caixa) | "comanda" (cozinha) | "bar"
  // O servidor PDV gerencia vias e envio (USB ou TCP) baseado na config de cada destino
  const isPDVDesktop = () => typeof window !== "undefined" && (window.__ELECTRON_PDV__ === true || !!window.__PRINT_SERVER__);

  const imprimirViaPDV = async (pedido, tipo = "recibo") => {
    if (!pedido) return false;
    const baseUrl = window.__PRINT_SERVER__ || "http://localhost:3456";
    const endpoint = tipo === "comanda" ? "/imprimir" : tipo === "bar" ? "/bar" : "/recibo";
    const url = baseUrl + endpoint;

    // Monta dados estruturados que o print-server v4.0 espera
    const dadosBase = {
      codigo: pedido.codigo,
      data: new Date().toLocaleString("pt-BR"),
      operador: pedido.cliente_nome || "—",
      garcom: pedido.cliente_nome || "—",
      info: `Pedido #${pedido.codigo}`,
      total: Number(pedido.total || 0),
      loja_nome: loja?.nome || "",
      loja_endereco: loja?.endereco || "",
      loja_telefone: loja?.telefone || loja?.whatsapp || "",
      loja_cnpj: loja?.cnpj || "",
      cliente_nome: pedido.cliente_nome || "",
      cliente_telefone: pedido.telefone_entrega || pedido.cliente_telefone || "",
      endereco: pedido.endereco_entrega || ""
    };

    // Helper: monta itens com adicionais
    const montarItensComAdis = (incluirPreco) => (pedido.itens || []).map(it => {
      const ads = getAdicionais(it);
      const adicionais = ads.map(ad => ({
        nome: ad.nome || "Adicional",
        preco: Number(ad.preco || ad.valor || 0),
        qtd: Number(ad.qtd || 1)
      }));
      const obsIt = getObsItem(it);
      const qtd = Number(it.quantidade || 1);
      const precoUnit = Number(it.preco_unitario || it.preco || 0);
      if (incluirPreco) {
        return {
          nome: it.nome || "",
          quantidade: qtd,
          preco: precoUnit,
          subtotal: precoUnit * qtd,
          adicionais: adicionais,
          observacao: obsIt || ""
        };
      }
      return {
        qtd: qtd,
        nome: it.nome || "",
        adicionais: adicionais,
        obs: obsIt || ""
      };
    });

    let dados;
    if (tipo === "recibo") {
      // Caixa — recibo completo com valores
      dados = {
        ...dadosBase,
        itens: montarItensComAdis(true),
        subtotal: Number(pedido.subtotal || 0),
        taxa_entrega: Number(pedido.taxa_entrega || 0),
        desconto: Number(pedido.cashback_usado || 0),
        cashback_usado: Number(pedido.cashback_usado || 0),
        forma_pagamento: (pedido.forma_pagamento || "—").toUpperCase(),
        valor_recebido: Number(pedido.valor_recebido || 0),
        troco: Number(pedido.troco || pedido.troco_para || 0)
      };
    } else {
      // Cozinha/Bar — só itens (sem valores)
      dados = {
        ...dadosBase,
        itens: montarItensComAdis(false)
      };
    }

    try {
      const r = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(dados)
      });
      const d = await r.json();
      if (!d.ok) {
        notify(`Erro PDV (${tipo}): ${d.erro || ""}`, false);
        return false;
      }
      if (d.ignorado) {
        // Destino desativado — não é erro, apenas pulou
        return false;
      }
      return true;
    } catch(e) {
      notify("PDV não respondeu — servidor 3456 offline?", false);
      return false;
    }
  };

  const imprimirViaAgent = async (texto, agentUrl, vias = 1, destino = "cozinha") => {
    try {
      const r = await fetch((agentUrl || "http://localhost:9111") + "/print", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ texto, destino, vias: vias || 1 })
      });
      const d = await r.json();
      if (!d.ok) { notify(`Erro Agent (${destino}): ${d.error || ""}`, false); return false; }
      if (d.ignorado) { notify(`Destino "${destino}" desativado no Agent`, false); return false; }
      return true;
    } catch(e) { notify("MixUp Agent não encontrado em " + (agentUrl || "localhost:9111"), false); return false; }
  };

  /* ═══ ORQUESTRADOR — imprime no caixa e/ou cozinha ═══ */
  const imprimirPedido = (pedido) => {
    const p = pedido || sel; if (!p) return;
    // Entrega avulsa: NÃO imprime (só pedidos do app cliente / catálogo)
    if (p.observacao && p.observacao.includes("[ENTREGA AVULSA]")) return;
    const htmlCaixa = gerarHtmlCaixa(p);
    // Modo da impressão vem do BANCO (loja.impressao_modo) — admin define
    const modo = (loja?.impressao_modo) || "off";
    // Vias vem do BANCO (loja.vias_impressao) — admin define
    const vias = Math.max(1, Math.min(5, parseInt(loja?.vias_impressao) || 1));
    const agentUrl = printCfg.agentUrl || "http://localhost:9111";

    // ═════════════════════════════════════════════════════════
    // PRIORIDADE 1 — PDV DESKTOP (Electron com node-thermal-printer)
    // Detecta automaticamente quando rodando dentro do MixUp PDV.
    // 100% silencioso, profissional, multi-destino (USB+TCP).
    // O servidor PDV gerencia: vias, ativa/inativa, USB ou TCP — tudo na config.
    // ═════════════════════════════════════════════════════════
    if (isPDVDesktop()) {
      // Manda pros 3 destinos — o servidor PDV decide se imprime ou ignora
      // (se o destino estiver com ativa=false, retorna ignorado:true sem erro)
      imprimirViaPDV(p, "recibo");   // caixa
      imprimirViaPDV(p, "comanda");  // cozinha
      imprimirViaPDV(p, "bar");      // bar/copa
      return;
    }

    // ─────────────────────────────────────────
    // MODO AGENT — caixa E cozinha pelo Agent (100% silencioso via PowerShell)
    // ─────────────────────────────────────────
    if (modo === "agent") {
      const txCaixa = gerarTextoCaixa(p);
      const txCoz = gerarTextoCozinha(p);
      imprimirViaAgent(txCaixa, agentUrl, vias, "caixa");
      imprimirViaAgent(txCoz, agentUrl, vias, "cozinha");
      return;
    }

    // ─────────────────────────────────────────
    // MODO REDE — caixa via iframe/popup; cozinha via TCP socket
    // ─────────────────────────────────────────
    if (modo === "rede") {
      if (silentPrint) imprimirViaIframe(htmlCaixa, silentIframeRef, vias);
      else imprimirViaPopup(htmlCaixa, vias);
      const htmlCoz = gerarHtmlCozinha(p);
      imprimirViaRede(htmlCoz, printCfg.cozinhaIP || "", printCfg.cozinhaPorta || "9100", vias, p.id || 0);
      return;
    }

    // ─────────────────────────────────────────
    // MODO OFF — só caixa via navegador
    // ─────────────────────────────────────────
    if (silentPrint) imprimirViaIframe(htmlCaixa, silentIframeRef, vias);
    else imprimirViaPopup(htmlCaixa, vias);
  };

  const toggleSilentPrint = () => {
    const novo = !silentPrint;
    setSilentPrint(novo);
    localStorage.setItem("mixup_silent_print", novo ? "1" : "0");
    if (novo) setShowPrintCfg(true);
    else notify("Impressão normal ativada");
  };

  // ── Entrega avulsa ──
  const [showAvulsa, setShowAvulsa] = useState(false);
  const [avulsaStep, setAvulsaStep] = useState(1);
  const [avulsaBusca, setAvulsaBusca] = useState("");
  const [avulsaResultados, setAvulsaResultados] = useState([]);
  const [avulsaCliente, setAvulsaCliente] = useState(null);
  const [avulsaForm, setAvulsaForm] = useState({ nome:"", telefone:"", endereco:"", complemento:"", bairro:"", cidade:"Ponta Porã", valor:"", obs:"", ifood_codigo:"" });
  const [avulsaLoading, setAvulsaLoading] = useState(false);
  const [wpTexto, setWpTexto] = useState("");
  const [wpParsing, setWpParsing] = useState(false);
  const [avulsaTaxa, setAvulsaTaxa] = useState(null);
  const [avulsaTaxaInfo, setAvulsaTaxaInfo] = useState("");
  const [avulsaKmInfo, setAvulsaKmInfo] = useState(""); // info de km — não sobrescreve ao editar manualmente

  // ── Autocomplete de endereço (Google Places via proxy) ──
  const [enderecoSug, setEnderecoSug] = useState([]);
  const [enderecoBusy, setEnderecoBusy] = useState(false);
  const [enderecoFocus, setEnderecoFocus] = useState(false);
  const enderecoTimerRef = useRef(null);
  const buscarEndereco = (txt) => {
    if (enderecoTimerRef.current) clearTimeout(enderecoTimerRef.current);
    if (!txt || txt.length < 3) { setEnderecoSug([]); return; }
    enderecoTimerRef.current = setTimeout(async () => {
      setEnderecoBusy(true);
      try {
        const r = await fetch(`https://mixupbr.com.br/api/places_autocomplete/?input=${encodeURIComponent(txt)}`);
        const d = await r.json();
        if (d.ok) setEnderecoSug(d.sugestoes || []);
      } catch {}
      setEnderecoBusy(false);
    }, 300);
  };
  const escolherEndereco = async (sug) => {
    setEnderecoSug([]);
    setEnderecoFocus(false);
    try {
      const r = await fetch(`https://mixupbr.com.br/api/places_autocomplete/?place_id=${encodeURIComponent(sug.place_id)}`);
      const d = await r.json();
      if (d.ok) {
        setAvulsaForm(f => ({
          ...f,
          endereco: d.endereco || sug.descricao,
          bairro: d.bairro || f.bairro,
          cidade: d.cidade || f.cidade,
        }));
        if (d.lat && d.lng) {
          setAvulsaLat(d.lat);
          setAvulsaLng(d.lng);
        }
      } else {
        setAvulsaForm(f => ({ ...f, endereco: sug.descricao }));
      }
    } catch {
      setAvulsaForm(f => ({ ...f, endereco: sug.descricao }));
    }
  };
  const [avulsaCalcTaxa, setAvulsaCalcTaxa] = useState(false);
  const [avulsaFormaPag, setAvulsaFormaPag] = useState("dinheiro");
  const [avulsaJaPagou, setAvulsaJaPagou] = useState(false);
  const [avulsaTroco, setAvulsaTroco] = useState("");
  const [lojaModo, setLojaModo] = useState(null); // { tipo:'km'|'bairro'|'fixo', bairros:[], valor_fixo:0 }
  const [lojaModoLoading, setLojaModoLoading] = useState(false);
  const [bairroBusca, setBairroBusca] = useState("");
  const [bairroAberto, setBairroAberto] = useState(false);

  // ── Localização ──
  const [avulsaLat, setAvulsaLat] = useState(null);
  const [avulsaLng, setAvulsaLng] = useState(null);
  const [avulsaEndGeo, setAvulsaEndGeo] = useState("");
  const [locCode, setLocCode] = useState(null);
  const [locLink, setLocLink] = useState("");
  const [locMsgWp, setLocMsgWp] = useState("");
  const [locPolling, setLocPolling] = useState(false);
  const [locStatus, setLocStatus] = useState(""); // "" | "pendente" | "recebido"
  const [geoLoading, setGeoLoading] = useState(false);
  const locPollRef = useRef(null);

  const resetLocalizacao = () => {
    setAvulsaLat(null); setAvulsaLng(null); setAvulsaEndGeo(""); setLocCode(null); setLocLink(""); setLocMsgWp(""); setLocPolling(false); setLocStatus("");
    if (locPollRef.current) { clearInterval(locPollRef.current); locPollRef.current = null; }
  };

  // ═══════════════════════════════════════════════════════════════
  // BANDEJA DE AVULSAS PAUSADAS — múltiplas em paralelo
  // ═══════════════════════════════════════════════════════════════
  // Cada item guarda um snapshot completo do estado do modal +
  // tem seu próprio poller rodando em background.
  // Quando localização chega, marca status="recebido" e o operador
  // pode retomar pelo mini-card flutuante.

  // ── Persistência localStorage (por loja) ──
  // Sobrevive a trocar de tela e a F5/reload.
  // Chave única por loja pra evitar mistura entre lojistas no mesmo PDV.
  const STORAGE_KEY_PAUSADAS = `@rapiditos_avulsas_pausadas_loja_${lojaId || "0"}`;

  const [avulsasPausadas, setAvulsasPausadas] = useState(() => {
    // Lazy initializer: lê localStorage uma única vez ao montar
    try {
      const raw = localStorage.getItem(STORAGE_KEY_PAUSADAS);
      if (!raw) return [];
      const arr = JSON.parse(raw);
      if (!Array.isArray(arr)) return [];
      return arr;
    } catch {
      return [];
    }
  });
  const pollersPausadosRef = useRef({}); // { [pausada_id]: intervalRef }

  // Sincroniza qualquer mudança no array com localStorage
  useEffect(() => {
    try {
      if (avulsasPausadas.length === 0) {
        localStorage.removeItem(STORAGE_KEY_PAUSADAS);
      } else {
        localStorage.setItem(STORAGE_KEY_PAUSADAS, JSON.stringify(avulsasPausadas));
      }
    } catch {}
  }, [avulsasPausadas, STORAGE_KEY_PAUSADAS]);

  // Gera ID único pra cada pausada
  const gerarIdPausada = () => "p" + Date.now() + Math.floor(Math.random()*1000);

  // Helper: aplica retorno do /loc/checar num form base e devolve form atualizado
  // Aproveita os campos novos: rua, número, bairro, complemento, entre_ruas, cor_casa, referencia, país
  const aplicarLocAoForm = (formBase, d) => {
    if (!d || !d.ok) return formBase;
    const f = { ...formBase };
    // Nome/telefone — só sobrescreve se vieram do cliente e o form ainda estava vazio
    if (d.cliente_nome && !f.nome) f.nome = d.cliente_nome;
    if (d.cliente_telefone && !f.telefone) f.telefone = d.cliente_telefone;
    // Endereço — montar com rua + número (BR) ou rua sozinha (PY)
    if (d.endereco_rua) {
      const numStr = d.numero ? (", " + d.numero) : "";
      f.endereco = d.endereco_rua + numStr;
    } else if (d.endereco_reverso) {
      f.endereco = d.endereco_reverso;
    }
    if (d.bairro) f.bairro = d.bairro;
    // Complemento: BR usa complemento direto; PY junta cor_casa + entre_ruas
    const compPartes = [];
    if (d.complemento) compPartes.push(d.complemento);
    if (d.entre_ruas) compPartes.push("entre " + d.entre_ruas);
    if (d.cor_casa) compPartes.push(d.cor_casa);
    if (compPartes.length > 0) f.complemento = compPartes.join(" — ");
    // Referência vai pra obs (preservando obs existente)
    if (d.referencia) {
      const ref = "Ref: " + d.referencia;
      if (!f.obs) f.obs = ref;
      else if (!f.obs.includes(ref)) f.obs = f.obs + " | " + ref;
    }
    return f;
  };

  // Tocar bip suave quando localização chega (Web Audio API — sem arquivo externo)
  const beepLoc = () => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine"; o.frequency.value = 880;
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
      o.connect(g); g.connect(ctx.destination);
      o.start(); o.stop(ctx.currentTime + 0.4);
      // Segundo bip 80ms depois (mais grave) — som de "ding-dong"
      setTimeout(() => {
        try {
          const o2 = ctx.createOscillator();
          const g2 = ctx.createGain();
          o2.type = "sine"; o2.frequency.value = 660;
          g2.gain.setValueAtTime(0.0001, ctx.currentTime);
          g2.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02);
          g2.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
          o2.connect(g2); g2.connect(ctx.destination);
          o2.start(); o2.stop(ctx.currentTime + 0.4);
        } catch (e) {}
      }, 180);
    } catch (e) {}
  };

  // Inicia poller independente pra uma avulsa pausada (não depende de estado do modal)
  const iniciarPollerPausada = (pausadaId, locCodeVal) => {
    if (!locCodeVal) return;
    if (pollersPausadosRef.current[pausadaId]) {
      clearInterval(pollersPausadosRef.current[pausadaId]);
    }
    const interval = setInterval(async () => {
      try {
        const r = await fetch(`https://mixupbr.com.br/loc/?acao=checar&code=${locCodeVal}`);
        const d = await r.json();
        // Atualizar flag "abriu" se mudou (mesmo antes de receber localização)
        if (d.ok && d.aberto_em) {
          setAvulsasPausadas(prev => prev.map(p => (p.id === pausadaId && !p.locAbertoEm) ? { ...p, locAbertoEm: d.aberto_em } : p));
        }
        if (d.ok && d.status === "recebido") {
          // Atualiza a pausada com a localização recebida + todos os campos novos
          setAvulsasPausadas(prev => prev.map(p => {
            if (p.id !== pausadaId) return p;
            const formAtualizado = aplicarLocAoForm(p.avulsaForm, d);
            return {
              ...p,
              avulsaLat: d.latitude,
              avulsaLng: d.longitude,
              avulsaEndGeo: d.endereco_reverso || p.avulsaEndGeo,
              avulsaForm: formAtualizado,
              locPaisDetectado: d.pais_detectado || null,
              locStatus: "recebido",
              locPolling: false
            };
          }));
          clearInterval(pollersPausadosRef.current[pausadaId]);
          delete pollersPausadosRef.current[pausadaId];
          beepLoc();
          notify(`📍 Localização recebida: ${(avulsasPausadas.find(p=>p.id===pausadaId)?.avulsaForm?.nome) || "cliente"}`);
        }
      } catch {}
    }, 3000);
    pollersPausadosRef.current[pausadaId] = interval;
  };

  // PAUSAR: salva snapshot do modal atual + limpa pra próximo cliente
  const pausarAvulsa = () => {
    if (!avulsaForm.nome) { notify("Preencha pelo menos o nome para pausar", false); return; }
    const id = gerarIdPausada();
    const snap = {
      id,
      criadoEm: Date.now(),
      avulsaStep,
      avulsaCliente,
      avulsaForm: { ...avulsaForm },
      avulsaLat, avulsaLng, avulsaEndGeo,
      avulsaTaxa, avulsaTaxaInfo, avulsaKmInfo,
      avulsaFormaPag, avulsaJaPagou, avulsaTroco,
      locCode, locLink, locMsgWp, locStatus,
      locPolling: locCode && locStatus !== "recebido"
    };
    setAvulsasPausadas(prev => [...prev, snap]);
    // Se tinha polling ativo no modal, transfere pra poller da pausada
    if (locPollRef.current) { clearInterval(locPollRef.current); locPollRef.current = null; }
    if (locCode && locStatus !== "recebido") {
      iniciarPollerPausada(id, locCode);
    }
    // Limpa modal pro próximo
    setAvulsaStep(1); setAvulsaBusca(""); setAvulsaResultados([]);
    setAvulsaCliente(null);
    setAvulsaForm({ nome:"", telefone:"", endereco:"", complemento:"", bairro:"", cidade:"Ponta Porã", valor:"", obs:"", ifood_codigo:"" });
    setAvulsaTaxa(null); setAvulsaTaxaInfo(""); setAvulsaKmInfo("");
    setAvulsaFormaPag("dinheiro"); setAvulsaJaPagou(false); setAvulsaTroco("");
    setBairroBusca(""); setBairroAberto(false);
    setAvulsaLat(null); setAvulsaLng(null); setAvulsaEndGeo("");
    setLocCode(null); setLocLink(""); setLocMsgWp(""); setLocStatus(""); setLocPolling(false);
    setShowAvulsa(false);
    notify(`💤 Pausado: ${snap.avulsaForm.nome}. Aguardando cliente...`);
  };

  // RETOMAR: carrega snapshot no modal + para poller da pausada
  const retomarAvulsa = (id) => {
    const p = avulsasPausadas.find(x => x.id === id);
    if (!p) return;
    // Se modal atual tem dados, força pausar antes de retomar
    if (avulsaForm.nome && !showAvulsa) {
      // não há dados ativos no modal; segue
    } else if (avulsaForm.nome && showAvulsa) {
      notify("Pause a avulsa atual antes de retomar outra", false);
      return;
    }
    // Para o poller dessa pausada (vai voltar pro modal)
    if (pollersPausadosRef.current[id]) {
      clearInterval(pollersPausadosRef.current[id]);
      delete pollersPausadosRef.current[id];
    }
    // Restaura estados
    setAvulsaStep(p.avulsaStep || 2);
    setAvulsaCliente(p.avulsaCliente);
    setAvulsaForm(p.avulsaForm);
    setAvulsaLat(p.avulsaLat); setAvulsaLng(p.avulsaLng); setAvulsaEndGeo(p.avulsaEndGeo || "");
    setAvulsaTaxa(p.avulsaTaxa); setAvulsaTaxaInfo(p.avulsaTaxaInfo || ""); setAvulsaKmInfo(p.avulsaKmInfo || "");
    setAvulsaFormaPag(p.avulsaFormaPag || "dinheiro");
    setAvulsaJaPagou(!!p.avulsaJaPagou);
    setAvulsaTroco(p.avulsaTroco || "");
    setLocCode(p.locCode); setLocLink(p.locLink || ""); setLocMsgWp(p.locMsgWp || "");
    setLocStatus(p.locStatus || ""); setLocPolling(false);
    // Se ainda estava aguardando, retoma polling no modal
    if (p.locCode && p.locStatus !== "recebido") {
      setLocPolling(true);
      if (locPollRef.current) clearInterval(locPollRef.current);
      locPollRef.current = setInterval(async () => {
        try {
          const r = await fetch(`https://mixupbr.com.br/loc/?acao=checar&code=${p.locCode}`);
          const d = await r.json();
          if (d.ok && d.status === "recebido") {
            setAvulsaLat(d.latitude); setAvulsaLng(d.longitude);
            if (d.endereco_reverso) setAvulsaEndGeo(d.endereco_reverso);
            setAvulsaForm(f => aplicarLocAoForm(f, d));
            setLocStatus("recebido"); setLocPolling(false);
            clearInterval(locPollRef.current); locPollRef.current = null;
            beepLoc();
            notify("Localização recebida do cliente!");
          }
        } catch {}
      }, 3000);
    }
    // Remove da lista de pausadas
    setAvulsasPausadas(prev => prev.filter(x => x.id !== id));
    setShowAvulsa(true);
  };

  // DESCARTAR: remove pausada sem retomar
  const descartarAvulsa = (id) => {
    if (pollersPausadosRef.current[id]) {
      clearInterval(pollersPausadosRef.current[id]);
      delete pollersPausadosRef.current[id];
    }
    setAvulsasPausadas(prev => prev.filter(x => x.id !== id));
  };

  // Ao montar: restaura pollers para pausadas que estavam aguardando (vieram do localStorage)
  // Ao desmontar: limpa intervals (o estado já está salvo no localStorage)
  // Faz 1 checagem imediata pra cada pausada — se a localização chegou enquanto
  // o caixa estava em outra tela, atualiza o card já na primeira renderização.
  useEffect(() => {
    avulsasPausadas.forEach(async (p) => {
      if (!p.locCode || p.locStatus === "recebido") return;
      // Checagem imediata (pode já ter chegado enquanto fora da tela)
      try {
        const r = await fetch(`https://mixupbr.com.br/loc/?acao=checar&code=${p.locCode}`);
        const d = await r.json();
        if (d.ok && d.status === "recebido") {
          setAvulsasPausadas(prev => prev.map(x => {
            if (x.id !== p.id) return x;
            return {
              ...x,
              avulsaLat: d.latitude,
              avulsaLng: d.longitude,
              avulsaEndGeo: d.endereco_reverso || x.avulsaEndGeo,
              avulsaForm: aplicarLocAoForm(x.avulsaForm, d),
              locPaisDetectado: d.pais_detectado || null,
              locStatus: "recebido",
              locPolling: false
            };
          }));
          beepLoc();
          notify(`📍 Localização chegou enquanto você estava fora: ${p.avulsaForm.nome}`);
          return; // não inicia poller (já recebida)
        }
        // Atualiza flag aberto_em se vier
        if (d.ok && d.aberto_em && !p.locAbertoEm) {
          setAvulsasPausadas(prev => prev.map(x => x.id === p.id ? { ...x, locAbertoEm: d.aberto_em } : x));
        }
      } catch {}
      // Ainda pendente — inicia poller normal
      if (!pollersPausadosRef.current[p.id]) {
        iniciarPollerPausada(p.id, p.locCode);
      }
    });
    return () => {
      Object.values(pollersPausadosRef.current).forEach(i => clearInterval(i));
      pollersPausadosRef.current = {};
    };
  }, []);

  // Criar link de localização
  const criarLinkLoc = async () => {
    try {
      const r = await fetch("https://mixupbr.com.br/loc/?acao=criar", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ loja_id: lojaId || 1, nome: avulsaForm.nome, telefone: avulsaForm.telefone })
      });
      const d = await r.json();
      if (d.ok) {
        setLocCode(d.code); setLocLink(d.link); setLocMsgWp(d.mensagem_whatsapp); setLocStatus("pendente"); setLocPolling(true);
        // Copiar mensagem pro clipboard
        try { await navigator.clipboard.writeText(d.mensagem_whatsapp); notify("Link copiado! Cole no WhatsApp"); } catch { notify("Link gerado! Copie manualmente"); }
        // Iniciar polling
        if (locPollRef.current) clearInterval(locPollRef.current);
        locPollRef.current = setInterval(async () => {
          try {
            const r2 = await fetch(`https://mixupbr.com.br/loc/?acao=checar&code=${d.code}`);
            const d2 = await r2.json();
            if (d2.ok && d2.status === "recebido") {
              setAvulsaLat(d2.latitude); setAvulsaLng(d2.longitude);
              if (d2.endereco_reverso) setAvulsaEndGeo(d2.endereco_reverso);
              setAvulsaForm(f => aplicarLocAoForm(f, d2));
              setLocStatus("recebido"); setLocPolling(false);
              clearInterval(locPollRef.current); locPollRef.current = null;
              beepLoc();
              notify("Localização recebida do cliente!");
            }
          } catch {}
        }, 3000);
      }
    } catch { notify("Erro ao gerar link", false); }
  };

  // Geocoding do endereço
  const geocodarEndereco = async () => {
    const end = avulsaForm.endereco || avulsaForm.bairro;
    if (!end) { notify("Preencha o endereço primeiro", false); return; }
    setGeoLoading(true);
    try {
      const r = await fetch(`https://mixupbr.com.br/loc/?acao=geocode&endereco=${encodeURIComponent(end)}`);
      const d = await r.json();
      if (d.ok) {
        setAvulsaLat(d.latitude); setAvulsaLng(d.longitude); setAvulsaEndGeo(d.endereco_formatado);
        if (d.endereco_formatado) setAvulsaForm(f => ({ ...f, endereco: d.endereco_formatado }));
        notify("Endereço localizado no mapa!");
      } else { notify(d.erro || "Endereço não encontrado", false); }
    } catch { notify("Erro no geocoding", false); }
    setGeoLoading(false);
  };

  // Detectar link Google Maps no texto — ordem de prioridade (mais preciso primeiro)
  const extrairCoordsDeTexto = (texto) => {
    // 1. !3d e !4d no data do Google Maps (PIN EXATO — mais preciso)
    //    Ex: ...data=!4m4!3m3!8m2!3d-22.5289444!4d-55.7171389
    const m3d = texto.match(/!3d(-?[\d.]+).*?!4d(-?[\d.]+)/);
    if (m3d) return { lat: parseFloat(m3d[1]), lng: parseFloat(m3d[2]) };

    // 2. ?q=lat,lng ou &q=lat,lng (link de compartilhamento direto)
    //    Ex: maps.google.com/?q=-22.53,-55.72
    const mQ = texto.match(/[?&]q=(-?[\d.]+)[,%20]+([-]?[\d.]+)/);
    if (mQ) return { lat: parseFloat(mQ[1]), lng: parseFloat(mQ[2]) };

    // 3. /place/.../@lat,lng (posição da câmera — menos preciso que !3d!4d)
    //    Ex: maps/place/.../@-22.5287394,-55.7189441,17z
    const mAt = texto.match(/@(-?[\d.]+),(-?[\d.]+)/);
    if (mAt) return { lat: parseFloat(mAt[1]), lng: parseFloat(mAt[2]) };

    // 4. Coordenadas DMS (graus, minutos, segundos) — Ex: 22°31'44.2"S 55°43'01.7"W
    const dmsPattern = /(\d{1,3})[°º](\d{1,2})[''′](\d{1,2}(?:\.\d+)?)[""″]?\s*([NSns])\s*(\d{1,3})[°º](\d{1,2})[''′](\d{1,2}(?:\.\d+)?)[""″]?\s*([EWewOo])/;
    const mDms = texto.match(dmsPattern);
    if (mDms) {
      let lat = parseFloat(mDms[1]) + parseFloat(mDms[2])/60 + parseFloat(mDms[3])/3600;
      let lng = parseFloat(mDms[5]) + parseFloat(mDms[6])/60 + parseFloat(mDms[7])/3600;
      if (mDms[4].toUpperCase() === 'S') lat = -lat;
      if (mDms[8].toUpperCase() === 'W' || mDms[8].toUpperCase() === 'O') lng = -lng;
      return { lat: parseFloat(lat.toFixed(7)), lng: parseFloat(lng.toFixed(7)) };
    }

    // 5. DMS URL-encoded — Ex: 22%C2%B031'44.2%22S+55%C2%B043'01.7%22W
    try {
      const decoded = decodeURIComponent(texto);
      const mDmsDec = decoded.match(dmsPattern);
      if (mDmsDec) {
        let lat = parseFloat(mDmsDec[1]) + parseFloat(mDmsDec[2])/60 + parseFloat(mDmsDec[3])/3600;
        let lng = parseFloat(mDmsDec[5]) + parseFloat(mDmsDec[6])/60 + parseFloat(mDmsDec[7])/3600;
        if (mDmsDec[4].toUpperCase() === 'S') lat = -lat;
        if (mDmsDec[8].toUpperCase() === 'W' || mDmsDec[8].toUpperCase() === 'O') lng = -lng;
        return { lat: parseFloat(lat.toFixed(7)), lng: parseFloat(lng.toFixed(7)) };
      }
    } catch {}

    // 6. Coordenadas soltas no texto — Ex: -22.5289444, -55.7171389
    const mSolto = texto.match(/(-?\d{1,3}\.\d{3,8})\s*[,;]\s*(-?\d{1,3}\.\d{3,8})/);
    if (mSolto) {
      const lat = parseFloat(mSolto[1]), lng = parseFloat(mSolto[2]);
      if (Math.abs(lat) <= 90 && Math.abs(lng) <= 180) return { lat, lng };
    }

    return null;
  };

  const refreshTimerRef = useRef(null);
  const userInteracting = useRef(false);
  const interactTimer = useRef(null);

  const notify = (msg, ok = true) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 2800); };

  useEffect(() => {
    const lId = lojaId || 1;
    const aplicarLoja = (l) => {
      if (!l?.id) return; setLoja(l);
      if (l.online_manual !== null && l.online_manual !== undefined && l.online_manual !== "") setLojaOnline(Number(l.online_manual) === 1);
      else setLojaOnline(isLojaOnlineAgora(l));
      if (l.saldo_credito !== undefined) setSaldo(l.saldo_credito);

      // Pausa ativa? (online_manual_expira no futuro)
      if (l.online_manual_expira) {
        try {
          const exp = new Date(l.online_manual_expira.replace(" ", "T") + "Z"); // banco salva UTC
          if (exp > new Date()) setPausaInfo({ expira: exp });
          else setPausaInfo(null);
        } catch { setPausaInfo(null); }
      } else {
        setPausaInfo(null);
      }

      // Modo forçado? (online_manual com valor mas SEM expira = vale até próximo evento de horário)
      // Mostra indicação visual quando o estado manual difere do que o horário pediria.
      if (l.online_manual !== null && l.online_manual !== undefined && l.online_manual !== "" && !l.online_manual_expira) {
        const manualOn = Number(l.online_manual) === 1;
        const horarioOn = isLojaOnlineAgora(l);
        // Só mostra "forçado" se o manual diverge do horário automático
        if (manualOn !== horarioOn) {
          let proximoEvento = null;
          if (l.proximo_evento_horario?.em_iso) {
            try { proximoEvento = new Date(l.proximo_evento_horario.em_iso); } catch {}
          }
          setForcadoInfo({ tipo: manualOn ? "online" : "offline", proximoEvento });
        } else {
          setForcadoInfo(null);
        }
      } else {
        setForcadoInfo(null);
      }
    };
    get("loja_config", { loja_id:lId, acao:"dados" })
      .then(r => { if (r.dados?.id) aplicarLoja(r.dados); else throw new Error(); })
      .catch(() => {
        get("lojas", { id:lId }).then(r => { const l = Array.isArray(r.dados) ? r.dados[0] : r.dados || r; aplicarLoja(l); })
        .catch(() => setLojaOnline(isLojaOnlineAgora({ horario_abre:"08:00", horario_fecha:"23:00", dias_funcionamento:"seg,ter,qua,qui,sex,sab,dom" })));
      });
  }, [lojaId]);

  // Polling saldo a cada 30s
  useEffect(() => {
    const lId = lojaId || 1;
    const pollSaldo = () => {
      get("loja_config", { loja_id:lId, acao:"dados" })
        .then(r => { if (r.dados?.saldo_credito !== undefined) setSaldo(r.dados.saldo_credito); })
        .catch(() => {});
    };
    const iv = setInterval(pollSaldo, 30000);
    return () => clearInterval(iv);
  }, [lojaId]);

  const triggerRefresh = useCallback(() => { if (!userInteracting.current) onRefresh(); }, [onRefresh]);
  useEffect(() => { refreshTimerRef.current = setInterval(triggerRefresh, 20000); return () => clearInterval(refreshTimerRef.current); }, [triggerRefresh]);

  const markInteracting = () => {
    userInteracting.current = true;
    clearTimeout(interactTimer.current);
    interactTimer.current = setTimeout(() => { userInteracting.current = false; }, 5000);
  };

  const toggleOnline = async () => {
    const novoStatus = !lojaOnline; setLojaOnline(novoStatus);
    notify(novoStatus ? _("lojaOnlineMsg") : _("lojaOfflineMsg"), novoStatus);
    setTogglingOnline(true);
    try { await post("loja_config", { loja_id: loja?.id || lojaId || 1, acao: "toggle_online", online: novoStatus }); } catch {}
    setTogglingOnline(false);
    setPausaInfo(null); // toggle manual cancela qualquer pausa em andamento
    // Atualiza forcadoInfo: força refresh dos dados pra recalcular
    try {
      const r = await get("loja_config", { loja_id: loja?.id || lojaId || 1, acao: "dados" });
      if (r?.dados) {
        const l = r.dados;
        const manualOn = Number(l.online_manual) === 1;
        const horarioOn = isLojaOnlineAgora(l);
        if (l.online_manual !== null && l.online_manual !== undefined && l.online_manual !== "" && !l.online_manual_expira && manualOn !== horarioOn) {
          let proxEv = null;
          if (l.proximo_evento_horario?.em_iso) {
            try { proxEv = new Date(l.proximo_evento_horario.em_iso); } catch {}
          }
          setForcadoInfo({ tipo: manualOn ? "online" : "offline", proximoEvento: proxEv });
        } else {
          setForcadoInfo(null);
        }
      }
    } catch {}
  };

  // ── PAUSA TEMPORÁRIA ──
  // Pausa a loja por X minutos. Backend marca online_manual=0 + online_manual_expira=NOW()+X
  // Após o tempo, backend volta automaticamente pra modo Auto.
  const pausarLoja = async (minutos) => {
    setShowPausaMenu(false);
    const txt = minutos === 30 ? _("pausar30min") : minutos === 60 ? _("pausar1h") : minutos === 120 ? _("pausar2h") : `${minutos} min`;
    if (!window.confirm(_("pausarConfirm").replace("{tempo}", txt))) return;

    setTogglingOnline(true);
    try {
      await post("loja_config", {
        loja_id: loja?.id || lojaId || 1,
        acao: "toggle_online",
        online: 0,
        pausar_minutos: minutos
      });
      const expira = new Date(Date.now() + minutos * 60 * 1000);
      setPausaInfo({ expira });
      setLojaOnline(false);
      notify(`${_("lojaPausadaPor")} ${txt}`, false);
    } catch (e) {
      notify(_("erroPausarLoja"), false);
    }
    setTogglingOnline(false);
  };

  const cancelarPausa = async () => {
    if (!window.confirm(_("cancelarPausaConfirm"))) return;
    setTogglingOnline(true);
    try {
      await post("loja_config", {
        loja_id: loja?.id || lojaId || 1,
        acao: "auto_online"
      });
      setPausaInfo(null);
      setForcadoInfo(null);
      // Recarrega config pra pegar estado real
      try {
        const r = await get("loja_config", { loja_id: loja?.id || lojaId || 1, acao: "dados" });
        if (r?.dados) {
          if (r.dados.online_manual !== null && r.dados.online_manual !== undefined && r.dados.online_manual !== "") {
            setLojaOnline(Number(r.dados.online_manual) === 1);
          } else {
            setLojaOnline(isLojaOnlineAgora(r.dados));
          }
        }
      } catch {}
      notify(_("pausaCanceladaMsg"));
    } catch (e) {
      notify(_("erroCancelarPausa"), false);
    }
    setTogglingOnline(false);
  };

  // Tick a cada 30s pra atualizar timer da pausa e detectar expiração
  useEffect(() => {
    if (!pausaInfo) return;
    const iv = setInterval(() => {
      if (pausaInfo.expira <= new Date()) {
        setPausaInfo(null);
        // força refresh da loja pro backend recalcular
        const lId = loja?.id || lojaId || 1;
        get("loja_config", { loja_id: lId, acao: "dados" })
          .then(r => {
            if (r?.dados) {
              if (r.dados.online_manual !== null && r.dados.online_manual !== undefined && r.dados.online_manual !== "") {
                setLojaOnline(Number(r.dados.online_manual) === 1);
              } else {
                setLojaOnline(isLojaOnlineAgora(r.dados));
              }
            }
          }).catch(() => {});
      } else {
        // força re-render pra atualizar countdown
        setPausaInfo(p => p ? { ...p } : null);
      }
    }, 30000);
    return () => clearInterval(iv);
  }, [pausaInfo, loja?.id, lojaId]);

  // Helper pra formatar tempo restante da pausa
  const formatTempoRestante = (expira) => {
    const ms = expira - new Date();
    if (ms <= 0) return "0min";
    const min = Math.ceil(ms / 60000);
    if (min < 60) return `${min}min`;
    const h = Math.floor(min / 60);
    const m = min % 60;
    return m === 0 ? `${h}h` : `${h}h${m}min`;
  };

  const criarPedidoTeste = async () => {
    markInteracting();
    try {
      const codigo = "TST" + String(Math.floor(Math.random() * 9000) + 1000);
      const res = await post("pedidos", { codigo, cliente_id:1, loja_id:lojaId||1, status:"novo", subtotal:"29.90", taxa_entrega:"5.00",
        desconto:"0", total:"34.90", forma_pagamento:"pix", endereco_entrega:"Rua Teste, 123 - Ponta Porã",
        observacao:"PEDIDO DE TESTE — pode excluir",
        itens: [{ produto_id:1, nome:"X-Bacon Duplo (TESTE)", quantidade:2, preco_unitario:"14.95", preco_total:"29.90" }] });
      if (res.id || res.success) { notify(`Pedido teste #${codigo} criado!`); onRefresh(); }
      else notify(res.error || "Erro ao criar pedido teste", false);
    } catch (e) { notify("Erro: " + e.message, false); }
  };

  const buscarCliente = async () => {
    if (!avulsaBusca.trim()) return;
    setAvulsaLoading(true);
    const lista = [];
    const visto = new Set(); // dedup por telefone normalizado
    // (1) sistema antigo - clientes_avulsos da loja
    try {
      const r1 = await get("loja_config", { acao:"buscar_cliente", busca:avulsaBusca, loja_id:lojaId||1 });
      (r1.dados || []).forEach(c => {
        const telKey = String(c.telefone||"").replace(/\D/g,"");
        const key = telKey || ("n:" + String(c.nome||"").toLowerCase());
        if (!visto.has(key)) { visto.add(key); lista.push({ ...c, origem: c.origem || "avulso" }); }
      });
    } catch(e) { console.warn("buscar_cliente legacy:", e); }
    // (2) sistema novo - cliente_referencias (entre lojas, por telefone)
    try {
      const r2 = await fetch(`https://mixupbr.com.br/api/cliente_referencias/?busca=${encodeURIComponent(avulsaBusca)}`);
      const d2 = await r2.json();
      (d2.dados || []).forEach(c => {
        const key = c.telefone_norm || ("n:" + String(c.nome||"").toLowerCase());
        if (!visto.has(key)) {
          visto.add(key);
          lista.push({
            id: "ref_" + c.id,                   // prefix pra não conflitar com IDs do legacy
            nome: c.nome,
            telefone: c.telefone_norm,
            endereco: c.endereco,
            obs_entrega: c.referencia,
            foto_ref_url: c.foto_url,
            origem: "referencia",
          });
        }
      });
    } catch(e) { console.warn("cliente_referencias busca:", e); }
    setAvulsaResultados(lista);
    setAvulsaLoading(false);
  };
  const selecionarClienteAvulsa = (cli) => {
    setAvulsaCliente(cli);
    setAvulsaForm({ nome:cli.nome||"", telefone:cli.telefone||"", endereco:cli.endereco||cli.endereco_padrao||"", complemento:cli.complemento||"", bairro:cli.bairro||"", cidade:cli.cidade||"Ponta Porã", valor:"", obs:cli.obs_entrega||"", ifood_codigo:"" });
    // Carregar lat/lng se o cliente já tem
    if (cli.latitude && cli.longitude) { setAvulsaLat(parseFloat(cli.latitude)); setAvulsaLng(parseFloat(cli.longitude)); setAvulsaEndGeo(cli.endereco||cli.endereco_padrao||""); }
    else { setAvulsaLat(null); setAvulsaLng(null); setAvulsaEndGeo(""); }
    setAvulsaStep(2);
  };
  const novoClienteAvulsa = () => { setAvulsaCliente(null); setAvulsaForm({ nome:"", telefone:"", endereco:"", complemento:"", bairro:"", cidade:"Ponta Porã", valor:"", obs:"" }); resetLocalizacao(); setAvulsaStep(2); };

  const parsearWhatsApp = async () => {
    if (!wpTexto.trim()) return; setWpParsing(true);
    // 1. Detectar link Google Maps no texto
    const coords = extrairCoordsDeTexto(wpTexto);
    if (coords) { setAvulsaLat(coords.lat); setAvulsaLng(coords.lng); notify("Localização detectada do Maps!"); }
    // 2. Chamar parser da API
    try {
      const r = await post("whatsapp_parser", { texto: wpTexto });
      if (r.sucesso && r.dados) {
        const d = r.dados;
        setAvulsaForm(f => ({ ...f, nome:d.nome||f.nome, telefone:d.telefone||f.telefone, endereco:d.endereco||f.endereco, complemento:d.complemento||f.complemento, bairro:d.bairro||f.bairro, cidade:d.cidade||f.cidade||"Ponta Porã", valor:d.valor||f.valor, obs:d.observacao||f.obs }));
        if (d.forma_pagamento) setAvulsaFormaPag(d.forma_pagamento);
        setAvulsaStep(2); notify(coords ? "Dados + localização extraídos!" : "Dados extraídos do WhatsApp!");
        // Se modo bairro, tentar auto-match com lista de bairros
        if (d.bairro && lojaModo?.tipo === 'bairro' && lojaModo.bairros?.length > 0) {
          const match = lojaModo.bairros.find(b => b.bairro_nome && d.bairro.toLowerCase().includes(b.bairro_nome.toLowerCase()));
          if (match) { setAvulsaTaxa(parseFloat(match.valor)); setAvulsaTaxaInfo("Bairro: " + match.bairro_nome); setAvulsaForm(f => ({...f, bairro: match.bairro_nome})); setBairroBusca(match.bairro_nome); setBairroAberto(false); }
        } else if (d.bairro) {
          calcularTaxaAvulsa(d.bairro, d.valor);
        }
        // Se não achou coords no texto mas tem endereço, tentar geocoding automático
        // Só para modo km — bairro/fixo não precisa de API Google
        if (!coords && (d.endereco || d.bairro) && lojaModo?.tipo !== 'bairro' && lojaModo?.tipo !== 'fixo') {
          try {
            const geoR = await fetch(`https://mixupbr.com.br/loc/?acao=geocode&endereco=${encodeURIComponent((d.endereco||"")+" "+(d.bairro||""))}`);
            const geoD = await geoR.json();
            if (geoD.ok) { setAvulsaLat(geoD.latitude); setAvulsaLng(geoD.longitude); setAvulsaEndGeo(geoD.endereco_formatado||""); }
          } catch {}
        }
      } else notify("Não foi possível extrair dados", false);
    } catch { notify("Erro ao processar", false); } setWpParsing(false);
  };
  const colarDoClipboard = async () => {
    try {
      const t = await navigator.clipboard.readText();
      if (t) {
        setWpTexto(t); notify("Texto colado!");
        // Auto-detectar link Maps
        const coords = extrairCoordsDeTexto(t);
        if (coords) { setAvulsaLat(coords.lat); setAvulsaLng(coords.lng); notify(`📍 Localização detectada: ${coords.lat.toFixed(7)}, ${coords.lng.toFixed(7)}`); }
      }
    } catch { notify("Permita acesso à área de transferência", false); }
  };

  // ── Buscar modo de cobrança da loja ──
  const fetchLojaModo = async () => {
    setLojaModoLoading(true);
    try {
      const r = await fetch(`https://mixupbr.com.br/api/delivery/?modulo=loja_modo&loja_id=${lojaId||1}`);
      const d = await r.json();
      setLojaModo(d);
      // Se modo fixo, já setar a taxa direto
      if (d.tipo === 'fixo') { setAvulsaTaxa(d.valor_fixo); setAvulsaTaxaInfo("Taxa fixa: " + d.pacote_nome); }
    } catch { setLojaModo({ tipo:'km', bairros:[] }); }
    setLojaModoLoading(false);
  };

  const calcularTaxaAvulsa = async (bairroOverride, valorOverride) => {
    setAvulsaCalcTaxa(true);
    try {
      const lojaLat = parseFloat(loja?.latitude || loja?.lat || 0);
      const lojaLng = parseFloat(loja?.longitude || loja?.lng || 0);
      const cliLat = avulsaLat || 0;
      const cliLng = avulsaLng || 0;
      const bairro = bairroOverride || avulsaForm.bairro || "";
      const valorPedido = parseFloat(valorOverride || avulsaForm.valor || 0);

      // Se tem coordenadas da loja E do cliente → calcular_completo (km/bairro/fixo automático)
      if (lojaLat && lojaLng && cliLat && cliLng) {
        const r = await fetch("https://mixupbr.com.br/api/delivery/?modulo=calcular_completo", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ lat_origem:lojaLat, lng_origem:lojaLng, lat_destino:cliLat, lng_destino:cliLng, loja_id:lojaId||1, valor_pedido:valorPedido, bairro })
        });
        const d = await r.json();
        if (d.taxa !== undefined) {
          setAvulsaTaxa(d.taxa);
          setAvulsaTaxaInfo(`${d.motivo || d.modo || ""} · ${d.distancia_km || 0}km · ~${d.tempo_estimado_min || 0}min`);
          // Guarda só a info de km separadamente (não sobrescreve ao editar manualmente)
          if (d.distancia_km) setAvulsaKmInfo(`${d.distancia_km}km · ~${d.tempo_estimado_min || 0}min`);
        } else {
          setAvulsaTaxa(null);
          setAvulsaTaxaInfo(d.error || "Erro no cálculo");
          setAvulsaKmInfo("");
        }
      }
      // Se não tem coords da loja mas tem bairro → tenta bairro
      else if (bairro) {
        const r = await fetch("https://mixupbr.com.br/api/delivery/?modulo=calcular_completo", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ lat_origem:-22.5348, lng_origem:-55.7249, lat_destino:cliLat||-22.5348, lng_destino:cliLng||-55.7249, loja_id:lojaId||1, valor_pedido:valorPedido, bairro })
        });
        const d = await r.json();
        if (d.taxa !== undefined) { setAvulsaTaxa(d.taxa); setAvulsaTaxaInfo(d.motivo || d.modo || ""); }
        else { setAvulsaTaxa(null); setAvulsaTaxaInfo("Configure coordenadas da loja"); }
      } else {
        setAvulsaTaxa(null); setAvulsaTaxaInfo("Preencha endereço ou obtenha localização");
      }
    } catch { setAvulsaTaxa(null); setAvulsaTaxaInfo("Erro ao calcular"); }
    setAvulsaCalcTaxa(false);
  };

  // Auto-calcular taxa quando localização do cliente muda (só modo KM)
  useEffect(() => {
    if (avulsaLat && avulsaLng && showAvulsa && lojaModo?.tipo !== 'bairro' && lojaModo?.tipo !== 'fixo') {
      calcularTaxaAvulsa();
    }
  }, [avulsaLat, avulsaLng]);

  const confirmarEntregaAvulsa = async () => {
    // Validação MÍNIMA: só nome é obrigatório
    if (!avulsaForm.nome) { notify("Nome é obrigatório", false); return; }
    setAvulsaLoading(true);
    try {
      const codigo = "AVL" + String(Math.floor(Math.random() * 9000) + 1000);
      const end = [avulsaForm.endereco, avulsaForm.complemento, avulsaForm.bairro, avulsaForm.cidade].filter(Boolean).join(", ");
      const valorNum = parseFloat(avulsaForm.valor) || 0;
      const taxaNum = avulsaTaxa || 0;
      const res = await post("pedidos", { codigo, cliente_id:avulsaCliente?.id||0, loja_id:lojaId||1, status:"novo",
        subtotal:valorNum.toFixed(2), taxa_entrega:taxaNum.toFixed(2), desconto:"0", total:(valorNum+taxaNum).toFixed(2),
        forma_pagamento:avulsaFormaPag||"dinheiro", endereco_entrega:end, telefone_entrega:avulsaForm.telefone,
        cliente_nome:avulsaForm.nome||"Avulso",
        observacao:`[ENTREGA AVULSA]${avulsaJaPagou?" [PAGO ONLINE]":""} Cliente: ${avulsaForm.nome} • Tel: ${avulsaForm.telefone}${avulsaJaPagou?" • PAGO ("+avulsaFormaPag.toUpperCase()+")":""}${!avulsaJaPagou&&avulsaFormaPag==="dinheiro"&&avulsaTroco?" • TROCO p/ R$"+avulsaTroco:""}${avulsaForm.obs ? " • "+avulsaForm.obs : ""}`,
        troco_para: (!avulsaJaPagou && avulsaFormaPag==="dinheiro" && avulsaTroco) ? avulsaTroco : null,
        itens:[{ produto_id:1, nome:`Entrega avulsa - ${avulsaForm.nome}`, quantidade:1, preco_unitario:valorNum.toFixed(2), preco_total:valorNum.toFixed(2) }],
        cliente_nome_avulso:avulsaForm.nome, cliente_tel_avulso:avulsaForm.telefone,
        ifood_codigo: avulsaForm.ifood_codigo || null,
        latitude: avulsaLat || null, longitude: avulsaLng || null });
      if (res.id || res.success) {
        notify(`Entrega avulsa #${codigo} criada!`);

        // ═══ SALVA O CADASTRO DO CLIENTE (2 lugares pra garantir) ═══
        // (a) Cliente avulso da loja — SEMPRE salva/atualiza (Bug 6).
        //     Quando avulsaCliente.id existe E origem é "avulso", manda cliente_id
        //     pra API forçar UPDATE daquele registro (não criar duplicado).
        //     Clientes de origem "referencia" têm id "ref_X" (string) — não mandar.
        if (avulsaForm.nome) {
          try {
            const payloadAvulso = { acao:"salvar_cliente_avulso", loja_id:lojaId||1,
              nome:avulsaForm.nome, telefone:avulsaForm.telefone||"",
              endereco:avulsaForm.endereco||"", complemento:avulsaForm.complemento||"",
              bairro:avulsaForm.bairro||"", cidade:avulsaForm.cidade||"",
              obs_entrega:avulsaForm.obs||"",
              latitude:avulsaLat, longitude:avulsaLng };
            // Se selecionou cliente avulso existente, força UPDATE pelo ID
            if (avulsaCliente && avulsaCliente.origem === "avulso" && typeof avulsaCliente.id === "number") {
              payloadAvulso.cliente_id = avulsaCliente.id;
            }
            await post("loja_config", payloadAvulso);
          } catch(e) { console.warn("salvar_cliente_avulso falhou:", e); }
        }
        // (b) cliente_referencias (sistema novo - vincula por telefone, vale entre lojas)
        if (avulsaForm.telefone) {
          try {
            await fetch("https://mixupbr.com.br/api/cliente_referencias/", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                telefone: avulsaForm.telefone,
                nome: avulsaForm.nome,
                endereco: avulsaForm.endereco,
                referencia: avulsaForm.obs || "",
                loja_id: lojaId || 1,
              }),
            });
          } catch(e) { console.warn("cliente_referencias falhou:", e); }
        }

        onRefresh(); setShowAvulsa(false); setAvulsaStep(1); setAvulsaBusca(""); setAvulsaResultados([]);
        setWpTexto(""); setAvulsaTaxa(null); setAvulsaTaxaInfo(""); setAvulsaKmInfo(""); setAvulsaFormaPag("dinheiro"); setAvulsaJaPagou(false); setAvulsaTroco(""); setBairroBusca(""); setBairroAberto(false); resetLocalizacao();
        // Reset campos do formulário (incluindo ifood_codigo que muda a cada pedido)
        setAvulsaForm({ nome:"", telefone:"", endereco:"", complemento:"", bairro:"", cidade:"Ponta Porã", valor:"", obs:"", ifood_codigo:"" });
        setAvulsaCliente(null);
      } else notify(res.error || "Erro ao criar pedido", false);
    } catch(e) { notify("Erro de conexão: " + (e?.message || ""), false); console.error("entrega avulsa:", e); }
    setAvulsaLoading(false);
  };

  // ── Cálculos vendas ──
  // Helpers de classificação de pedido:
  // - isAvulsa: entrega avulsa (só chamou entregador, não é venda)
  // - isPDV: venda de balcão via PDV (é venda, mas não gera comissão — já paga no momento)
  // - isAppCliente: pedido pelo app (é venda E gera comissão)
  // - Catálogo digital: é venda, mas não gera comissão (igual PDV)
  const isAvulsa = (o) => String(o.codigo||"").startsWith("AVL") || String(o.observacao||"").includes("[ENTREGA AVULSA]");
  const isPDV = (o) => o.canal === "pdv_interno" || String(o.codigo||"").startsWith("PDV");
  const isAppCliente = (o) => !isAvulsa(o) && !isPDV(o) && (o.canal === "app" || !o.canal || o.canal === "");

  const vendasHoje = orders.filter(o => o.criado_em?.startsWith(new Date().toISOString().slice(0,10)));
  // Venda real = pedido entregue/em_entrega que NÃO é avulsa (PDV e app/catálogo contam)
  const vendasDone = vendasHoje.filter(o => DONE_STATUS.includes(o.status) && !isAvulsa(o));
  const totalVendas = vendasDone.reduce((s, o) => s + valorReal(o), 0);
  const totalSubtotal = vendasDone.reduce((s, o) => s + Number(o.subtotal || 0), 0);
  const vendaPix = vendasDone.filter(o => o.forma_pagamento==="pix").reduce((s,o) => s+Number(o.total||0), 0);
  const vendaCartao = vendasDone.filter(o => ["credito","debito"].includes(o.forma_pagamento)).reduce((s,o) => s+Number(o.total||0), 0);
  const vendaDinheiro = vendasDone.filter(o => o.forma_pagamento==="dinheiro").reduce((s,o) => s+Number(o.total||0), 0);
  const vendaCash = vendasDone.reduce((s,o) => s+Number(o.cashback_usado||0), 0);
  const cashbackGerado = vendasDone.reduce((s,o) => s+Number(o.cashback_gerado||0), 0);
  const comissaoPercent = Number(loja?.comissao_percentual || 10);
  // Comissão = só sobre vendas do app cliente (exclui PDV, catálogo e avulsa)
  const subtotalComissao = vendasDone.filter(isAppCliente).reduce((s, o) => s + Number(o.subtotal || 0), 0);
  const comissaoValor = subtotalComissao * (comissaoPercent / 100);

  const sel = selected ? orders.find(o => o.id === selected.id) : null;
  const stLabel = (st) => ({ novo:_("stNovo"), aceito:_("stAceito"), preparando:_("stPreparando"), pronto:_("stPronto"), buscando_entregador:_("stBuscando"), em_entrega:_("stEmEntrega"), entregue:_("stEntregue"), cancelado:_("stCancelado") }[st] || st);

  const iS = { width:"100%", padding:"10px 12px", borderRadius:8, border:`1px solid ${C.border}`, background:C.bg, color:C.text, fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box" };

  // Lojas com modo bairro ou fixo NÃO consomem API Google (geocoding, static maps, GPS link)
  const modoSemGPS = lojaModo?.tipo === 'bairro' || lojaModo?.tipo === 'fixo';

  // ── Seções da lista ──
  const SECTIONS = [
    { key:"pendente", label:"Pendente", color:C.red, items: orders.filter(o => o.status === "novo") },
    { key:"aceito",   label:"Aceito",  color:C.orange, items: orders.filter(o => ["aceito","preparando"].includes(o.status)) },
    { key:"entregador",label:"Aguardando entregador", color:"#3B82F6", items: orders.filter(o => ["pronto","buscando_entregador"].includes(o.status)) },
    { key:"rota",     label:"Em entrega", color:C.greenLight, items: orders.filter(o => o.status === "em_entrega") },
    { key:"concluido",label:"Concluído", color:"#6B7280", items: orders.filter(o => ["entregue","cancelado"].includes(o.status)).slice(0,10) },
  ];

  // Busca
  const buscaLower = busca.toLowerCase().trim();
  const filtrarBusca = (arr) => buscaLower ? arr.filter(o =>
    (o.codigo||"").toLowerCase().includes(buscaLower) ||
    (o.cliente_nome||"").toLowerCase().includes(buscaLower) ||
    (o.observacao||"").toLowerCase().includes(buscaLower)
  ) : arr;

  // ──────────────────────────────────────────
  // RENDER
  // ──────────────────────────────────────────
  return (
    <div style={{ display:"flex", flexDirection:"column", height:"calc(100vh - 32px)", animation:"fadeUp .3s ease-out", overflow:"hidden" }}
      onMouseMove={markInteracting} onKeyDown={markInteracting}>

      <audio ref={somRef} src="/mixup-lojista/notification.mp3" preload="auto" />

      {toast && (
        <div style={{ position:"fixed", top:20, left:"50%", transform:"translateX(-50%)", zIndex:999,
          background: toast.ok ? C.green : C.red, color:"#fff", padding:"12px 28px",
          borderRadius:12, fontWeight:700, fontSize:14, animation:"slideDown .3s ease-out", whiteSpace:"nowrap" }}>
          {toast.msg}
        </div>
      )}

      {/* ══ MODALS ══ */}

      {/* Modal configuração de impressão */}
      {showPrintCfg && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.6)", zIndex:900, display:"flex", alignItems:"center", justifyContent:"center" }} onClick={() => setShowPrintCfg(false)}>
          <div style={{ background:C.card, borderRadius:20, padding:28, width:520, maxHeight:"90vh", overflowY:"auto", border:`1px solid ${C.border}`, animation:"fadeUp .3s ease-out" }} onClick={e=>e.stopPropagation()}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:20 }}>
              <div style={{ width:44, height:44, borderRadius:12, background:"#F59E0B18", display:"flex", alignItems:"center", justifyContent:"center", fontSize:22 }}>🖨</div>
              <div>
                <h3 style={{ fontWeight:800, fontSize:20 }}>Configuração de Impressão</h3>
                <p style={{ fontSize:11, color:C.textMuted, marginTop:2 }}>Configure impressoras do caixa e da cozinha</p>
              </div>
            </div>

            {/* ═══ SEÇÃO 1: MODO DE IMPRESSÃO ═══ */}
            <div style={{ padding:16, borderRadius:14, background:silentPrint?`#22C55E08`:`${C.red}08`, border:`1px solid ${silentPrint?"#22C55E25":C.red+"25"}`, marginBottom:16 }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <div style={{ width:10, height:10, borderRadius:"50%", background:silentPrint?C.greenLight:C.red }} />
                  <span style={{ fontSize:14, fontWeight:700 }}>Impressão Silenciosa</span>
                </div>
                <button onClick={() => { const n = !silentPrint; setSilentPrint(n); localStorage.setItem("mixup_silent_print", n?"1":"0"); }}
                  style={{ padding:"6px 16px", borderRadius:8, border:"none", background:silentPrint?C.red:C.green, color:"#fff", fontWeight:700, fontSize:12, cursor:"pointer" }}>
                  {silentPrint?"Desativar":"Ativar"}
                </button>
              </div>
              <div style={{ fontSize:11, color:C.textSoft, lineHeight:1.6 }}>
                Quando ativada, imprime via iframe oculto sem abrir nova janela. Combine com <b>Auto ON</b> para aceitar + imprimir automaticamente.
              </div>
            </div>

            {/* ═══ SEÇÃO 2: VIAS POR PEDIDO (do banco — read-only) ═══ */}
            <div style={{ padding:16, borderRadius:14, background:`#3B82F608`, border:"1px solid #3B82F625", marginBottom:16 }}>
              <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:6, display:"flex", alignItems:"center", gap:6 }}>
                <span style={{ fontSize:16 }}>📑</span> Vias por Pedido
              </div>
              <div style={{ fontSize:18, fontWeight:900, color:"#3B82F6", marginBottom:6 }}>
                {loja?.vias_impressao || 1} via{(loja?.vias_impressao || 1) > 1 ? "s" : ""}
              </div>
              <div style={{ fontSize:10, color:C.textMuted, lineHeight:1.5 }}>
                Quantas vias serão impressas a cada pedido (caixa + cozinha). Esta configuração é definida pelo administrador na configuração da loja.
              </div>
            </div>

            {/* ═══ SEÇÃO 3: IMPRESSORA DO CAIXA ═══ */}
            <div style={{ padding:16, borderRadius:14, border:`1px solid ${C.border}`, marginBottom:16 }}>
              <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:10, display:"flex", alignItems:"center", gap:6 }}>
                <span style={{ fontSize:16 }}>💰</span> Impressora do Caixa
              </div>
              <div style={{ fontSize:11, color:C.textMuted, marginBottom:10 }}>
                Usa a impressora <b>padrão do sistema</b>. Imprime nota completa com valores, pagamento e endereço.
              </div>
              <div>
                <label style={{ fontSize:10, fontWeight:600, color:C.textMuted, display:"block", marginBottom:4 }}>Nome da impressora (referência):</label>
                <input value={printCfg.caixaNome||""} onChange={e => savePrintCfg({...printCfg, caixaNome:e.target.value})}
                  placeholder="Ex: Epson TM-T20, Elgin i9..."
                  style={{ width:"100%", padding:"8px 12px", borderRadius:8, border:`1px solid ${C.border}`, background:C.bg, color:C.text, fontSize:12, outline:"none", fontFamily:"inherit" }} />
              </div>
            </div>

            {/* ═══ SEÇÃO 4: IMPRESSORA DA COZINHA ═══ */}
            <div style={{ padding:16, borderRadius:14, border:`1px solid #F97316${(loja?.impressao_modo||"off")!=="off"?"50":"20"}`, background:(loja?.impressao_modo||"off")!=="off"?"#F9731608":"transparent", marginBottom:16 }}>
              <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:10, display:"flex", alignItems:"center", gap:6 }}>
                <span style={{ fontSize:16 }}>🍳</span> Impressora da Cozinha
              </div>
              <div style={{ fontSize:11, color:C.textMuted, marginBottom:6 }}>
                Imprime nota simplificada: só itens + adicionais + observações. Fonte grande, sem valores de pagamento.
              </div>
              <div style={{ fontSize:10, color:C.textMuted, marginBottom:12, padding:"6px 10px", background:`${C.bg}`, borderRadius:6, border:`1px dashed ${C.border}` }}>
                <b>Modo atual:</b> <span style={{ color:C.text, fontWeight:700, textTransform:"uppercase" }}>{loja?.impressao_modo || "off"}</span> — definido pelo administrador da plataforma
              </div>

              {/* Config de hardware (IP/Agent) — sempre editável pela loja, fica em localStorage */}
              {(loja?.impressao_modo||"off") === "rede" && (
                <div style={{ padding:12, borderRadius:10, background:`#22C55E08`, border:"1px solid #22C55E25" }}>
                  <div style={{ fontSize:11, color:C.textSoft, marginBottom:10, lineHeight:1.6 }}>
                    A impressora da cozinha precisa estar na <b>mesma rede Wi-Fi</b> com IP fixo. A nota é enviada via servidor API direto para o IP da impressora.
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:8 }}>
                    <div>
                      <label style={{ fontSize:10, fontWeight:600, color:C.textMuted, display:"block", marginBottom:4 }}>IP da Impressora:</label>
                      <input value={printCfg.cozinhaIP||""} onChange={e => savePrintCfg({...printCfg, cozinhaIP:e.target.value})}
                        placeholder="192.168.1.100" style={{ width:"100%", padding:"8px 12px", borderRadius:8, border:`1px solid ${C.border}`, background:C.bg, color:C.text, fontSize:12, outline:"none", fontFamily:"monospace" }} />
                    </div>
                    <div>
                      <label style={{ fontSize:10, fontWeight:600, color:C.textMuted, display:"block", marginBottom:4 }}>Porta:</label>
                      <input value={printCfg.cozinhaPorta||"9100"} onChange={e => savePrintCfg({...printCfg, cozinhaPorta:e.target.value})}
                        placeholder="9100" style={{ width:"100%", padding:"8px 12px", borderRadius:8, border:`1px solid ${C.border}`, background:C.bg, color:C.text, fontSize:12, outline:"none", fontFamily:"monospace" }} />
                    </div>
                  </div>
                  <div style={{ marginTop:8 }}>
                    <label style={{ fontSize:10, fontWeight:600, color:C.textMuted, display:"block", marginBottom:4 }}>Nome (referência):</label>
                    <input value={printCfg.cozinhaNome||""} onChange={e => savePrintCfg({...printCfg, cozinhaNome:e.target.value})}
                      placeholder="Ex: Epson Cozinha" style={{ width:"100%", padding:"8px 12px", borderRadius:8, border:`1px solid ${C.border}`, background:C.bg, color:C.text, fontSize:12, outline:"none", fontFamily:"inherit" }} />
                  </div>
                </div>
              )}

              {(loja?.impressao_modo||"off") === "agent" && (
                <div style={{ padding:12, borderRadius:10, background:`#8B5CF608`, border:"1px solid #8B5CF625" }}>
                  <div style={{ fontSize:11, color:C.textSoft, marginBottom:10, lineHeight:1.6 }}>
                    O <b>MixUp Agent</b> é um programa que roda no computador do restaurante e gerencia múltiplas impressoras. Quando disponível, o painel envia a nota e o Agent imprime na impressora correta.
                  </div>
                  <div>
                    <label style={{ fontSize:10, fontWeight:600, color:C.textMuted, display:"block", marginBottom:4 }}>URL do Agent:</label>
                    <input value={printCfg.agentUrl||"http://localhost:9111"} onChange={e => savePrintCfg({...printCfg, agentUrl:e.target.value})}
                      placeholder="http://localhost:9111" style={{ width:"100%", padding:"8px 12px", borderRadius:8, border:`1px solid ${C.border}`, background:C.bg, color:C.text, fontSize:12, outline:"none", fontFamily:"monospace" }} />
                  </div>
                </div>
              )}

              {(loja?.impressao_modo||"off") === "off" && (
                <div style={{ padding:12, borderRadius:10, background:`${C.bg}`, border:`1px dashed ${C.border}`, fontSize:11, color:C.textMuted, lineHeight:1.6, textAlign:"center" }}>
                  Impressão da cozinha desativada. Solicite ao administrador para ativar o modo <b>Rede</b> ou <b>Agent</b>.
                </div>
              )}
            </div>

            {/* ═══ SEÇÃO 5: CHROME SILENCIOSO ═══ */}
            <div style={{ padding:16, borderRadius:14, background:"#3B82F608", border:"1px solid #3B82F625", marginBottom:16 }}>
              <div style={{ fontSize:12, fontWeight:700, color:"#3B82F6", marginBottom:10 }}>Impressão 100% sem popup (Chrome):</div>
              <div style={{ fontSize:11, color:C.textSoft, lineHeight:2 }}>
                <div><b>Windows:</b> Crie atalho do Chrome com:</div>
                <div style={{ padding:"6px 10px", background:C.bg, borderRadius:6, fontSize:10, fontFamily:"monospace", margin:"4px 0 8px", wordBreak:"break-all" }}>
                  chrome.exe --kiosk-printing
                </div>
                <div><b>Alternativa:</b> <code style={{ background:C.bg, padding:"1px 6px", borderRadius:4, fontSize:10 }}>chrome://flags</code> → busque "System print dialog" → <b>Disabled</b> → Reinicie</div>
              </div>
            </div>

            <button onClick={() => setShowPrintCfg(false)} style={{ width:"100%", padding:14, borderRadius:12, border:"none", background:C.green, color:"#fff", fontWeight:700, fontSize:14, cursor:"pointer" }}>Salvar e Fechar</button>
          </div>
        </div>
      )}

      {showVendas && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.6)", zIndex:900, display:"flex", alignItems:"center", justifyContent:"center" }} onClick={() => setShowVendas(false)}>
          <div style={{ background:C.card, borderRadius:20, padding:28, width:420, border:`1px solid ${C.border}`, animation:"fadeUp .3s ease-out" }} onClick={e=>e.stopPropagation()}>
            <h3 style={{ fontWeight:800, fontSize:22, marginBottom:20 }}>Vendas de Hoje</h3>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:16 }}>
              {[{label:"PIX",value:fmt(vendaPix),color:C.green},{label:"Cartão",value:fmt(vendaCartao),color:C.red},{label:"Dinheiro",value:fmt(vendaDinheiro),color:C.yellow},{label:"Cashback Usado",value:fmt(vendaCash),color:"#8B5CF6"}].map((v,i) => (
                <div key={i} style={{ padding:16, background:C.bg, borderRadius:14, border:`1px solid ${C.border}` }}>
                  <div style={{ width:8, height:8, borderRadius:"50%", background:v.color, marginBottom:10 }} />
                  <div style={{ fontSize:20, fontWeight:800 }}>{v.value}</div>
                  <div style={{ fontSize:12, color:C.textMuted, marginTop:4 }}>{v.label}</div>
                </div>
              ))}
            </div>
            {/* Cashback a Receber — indicador especial */}
            {(vendaCash > 0 || cashbackGerado > 0) && (
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:16 }}>
                <div style={{ padding:16, background:"#8B5CF608", borderRadius:14, border:"1px solid #8B5CF625" }}>
                  <div style={{ fontSize:11, color:"#8B5CF6", fontWeight:700, marginBottom:6 }}>Cashback Recebido (desconto cliente)</div>
                  <div style={{ fontSize:22, fontWeight:800, color:"#8B5CF6" }}>{fmt(vendaCash)}</div>
                  <div style={{ fontSize:10, color:C.textMuted, marginTop:4 }}>{vendasDone.filter(o=>Number(o.cashback_usado||0)>0).length} pedidos com cashback</div>
                </div>
                <div style={{ padding:16, background:`${C.orange}08`, borderRadius:14, border:`1px solid ${C.orange}25` }}>
                  <div style={{ fontSize:11, color:C.orange, fontWeight:700, marginBottom:6 }}>Cashback Gerado (a pagar)</div>
                  <div style={{ fontSize:22, fontWeight:800, color:C.orange }}>{fmt(cashbackGerado)}</div>
                  <div style={{ fontSize:10, color:C.textMuted, marginTop:4 }}>Creditado nos clientes hoje</div>
                </div>
              </div>
            )}
            <div style={{ padding:18, background:`${C.green}10`, borderRadius:14, border:`1px solid ${C.green}30`, textAlign:"center" }}>
              <div style={{ fontSize:13, color:C.textMuted, marginBottom:4 }}>Total do Dia</div>
              <div style={{ fontSize:28, fontWeight:800, color:C.green }}>{fmt(totalVendas)}</div>
              <div style={{ fontSize:12, color:C.textMuted, marginTop:4 }}>{vendasDone.length} pedidos</div>
            </div>
            <button onClick={() => setShowVendas(false)} style={{ width:"100%", padding:14, borderRadius:12, border:"none", background:C.border, color:C.textSoft, fontWeight:700, fontSize:14, marginTop:16, cursor:"pointer" }}>Fechar</button>
          </div>
        </div>
      )}

      {showSaldo && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.6)", zIndex:900, display:"flex", alignItems:"center", justifyContent:"center" }} onClick={() => { if(!recargaPolling){ setShowSaldo(false); setRecargaPix(null); setRecargaValor(""); } }}>
          <div style={{ background:C.card, borderRadius:20, padding:28, width:400, maxHeight:"90vh", overflowY:"auto", border:`1px solid ${C.border}`, animation:"fadeUp .3s ease-out", textAlign:"center" }} onClick={e=>e.stopPropagation()}>
            <h3 style={{ fontWeight:800, fontSize:20, marginBottom:6 }}>Saldo de Entregas</h3>
            <p style={{ fontSize:12, color:C.textMuted, marginBottom:16 }}>Debitado automaticamente a cada entrega aceita.</p>
            <div style={{ padding:20, background:`${C.blue}10`, borderRadius:14, border:`1px solid ${C.blue}30`, marginBottom:20 }}>
              <div style={{ fontSize:32, fontWeight:900, color:C.blue }}>{saldo !== null ? fmt(saldo) : "—"}</div>
            </div>

            {!recargaPix ? (
              <div>
                <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:10 }}>Comprar crédito via PIX</div>
                <div style={{ display:"flex", gap:8, marginBottom:12 }}>
                  <input type="text" inputMode="decimal" value={recargaValor} onChange={e=>{
                    let v=e.target.value.replace(/[^\d,\.]/g,"");
                    v=v.replace(",",".");
                    setRecargaValor(v);
                  }} placeholder="0,00" style={{ flex:1, padding:"10px 14px", borderRadius:10, border:`1px solid ${C.border}`, background:C.bg, color:C.text, fontSize:18, fontWeight:700, textAlign:"center", outline:"none" }}/>
                  <button onClick={async()=>{
                    const val=parseFloat(recargaValor);
                    if(!val||val<1){alert("Valor mínimo: R$ 1,00");return;}
                    setRecargaLoading(true);
                    const _t=localStorage.getItem("mixup_loja_token");
                    const _h=(ct)=>{const h={};if(ct)h["Content-Type"]="application/json";if(_t)h["Authorization"]="Bearer "+_t;return h;};
                    try{
                      const r=await fetch("https://mixupbr.com.br/api/pagamentos/plataforma.php?mod=criar_pix",{method:"POST",headers:_h(true),body:JSON.stringify({loja_id:loja?.id||1,valor:val,tipo:"recarga",descricao:"Recarga saldo entregas"})});
                      const d=await r.json();
                      if(d.ok&&d.qr_code){
                        setRecargaPix({qr:d.qr_code,qr64:d.qr_code_base64,pagId:d.pagamento_id,valor:val});
                        setRecargaPolling(true);
                        const poll=setInterval(async()=>{
                          try{
                            const s=await fetch(`https://mixupbr.com.br/api/pagamentos/plataforma.php?mod=status&pagamento_id=${d.pagamento_id}`,{headers:_h(false)});
                            const sd=await s.json();
                            if(sd.ok&&sd.status==="aprovado"){
                              clearInterval(poll);
                              setRecargaPolling(false);
                              const novoSaldo=Number(saldo||0)+val;
                              try{await put("lojas",loja?.id||1,{saldo_credito:novoSaldo});}catch(e){}
                              setSaldo(novoSaldo);
                              setRecargaPix(null);setRecargaValor("");
                              alert("Pagamento confirmado!\nR$ "+val.toFixed(2)+" adicionado ao saldo.\nNovo saldo: R$ "+novoSaldo.toFixed(2));
                            }
                          }catch(e){}
                        },4000);
                        recargaPollRef.current=poll;
                      }else{alert(d.error||"Erro ao gerar PIX");}
                    }catch(e){alert("Erro: "+e.message);}
                    setRecargaLoading(false);
                  }} disabled={recargaLoading} style={{ padding:"10px 20px", borderRadius:10, border:"none", background:C.green||"#22C55E", color:"#fff", fontWeight:700, fontSize:14, cursor:"pointer", opacity:recargaLoading?0.6:1 }}>
                    {recargaLoading?"Gerando...":"Gerar PIX"}
                  </button>
                </div>
                <div style={{ display:"flex", gap:6, justifyContent:"center" }}>
                  {[10,25,50,100].map(v=>(
                    <button key={v} onClick={()=>setRecargaValor(String(v))} style={{ padding:"6px 14px", borderRadius:8, border:`1px solid ${C.border}`, background:recargaValor===String(v)?`${C.blue}20`:"transparent", color:recargaValor===String(v)?C.blue:C.textMuted, fontSize:12, fontWeight:700, cursor:"pointer" }}>R$ {v}</button>
                  ))}
                </div>
              </div>
            ) : (
              <div>
                <div style={{ fontSize:14, fontWeight:700, color:C.green||"#22C55E", marginBottom:8 }}>Pague {fmt(recargaPix.valor)} via PIX</div>
                {recargaPix.qr64 && <img src={`data:image/png;base64,${recargaPix.qr64}`} style={{ width:200, height:200, margin:"0 auto 12px", display:"block", borderRadius:12 }} alt="QR PIX"/>}
                <div style={{ background:C.bg, borderRadius:10, padding:10, marginBottom:12, wordBreak:"break-all", fontSize:10, color:C.textMuted, cursor:"pointer" }} onClick={()=>{navigator.clipboard?.writeText(recargaPix.qr);alert("Código copiado!")}}>
                  {recargaPix.qr?.slice(0,80)}... <span style={{ color:C.blue, fontWeight:700 }}>Copiar</span>
                </div>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:8, marginBottom:12 }}>
                  <div style={{ width:10, height:10, borderRadius:5, background:"#FFB74D", animation:"glow 1.5s infinite" }}/>
                  <span style={{ fontSize:13, color:"#FFB74D", fontWeight:600 }}>Aguardando pagamento...</span>
                </div>
                <button onClick={()=>{if(recargaPollRef.current)clearInterval(recargaPollRef.current);setRecargaPolling(false);setRecargaPix(null);}} style={{ padding:"10px 20px", borderRadius:10, border:"none", background:C.border, color:C.textSoft, fontWeight:700, fontSize:13, cursor:"pointer" }}>Cancelar</button>
              </div>
            )}

            {!recargaPix && <button onClick={() => setShowSaldo(false)} style={{ width:"100%", padding:13, borderRadius:12, border:"none", background:C.border, color:C.textSoft, fontWeight:700, fontSize:14, cursor:"pointer", marginTop:16 }}>Fechar</button>}
          </div>
        </div>
      )}

      {/* MODAL COMISSÃO */}
      {showComissao && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.6)", zIndex:900, display:"flex", alignItems:"center", justifyContent:"center" }} onClick={() => { if(!comPolling){ setShowComissao(false); setComPix(null); } }}>
          <div style={{ background:C.card, borderRadius:20, padding:28, width:420, maxHeight:"90vh", overflowY:"auto", border:`1px solid ${C.border}`, animation:"fadeUp .3s ease-out", textAlign:"center" }} onClick={e=>e.stopPropagation()}>
            <h3 style={{ fontWeight:800, fontSize:20, marginBottom:6 }}>Comissão do Dia</h3>
            <p style={{ fontSize:12, color:C.textMuted, marginBottom:16 }}>{comissaoPercent}% sobre o subtotal de vendas pelo app do cliente.</p>
            <div style={{ padding:16, background:`${C.red}10`, borderRadius:14, border:`1px solid ${C.red}30`, marginBottom:16 }}>
              <div style={{ fontSize:28, fontWeight:900, color:C.red }}>{fmt(comissaoValor)}</div>
              <div style={{ fontSize:12, color:C.textMuted, marginTop:4 }}>Subtotal app: {fmt(subtotalComissao)} x {comissaoPercent}%</div>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16, textAlign:"left" }}>
              <div style={{ background:C.bg, borderRadius:10, padding:12 }}>
                <div style={{ fontSize:11, color:C.textMuted }}>Vendas (total)</div>
                <div style={{ fontSize:16, fontWeight:800, color:C.greenLight||"#22C55E" }}>{fmt(totalVendas)}</div>
              </div>
              <div style={{ background:C.bg, borderRadius:10, padding:12 }}>
                <div style={{ fontSize:11, color:C.textMuted }}>Pedidos</div>
                <div style={{ fontSize:16, fontWeight:800, color:C.text }}>{vendasDone.length}</div>
              </div>
            </div>

            {!comPix && comissaoValor > 0 && (
              <button onClick={async()=>{
                if(!confirm("Pagar comissão de "+fmt(comissaoValor)+" via PIX?"))return;
                setComPagLoading(true);
                const _t=localStorage.getItem("mixup_loja_token");
                const _h=(ct)=>{const h={};if(ct)h["Content-Type"]="application/json";if(_t)h["Authorization"]="Bearer "+_t;return h;};
                try{
                  const r=await fetch("https://mixupbr.com.br/api/pagamentos/plataforma.php?mod=criar_pix",{method:"POST",headers:_h(true),body:JSON.stringify({loja_id:loja?.id||1,valor:comissaoValor,tipo:"comissao",descricao:"Comissão do dia - "+(loja?.nome||"Loja")})});
                  const d=await r.json();
                  if(d.ok&&d.qr_code){
                    setComPix({qr:d.qr_code,qr64:d.qr_code_base64,pagId:d.pagamento_id,valor:comissaoValor});
                    setComPolling(true);
                    const poll=setInterval(async()=>{
                      try{
                        const s=await fetch(`https://mixupbr.com.br/api/pagamentos/plataforma.php?mod=status&pagamento_id=${d.pagamento_id}`,{headers:_h(false)});
                        const sd=await s.json();
                        if(sd.ok&&sd.status==="aprovado"){
                          clearInterval(poll);setComPolling(false);setComPix(null);
                          alert("Comissão paga com sucesso!\nValor: "+fmt(comissaoValor));
                        }
                      }catch(e){}
                    },4000);
                    comPollRef.current=poll;
                  }else{alert(d.error||"Erro ao gerar PIX");}
                }catch(e){alert("Erro: "+e.message);}
                setComPagLoading(false);
              }} disabled={comPagLoading} style={{ width:"100%", padding:14, borderRadius:12, border:"none", background:C.green||"#22C55E", color:"#fff", fontWeight:700, fontSize:15, cursor:"pointer", marginBottom:12, opacity:comPagLoading?0.6:1 }}>
                {comPagLoading?"Gerando PIX...":"Pagar Comissão via PIX"}
              </button>
            )}

            {comPix && (
              <div>
                <div style={{ fontSize:14, fontWeight:700, color:C.green||"#22C55E", marginBottom:8 }}>Pague {fmt(comPix.valor)} via PIX</div>
                {comPix.qr64 && <img src={`data:image/png;base64,${comPix.qr64}`} style={{ width:200, height:200, margin:"0 auto 12px", display:"block", borderRadius:12 }} alt="QR PIX"/>}
                <div style={{ background:C.bg, borderRadius:10, padding:10, marginBottom:12, wordBreak:"break-all", fontSize:10, color:C.textMuted, cursor:"pointer" }} onClick={()=>{navigator.clipboard?.writeText(comPix.qr);alert("Código copiado!")}}>
                  {comPix.qr?.slice(0,80)}... <span style={{ color:C.blue, fontWeight:700 }}>Copiar</span>
                </div>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:8, marginBottom:12 }}>
                  <div style={{ width:10, height:10, borderRadius:5, background:"#FFB74D", animation:"glow 1.5s infinite" }}/>
                  <span style={{ fontSize:13, color:"#FFB74D", fontWeight:600 }}>Aguardando pagamento...</span>
                </div>
                <button onClick={()=>{if(comPollRef.current)clearInterval(comPollRef.current);setComPolling(false);setComPix(null);}} style={{ padding:"10px 20px", borderRadius:10, border:"none", background:C.border, color:C.textSoft, fontWeight:700, fontSize:13, cursor:"pointer" }}>Cancelar</button>
              </div>
            )}

            {!comPix && <button onClick={() => setShowComissao(false)} style={{ width:"100%", padding:13, borderRadius:12, border:"none", background:C.border, color:C.textSoft, fontWeight:700, fontSize:14, cursor:"pointer" }}>Fechar</button>}
          </div>
        </div>
      )}

      {/* MODAL ENTREGA AVULSA */}
      {showAvulsa && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.6)", zIndex:900, display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ background:C.card, borderRadius:20, padding:28, width:480, maxHeight:"90vh", overflowY:"auto", border:`1px solid ${C.border}`, animation:"fadeUp .3s ease-out" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
              <div>
                <h3 style={{ fontWeight:800, fontSize:20, marginBottom:2 }}>Entrega Avulsa</h3>
                <p style={{ fontSize:12, color:C.textMuted }}>{avulsaStep===1?"":avulsaStep===3?"Cadastrar novo cliente":"Dados da entrega"}</p>
              </div>
              <div style={{ display:"flex", gap:8 }}>
                {avulsaStep > 1 && <button onClick={() => setAvulsaStep(avulsaStep-1)} style={{ padding:"6px 14px", borderRadius:8, border:`1px solid ${C.border}`, background:"transparent", color:C.textSoft, fontSize:12, fontWeight:600, cursor:"pointer" }}>← Voltar</button>}
                <button onClick={() => { setShowAvulsa(false); setAvulsaStep(1); setAvulsaBusca(""); setAvulsaResultados([]); setAvulsaTaxa(null); setAvulsaTaxaInfo(""); setAvulsaKmInfo(""); setBairroBusca(""); setBairroAberto(false); setAvulsaCliente(null); setAvulsaForm({ nome:"", telefone:"", endereco:"", complemento:"", bairro:"", cidade:"Ponta Porã", valor:"", obs:"", ifood_codigo:"" }); resetLocalizacao(); }} title="Fechar" style={{ width:32, height:32, borderRadius:8, border:`1px solid ${C.border}`, background:"transparent", color:C.textSoft, fontSize:18, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", lineHeight:1 }}>×</button>
              </div>
            </div>

            {avulsaStep === 1 && (<div>
              <div style={{ display:"flex", gap:8, marginBottom:16 }}>
                <input placeholder="Nome ou telefone..." value={avulsaBusca} onChange={e=>setAvulsaBusca(e.target.value)} onKeyDown={e=>e.key==="Enter"&&buscarCliente()} style={{ ...iS, flex:1 }} />
                <button onClick={buscarCliente} disabled={avulsaLoading} style={{ padding:"10px 18px", borderRadius:8, border:"none", background:C.blue, color:"#fff", fontWeight:700, fontSize:13, cursor:"pointer" }}>{avulsaLoading?"...":"Buscar"}</button>
              </div>
              {avulsaResultados.length > 0 && <div style={{ marginBottom:12 }}>{avulsaResultados.map(cli => (
                <div key={cli.id} onClick={() => selecionarClienteAvulsa(cli)} style={{ padding:"12px 14px", borderRadius:12, border:`1px solid ${C.border}`, background:C.bg, marginBottom:8, cursor:"pointer", display:"flex", alignItems:"center", gap:12 }}>
                  <div style={{ width:44, height:44, borderRadius:10, background:C.card, display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, flexShrink:0, overflow:"hidden" }}>
                    {cli.foto_ref_url ? <img src={String(cli.foto_ref_url).startsWith("http") ? cli.foto_ref_url : ("https://mixupbr.com.br"+cli.foto_ref_url)} style={{ width:"100%", height:"100%", objectFit:"cover" }} /> : (cli.origem==="avulso"?"🏠":"👤")}
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700, fontSize:14 }}>{cli.nome}</div>
                    <div style={{ fontSize:12, color:C.textMuted }}>{cli.telefone}</div>
                  </div>
                  <span style={{ fontSize:12, color:C.blue, fontWeight:700 }}>→</span>
                </div>
              ))}</div>}
              <div style={{ display:"flex", gap:8 }}>
                {!modoSemGPS && <button onClick={novoClienteAvulsa} style={{ flex:1, padding:13, borderRadius:12, border:`1.5px dashed ${C.border}`, background:"transparent", color:C.textSoft, fontWeight:700, fontSize:13, cursor:"pointer" }}>Sem cadastro</button>}
                <button onClick={novoClienteAvulsa} style={{ flex:1, padding:13, borderRadius:12, border:"none", background:`${C.blue}20`, color:C.blue, fontWeight:700, fontSize:13, cursor:"pointer" }}>+ Cadastrar</button>
              </div>
            </div>)}

            {avulsaStep === 3 && (<div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
                <div><label style={{ fontSize:11, fontWeight:700, color:C.textMuted, display:"block", marginBottom:5 }}>Nome *</label><input value={avulsaForm.nome} onChange={e=>setAvulsaForm({...avulsaForm, nome:e.target.value})} style={iS} /></div>
                <div><label style={{ fontSize:11, fontWeight:700, color:C.textMuted, display:"block", marginBottom:5 }}>Telefone</label><input value={avulsaForm.telefone} onChange={e=>setAvulsaForm({...avulsaForm, telefone:e.target.value})} style={iS} /></div>
              </div>
              <div style={{ marginBottom:10 }}><label style={{ fontSize:11, fontWeight:700, color:C.textMuted, display:"block", marginBottom:5 }}>Endereço</label><input value={avulsaForm.endereco} onChange={e=>setAvulsaForm({...avulsaForm, endereco:e.target.value})} style={iS} /></div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
                <div><label style={{ fontSize:11, fontWeight:700, color:C.textMuted, display:"block", marginBottom:5 }}>Bairro</label><input value={avulsaForm.bairro} onChange={e=>setAvulsaForm({...avulsaForm, bairro:e.target.value})} style={iS} /></div>
                <div><label style={{ fontSize:11, fontWeight:700, color:C.textMuted, display:"block", marginBottom:5 }}>Cidade</label><input value={avulsaForm.cidade} onChange={e=>setAvulsaForm({...avulsaForm, cidade:e.target.value})} style={iS} /></div>
              </div>

              {/* Localização — campos editáveis + geocoding */}
              <div style={{ padding:12, borderRadius:10, background:`#22C55E08`, border:`1px solid #22C55E25`, marginBottom:16 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                  <span style={{ fontSize:11, fontWeight:800, color:C.greenLight, letterSpacing:"0.04em" }}>📍 LOCALIZAÇÃO</span>
                  {avulsaLat && avulsaLng && <span style={{ fontSize:10, fontWeight:700, color:C.greenLight, background:`${C.greenLight}18`, padding:"2px 8px", borderRadius:6 }}>✓ Obtida</span>}
                </div>
                {avulsaLat && avulsaLng && (
                  <div style={{ borderRadius:8, overflow:"hidden", border:`1px solid ${C.border}`, height:80, marginBottom:8 }}>
                    <img src={`https://maps.googleapis.com/maps/api/staticmap?center=${avulsaLat},${avulsaLng}&zoom=16&size=460x80&markers=color:red|${avulsaLat},${avulsaLng}&key=AIzaSyDKRGxT4nBsTYRkSJ_zrmMgb8cYI00WGK8`}
                      style={{ width:"100%", height:"100%", objectFit:"cover" }} alt="Mapa" />
                  </div>
                )}
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:8 }}>
                  <div>
                    <label style={{ fontSize:10, fontWeight:700, color:C.greenLight, display:"block", marginBottom:4 }}>Latitude</label>
                    <input type="number" step="any" value={avulsaLat||""} onChange={e => setAvulsaLat(e.target.value ? parseFloat(e.target.value) : null)}
                      placeholder="-22.53000" style={{ ...iS, fontSize:11, padding:"7px 10px", borderColor:`#22C55E40` }} />
                  </div>
                  <div>
                    <label style={{ fontSize:10, fontWeight:700, color:C.greenLight, display:"block", marginBottom:4 }}>Longitude</label>
                    <input type="number" step="any" value={avulsaLng||""} onChange={e => setAvulsaLng(e.target.value ? parseFloat(e.target.value) : null)}
                      placeholder="-55.72000" style={{ ...iS, fontSize:11, padding:"7px 10px", borderColor:`#22C55E40` }} />
                  </div>
                </div>
                <button onClick={geocodarEndereco} disabled={geoLoading || !avulsaForm.endereco}
                  style={{ width:"100%", padding:7, borderRadius:8, border:`1px solid ${C.blue}40`, background:`${C.blue}08`, color:C.blue, fontWeight:700, fontSize:11, cursor:"pointer", opacity:(geoLoading||!avulsaForm.endereco)?0.5:1 }}>
                  {geoLoading ? "Buscando..." : "🔍 Buscar pelo endereço acima"}
                </button>
              </div>

              <div style={{ display:"flex", gap:10 }}>
                <button onClick={() => setAvulsaStep(1)} style={{ flex:1, padding:13, borderRadius:12, border:`1px solid ${C.border}`, background:"transparent", color:C.textSoft, fontWeight:700, cursor:"pointer" }}>Cancelar</button>
                <button onClick={async () => {
                  if (!avulsaForm.nome) { notify("Nome obrigatório", false); return; }
                  try {
                    const resp = await fetch("https://mixupbr.com.br/api/loja_config/index.php", { method:"POST", headers:{"Content-Type":"application/json"},
                      body:JSON.stringify({ acao:"salvar_cliente_avulso", loja_id:lojaId||1, nome:avulsaForm.nome, telefone:avulsaForm.telefone, endereco:avulsaForm.endereco, bairro:avulsaForm.bairro, cidade:avulsaForm.cidade, obs_entrega:avulsaForm.obs||"", latitude:avulsaLat, longitude:avulsaLng }) });
                    const txt = await resp.text();
                    if (!txt) { notify("API vazia", false); return; }
                    let salvRes; try { salvRes = JSON.parse(txt); } catch { notify("API: "+txt.slice(0,120), false); return; }
                    if (salvRes.error) { notify("Erro: "+salvRes.error, false); return; }
                    notify("Cliente cadastrado!"); setAvulsaCliente({ ...avulsaForm, id:salvRes.id, origem:"avulso", latitude:avulsaLat, longitude:avulsaLng }); setAvulsaStep(2);
                  } catch(err) { notify("Erro: "+(err?.message||""), false); }
                }} style={{ flex:2, padding:13, borderRadius:12, border:"none", background:C.blue, color:"#fff", fontWeight:700, cursor:"pointer" }}>Salvar e continuar</button>
              </div>
            </div>)}

            {avulsaStep === 2 && (<div>
              {avulsaCliente && <div style={{ padding:"10px 14px", borderRadius:10, background:`${C.blue}10`, border:`1px solid ${C.blue}30`, marginBottom:16, fontSize:13 }}><strong>{avulsaCliente.nome}</strong> · {avulsaCliente.telefone}</div>}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
                <div><label style={{ fontSize:11, fontWeight:700, color:C.textMuted, display:"block", marginBottom:5 }}>Nome *</label><input value={avulsaForm.nome} onChange={e=>setAvulsaForm({...avulsaForm, nome:e.target.value})} style={iS} /></div>
                <div><label style={{ fontSize:11, fontWeight:700, color:C.textMuted, display:"block", marginBottom:5 }}>Telefone</label><input value={avulsaForm.telefone} onChange={e=>setAvulsaForm({...avulsaForm, telefone:e.target.value})} style={iS} /></div>
              </div>
              <div style={{ marginBottom:10, position:"relative" }}>
                <label style={{ fontSize:11, fontWeight:700, color:C.textMuted, display:"block", marginBottom:5 }}>Endereço *</label>
                <input value={avulsaForm.endereco}
                  onChange={e=>{setAvulsaForm({...avulsaForm, endereco:e.target.value}); buscarEndereco(e.target.value);}}
                  onFocus={() => setEnderecoFocus(true)}
                  onBlur={() => setTimeout(() => setEnderecoFocus(false), 200)}
                  placeholder="Comece a digitar para buscar..."
                  style={iS}
                  autoComplete="off" />
                {enderecoFocus && enderecoSug.length > 0 && (
                  <div style={{ position:"absolute", left:0, right:0, top:"100%", zIndex:50, background:C.card, border:`1px solid ${C.blue}40`, borderRadius:10, marginTop:4, maxHeight:240, overflowY:"auto", boxShadow:"0 8px 24px rgba(0,0,0,.4)" }}>
                    {enderecoSug.map(s => (
                      <div key={s.place_id} onClick={() => escolherEndereco(s)}
                        onMouseDown={e => e.preventDefault()}
                        style={{ padding:"10px 14px", cursor:"pointer", borderBottom:`1px solid ${C.border}30` }}
                        onMouseOver={e => e.currentTarget.style.background = C.bg}
                        onMouseOut={e => e.currentTarget.style.background = "transparent"}>
                        <div style={{ fontSize:13, fontWeight:700, color:C.text }}>📍 {s.principal || s.descricao}</div>
                        {s.secundario && <div style={{ fontSize:11, color:C.textMuted, marginTop:2 }}>{s.secundario}</div>}
                      </div>
                    ))}
                  </div>
                )}
                {enderecoBusy && <div style={{ position:"absolute", right:14, top:36, fontSize:11, color:C.textMuted }}>Buscando...</div>}
              </div>
              <div style={{ marginBottom:10 }}><label style={{ fontSize:11, fontWeight:700, color:C.textMuted, display:"block", marginBottom:5 }}>Complemento</label><input value={avulsaForm.complemento||""} onChange={e=>setAvulsaForm({...avulsaForm, complemento:e.target.value})} placeholder="Apto, bloco, referência..." style={iS} /></div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
                <div><label style={{ fontSize:11, fontWeight:700, color:C.textMuted, display:"block", marginBottom:5 }}>Bairro</label><input value={avulsaForm.bairro} onChange={e=>setAvulsaForm({...avulsaForm, bairro:e.target.value})} style={iS} /></div>
                <div><label style={{ fontSize:11, fontWeight:700, color:C.textMuted, display:"block", marginBottom:5 }}>Valor (R$)</label><input type="number" value={avulsaForm.valor} onChange={e=>setAvulsaForm({...avulsaForm, valor:e.target.value})} placeholder="Valor do pedido (opcional)" style={iS} /></div>
              </div>

              {/* iFood — código pra entregador finalizar lá depois */}
              <div style={{ marginBottom:10, padding:10, borderRadius:10, background:"#EA1D2C0F", border:"1px solid #EA1D2C30" }}>
                <label style={{ fontSize:11, fontWeight:700, color:"#EA1D2C", display:"block", marginBottom:5, letterSpacing:"0.04em" }}>🍔 PEDIDO DO IFOOD? (OPCIONAL)</label>
                <input value={avulsaForm.ifood_codigo||""}
                  onChange={e=>setAvulsaForm({...avulsaForm, ifood_codigo:e.target.value.replace(/\D/g,"").slice(0,8)})}
                  placeholder="Cole os 8 dígitos do nº do pedido iFood"
                  maxLength={8}
                  style={{ ...iS, fontFamily:"monospace", fontSize:15, letterSpacing:"2px" }} />
                <div style={{ fontSize:10, color:C.textMuted, marginTop:4 }}>
                  Se preenchido, o entregador verá um botão "Finalizar no iFood" com esse código pronto pra usar.
                </div>
              </div>

              {/* ═══ LOCALIZAÇÃO — 3 métodos (só modo km) ═══ */}
              {!modoSemGPS && <div style={{ padding:12, borderRadius:12, background:`#22C55E08`, border:`1px solid #22C55E25`, marginBottom:12 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                  <span style={{ fontSize:11, fontWeight:800, color:C.greenLight, letterSpacing:"0.04em" }}>📍 LOCALIZAÇÃO</span>
                  {avulsaLat && avulsaLng && <span style={{ fontSize:10, fontWeight:700, color:C.greenLight, background:`${C.greenLight}18`, padding:"2px 8px", borderRadius:6 }}>✓ Obtida</span>}
                </div>

                {/* Mini mapa preview quando tem lat/lng */}
                {avulsaLat && avulsaLng ? (
                  <div style={{ marginBottom:8 }}>
                    <div style={{ borderRadius:10, overflow:"hidden", border:`1px solid ${C.border}`, height:120, position:"relative" }}>
                      <img src={`https://maps.googleapis.com/maps/api/staticmap?center=${avulsaLat},${avulsaLng}&zoom=16&size=460x120&markers=color:red|${avulsaLat},${avulsaLng}&key=AIzaSyDKRGxT4nBsTYRkSJ_zrmMgb8cYI00WGK8`}
                        style={{ width:"100%", height:"100%", objectFit:"cover" }} alt="Mapa" />
                    </div>
                    {avulsaEndGeo && <div style={{ fontSize:10, color:C.greenLight, marginTop:4, fontWeight:600 }}>📍 {avulsaEndGeo}</div>}
                    <div style={{ fontSize:9, color:C.textMuted, marginTop:2 }}>Lat: {avulsaLat.toFixed(5)} · Lng: {avulsaLng.toFixed(5)}</div>
                    <button onClick={() => { setAvulsaLat(null); setAvulsaLng(null); setAvulsaEndGeo(""); }} style={{ marginTop:6, padding:"4px 10px", borderRadius:6, border:`1px solid ${C.border}`, background:"transparent", color:C.textMuted, fontSize:10, fontWeight:600, cursor:"pointer" }}>✕ Limpar localização</button>
                  </div>
                ) : (
                  <div>
                    {/* Método 1: Pedir localização via link */}
                    {!locCode ? (
                      <button onClick={criarLinkLoc} style={{ width:"100%", padding:10, borderRadius:10, border:`1.5px dashed ${C.greenLight}50`, background:`${C.greenLight}08`, color:C.greenLight, fontWeight:700, fontSize:12, cursor:"pointer", marginBottom:8, display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}>
                        📲 Gerar link p/ cliente (GPS)
                      </button>
                    ) : (
                      <div style={{ padding:10, borderRadius:10, background:C.card, border:`1px solid ${C.border}`, marginBottom:8 }}>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
                          <span style={{ fontSize:11, fontWeight:700, color: locStatus==="recebido" ? C.greenLight : C.yellow }}>
                            {locStatus === "recebido" ? "✅ Localização recebida!" : "⏳ Aguardando cliente..."}
                          </span>
                          {locPolling && <div style={{ width:14, height:14, border:"2px solid "+C.border, borderTopColor:C.greenLight, borderRadius:"50%", animation:"spin .8s linear infinite" }} />}
                        </div>
                        <div style={{ fontSize:10, color:C.textMuted, wordBreak:"break-all", marginBottom:6 }}>{locLink}</div>
                        <div style={{ display:"flex", gap:6 }}>
                          <button onClick={async () => { try { await navigator.clipboard.writeText(locMsgWp); notify("Mensagem copiada!"); } catch {} }}
                            style={{ flex:1, padding:6, borderRadius:6, border:`1px solid #25D36640`, background:"#25D36612", color:"#25D366", fontSize:10, fontWeight:700, cursor:"pointer" }}>
                            Copiar p/ WhatsApp
                          </button>
                          <button onClick={() => { resetLocalizacao(); }} style={{ padding:6, borderRadius:6, border:`1px solid ${C.border}`, background:"transparent", color:C.textMuted, fontSize:10, cursor:"pointer" }}>✕</button>
                        </div>
                      </div>
                    )}

                    {/* Método 2: Geocoding automático */}
                    <button onClick={geocodarEndereco} disabled={geoLoading || !avulsaForm.endereco}
                      style={{ width:"100%", padding:8, borderRadius:8, border:`1px solid ${C.blue}40`, background:`${C.blue}08`, color:C.blue, fontWeight:700, fontSize:11, cursor:"pointer", marginBottom:4, opacity:(geoLoading||!avulsaForm.endereco)?0.5:1 }}>
                      {geoLoading ? "Buscando..." : "🔍 Buscar no mapa pelo endereço"}
                    </button>

                    <div style={{ fontSize:9, color:C.textMuted, textAlign:"center", marginTop:4 }}>
                      Link Maps no WhatsApp? Cole o texto acima que detectamos automaticamente
                    </div>
                  </div>
                )}
              </div>}

              {/* ═══ OBTER ENDEREÇO — bairro/fixo (sem mapa, sem rota, só pega o nome da rua) ═══ */}
              {modoSemGPS && (
                <div style={{ padding:12, borderRadius:12, background:`#22C55E08`, border:`1px solid #22C55E25`, marginBottom:12 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                    <span style={{ fontSize:11, fontWeight:800, color:C.greenLight, letterSpacing:"0.04em" }}>📍 OBTER ENDEREÇO DO CLIENTE</span>
                    {avulsaEndGeo && <span style={{ fontSize:10, fontWeight:700, color:C.greenLight, background:`${C.greenLight}18`, padding:"2px 8px", borderRadius:6 }}>✓ Recebido</span>}
                  </div>
                  {avulsaEndGeo ? (
                    <div style={{ padding:10, borderRadius:8, background:C.card, border:`1px solid ${C.greenLight}30` }}>
                      <div style={{ fontSize:12, fontWeight:700, color:C.greenLight, marginBottom:4 }}>📍 {avulsaEndGeo}</div>
                      <div style={{ fontSize:10, color:C.textMuted }}>Endereço preenchido automaticamente</div>
                    </div>
                  ) : (
                    <div>
                      {!locCode ? (
                        <button onClick={criarLinkLoc} style={{ width:"100%", padding:10, borderRadius:10, border:`1.5px dashed ${C.greenLight}50`, background:`${C.greenLight}08`, color:C.greenLight, fontWeight:700, fontSize:12, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}>
                          📲 Enviar link para cliente compartilhar endereço
                        </button>
                      ) : (
                        <div style={{ padding:10, borderRadius:10, background:C.card, border:`1px solid ${C.border}` }}>
                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
                            <span style={{ fontSize:11, fontWeight:700, color: locStatus==="recebido" ? C.greenLight : C.yellow }}>
                              {locStatus === "recebido" ? "✅ Endereço recebido!" : "⏳ Aguardando cliente..."}
                            </span>
                            {locPolling && <div style={{ width:14, height:14, border:"2px solid "+C.border, borderTopColor:C.greenLight, borderRadius:"50%", animation:"spin .8s linear infinite" }} />}
                          </div>
                          <div style={{ display:"flex", gap:6 }}>
                            <button onClick={async () => { try { await navigator.clipboard.writeText(locMsgWp); notify("Mensagem copiada!"); } catch {} }}
                              style={{ flex:1, padding:6, borderRadius:6, border:`1px solid #25D36640`, background:"#25D36612", color:"#25D366", fontSize:10, fontWeight:700, cursor:"pointer" }}>
                              Copiar p/ WhatsApp
                            </button>
                            <button onClick={resetLocalizacao} style={{ padding:6, borderRadius:6, border:`1px solid ${C.border}`, background:"transparent", color:C.textMuted, fontSize:10, cursor:"pointer" }}>✕</button>
                          </div>
                        </div>
                      )}
                      <div style={{ fontSize:9, color:C.textMuted, textAlign:"center", marginTop:6 }}>O cliente recebe um link, clica, e o endereço é preenchido automaticamente</div>
                    </div>
                  )}
                </div>
              )}

              <div style={{ padding:12, borderRadius:10, background:`${C.blue}08`, border:`1px solid ${C.blue}25`, marginBottom:12 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                  <span style={{ fontSize:11, fontWeight:700, color:C.blue }}>TAXA DE ENTREGA {lojaModo?.tipo === 'bairro' ? "(por bairro)" : lojaModo?.tipo === 'fixo' ? "(fixa)" : avulsaLat && avulsaLng ? "(auto)" : ""}</span>
                  {lojaModo?.tipo === 'km' && <button onClick={() => calcularTaxaAvulsa()} disabled={avulsaCalcTaxa} style={{ padding:"4px 12px", borderRadius:6, border:`1px solid ${C.blue}40`, background:`${C.blue}15`, color:C.blue, cursor:"pointer", fontWeight:700, fontSize:11 }}>{avulsaCalcTaxa?"Calculando...":"Recalcular"}</button>}
                </div>

                {/* Modo BAIRRO: busca de bairros com filtro */}
                {lojaModo?.tipo === 'bairro' && lojaModo.bairros?.length > 0 && (
                  <div style={{ marginBottom:10, position:"relative" }}>
                    <label style={{ fontSize:10, fontWeight:700, color:C.textMuted, display:"block", marginBottom:4 }}>Buscar bairro:</label>
                    <input value={bairroBusca} onChange={e => {
                      setBairroBusca(e.target.value);
                      setBairroAberto(true);
                      // Se limpar, resetar taxa
                      if (!e.target.value.trim()) { setAvulsaTaxa(null); setAvulsaTaxaInfo(""); setAvulsaForm(f => ({...f, bairro:""})); }
                    }}
                    onFocus={() => setBairroAberto(true)}
                    onBlur={() => setTimeout(() => setBairroAberto(false), 200)}
                    placeholder="Digite o bairro..."
                    style={{ width:"100%", padding:"10px 12px", borderRadius:8, border:`1px solid ${avulsaTaxa!==null?C.greenLight+"80":C.blue+"40"}`, background:C.card, color:C.text, fontSize:13, fontWeight:600, outline:"none", fontFamily:"inherit" }} />
                    {bairroAberto && (()=>{
                      const filtro = bairroBusca.toLowerCase().trim();
                      const lista = filtro ? lojaModo.bairros.filter(b => b.bairro_nome.toLowerCase().includes(filtro)) : lojaModo.bairros;
                      if (lista.length === 0) return <div style={{ padding:8, fontSize:11, color:C.textMuted }}>Nenhum bairro encontrado</div>;
                      return <div style={{ position:"absolute", left:0, right:0, top:"100%", zIndex:50, background:C.card, border:`1px solid ${C.blue}40`, borderRadius:8, maxHeight:200, overflowY:"auto", marginTop:2, boxShadow:"0 8px 24px rgba(0,0,0,.4)" }}>
                        {lista.map(b => (
                          <div key={b.id} onClick={() => {
                            setBairroBusca(b.bairro_nome);
                            setAvulsaForm(f => ({...f, bairro: b.bairro_nome}));
                            setAvulsaTaxa(parseFloat(b.valor));
                            setAvulsaTaxaInfo("Bairro: " + b.bairro_nome);
                            setBairroAberto(false);
                          }}
                          style={{ padding:"10px 14px", cursor:"pointer", fontSize:12, fontWeight:600, borderBottom:`1px solid ${C.border}20`, display:"flex", justifyContent:"space-between", alignItems:"center" }}
                          onMouseOver={e=>{e.currentTarget.style.background=C.bg;}} onMouseOut={e=>{e.currentTarget.style.background="transparent";}}>
                            <span>{b.bairro_nome}</span>
                            <span style={{ fontWeight:800, color:C.blue }}>R$ {parseFloat(b.valor).toFixed(2)}</span>
                          </div>
                        ))}
                      </div>;
                    })()}
                  </div>
                )}

                {avulsaTaxa !== null ? (
                  <div>
                    <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                      <span style={{ fontSize:18, fontWeight:800, color:C.text }}>R$</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={Number(avulsaTaxa || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        onChange={e => {
                          // Máscara de centavos: digita só dígitos, formata como decimal
                          const apenasDigitos = e.target.value.replace(/\D/g, "");
                          if (!apenasDigitos) { setAvulsaTaxa(0); return; }
                          const v = parseInt(apenasDigitos, 10) / 100;
                          setAvulsaTaxa(v);
                          setAvulsaTaxaInfo("Editado manualmente");
                        }}
                        style={{ flex:"0 1 160px", padding:"6px 10px", borderRadius:8, border:`1px solid ${C.blue}50`, background:C.card, color:C.text, fontSize:22, fontWeight:800, outline:"none", fontFamily:"inherit" }}
                      />
                      {avulsaKmInfo && (
                        <div style={{ display:"flex", alignItems:"center", gap:6, padding:"6px 12px", borderRadius:8, background:`${C.greenLight}15`, border:`1px solid ${C.greenLight}40` }}>
                          <span style={{ fontSize:15 }}>📏</span>
                          <span style={{ fontSize:14, fontWeight:800, color:C.greenLight }}>{avulsaKmInfo}</span>
                        </div>
                      )}
                      {avulsaTaxa === 0 && <span style={{ fontSize:12, fontWeight:800, color:C.greenLight }}>GRÁTIS</span>}
                    </div>
                    {avulsaTaxaInfo && <div style={{ fontSize:10, color:C.textMuted, marginTop:4 }}>{avulsaTaxaInfo}</div>}
                    <div style={{ fontSize:10, color:C.textMuted, marginTop:2 }}>💡 Pode editar manualmente (ex: igualar à taxa do iFood)</div>
                  </div>
                ) : (
                  <span style={{ fontSize:11, color:C.textMuted }}>
                    {lojaModo?.tipo === 'bairro' ? "Selecione o bairro acima" : avulsaLat ? "Clique Recalcular" : "Obtenha localização ou preencha bairro"}
                  </span>
                )}
              </div>
              {/* ── Já pagou? ── */}
              <div style={{ marginBottom:12 }}>
                <label style={{ fontSize:11, fontWeight:700, color:C.textMuted, display:"block", marginBottom:6 }}>Já pagou?</label>
                <div style={{ display:"flex", gap:6 }}>
                  {[{k:false,l:"Não — cobrar na entrega",c:C.yellow},{k:true,l:"Sim — já pago",c:C.greenLight}].map(op => (
                    <button key={String(op.k)} onClick={() => setAvulsaJaPagou(op.k)}
                      style={{ flex:1, padding:"9px 6px", borderRadius:8, border:`1px solid ${avulsaJaPagou===op.k?op.c:C.border}`,
                        background:avulsaJaPagou===op.k?op.c+"20":"transparent", color:avulsaJaPagou===op.k?op.c:C.textMuted,
                        cursor:"pointer", fontSize:11, fontWeight:700 }}>
                      {op.l}
                    </button>
                  ))}
                </div>
              </div>

              {/* ── Forma de pagamento ── */}
              <div style={{ marginBottom:12 }}>
                <label style={{ fontSize:11, fontWeight:700, color:C.textMuted, display:"block", marginBottom:6 }}>
                  {avulsaJaPagou ? "Pago via" : "Forma de pagamento"}
                </label>
                <div style={{ display:"flex", gap:6 }}>
                  {[{k:"dinheiro",l:"Dinheiro",c:C.yellow},{k:"pix",l:"PIX",c:C.green},{k:"credito",l:"Crédito",c:C.red},{k:"debito",l:"Débito",c:C.blue}].map(fp => (
                    <button key={fp.k} onClick={() => setAvulsaFormaPag(fp.k)} style={{ flex:1, padding:"8px 4px", borderRadius:8, border:`1px solid ${avulsaFormaPag===fp.k?fp.c:C.border}`, background:avulsaFormaPag===fp.k?fp.c+"20":"transparent", color:avulsaFormaPag===fp.k?fp.c:C.textMuted, cursor:"pointer", fontSize:11, fontWeight:700 }}>{fp.l}</button>
                  ))}
                </div>
              </div>

              {/* ── Troco (só se não pagou e forma = dinheiro) ── */}
              {!avulsaJaPagou && avulsaFormaPag === "dinheiro" && (
                <div style={{ marginBottom:12 }}>
                  <label style={{ fontSize:11, fontWeight:700, color:C.yellow, display:"block", marginBottom:5 }}>Troco para quanto? (R$)</label>
                  <input type="number" value={avulsaTroco} onChange={e => setAvulsaTroco(e.target.value)}
                    placeholder="Ex: 100 (deixe vazio se não precisa)" style={{ ...iS, borderColor:`${C.yellow}40` }} />
                </div>
              )}

              {/* ── Observação / Referência ── */}
              <div style={{ marginBottom:12 }}>
                <label style={{ fontSize:11, fontWeight:700, color:C.textMuted, display:"block", marginBottom:5 }}>Observação / Referência do local</label>
                <textarea value={avulsaForm.obs||""} onChange={e => setAvulsaForm({...avulsaForm, obs:e.target.value})}
                  placeholder="Ex: portão azul, ligar antes, entrar pela lateral..."
                  style={{ ...iS, height:56, resize:"vertical" }} />
              </div>

              <div style={{ display:"flex", gap:10 }}>
                <button onClick={() => { setShowAvulsa(false); setAvulsaStep(1); setAvulsaBusca(""); setAvulsaResultados([]); setAvulsaTaxa(null); setAvulsaTaxaInfo(""); setAvulsaKmInfo(""); setBairroBusca(""); setBairroAberto(false); resetLocalizacao(); }} style={{ flex:1, padding:13, borderRadius:12, border:`1px solid ${C.border}`, background:"transparent", color:C.textSoft, fontWeight:700, cursor:"pointer" }}>Cancelar</button>
                <button onClick={pausarAvulsa} title="Salvar e atender outro cliente" style={{ flex:1, padding:13, borderRadius:12, border:`1px solid ${C.yellow}60`, background:`${C.yellow}15`, color:C.yellow, fontWeight:700, cursor:"pointer", fontSize:13 }}>💤 Pausar</button>
                <button onClick={confirmarEntregaAvulsa} disabled={avulsaLoading} style={{ flex:2, padding:13, borderRadius:12, border:"none", background:`linear-gradient(135deg,${C.green},${C.greenLight})`, color:"#fff", fontWeight:700, cursor:"pointer" }}>{avulsaLoading?"Criando...":"Criar Entrega"}</button>
              </div>
            </div>)}
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════
          BANDEJA FLUTUANTE — Avulsas Pausadas
          (fica no canto inferior direito, sempre visível quando há pausadas)
      ═══════════════════════════════════════ */}
      {avulsasPausadas.length > 0 && (
        <div style={{
          position:"fixed", right:16, bottom:16, zIndex:850,
          display:"flex", flexDirection:"column", gap:8, maxWidth:300
        }}>
          <style>{`@keyframes pulseGlowGreen { 0%,100% { box-shadow: 0 0 0 0 rgba(34,197,94,0.6), 0 4px 14px rgba(0,0,0,0.35); } 50% { box-shadow: 0 0 0 8px rgba(34,197,94,0), 0 4px 14px rgba(0,0,0,0.35); } }`}</style>
          <div style={{
            fontSize:11, fontWeight:800, color:C.textSoft,
            background:C.card, padding:"6px 12px", borderRadius:10,
            border:`1px solid ${C.border}`, display:"flex", alignItems:"center", gap:6,
            boxShadow:"0 4px 12px rgba(0,0,0,0.25)"
          }}>
            <span style={{ fontSize:14 }}>💤</span>
            <span>{avulsasPausadas.length} avulsa{avulsasPausadas.length!==1?"s":""} pausada{avulsasPausadas.length!==1?"s":""}</span>
          </div>
          {avulsasPausadas.map(p => {
            const recebida = p.locStatus === "recebido";
            const aguardando = p.locCode && !recebida;
            const abriu = !!p.locAbertoEm;
            const semLink = !p.locCode;
            const corBarra = recebida ? C.greenLight : (aguardando ? (abriu ? C.green : C.yellow) : C.blue);
            const tempoMin = Math.floor((Date.now() - p.criadoEm) / 60000);
            const bandeiraPais = p.locPaisDetectado === "PY" ? "🇵🇾" : (p.locPaisDetectado === "BR" ? "🇧🇷" : "");
            return (
              <div key={p.id}
                style={{
                  background:C.card, border:`1px solid ${corBarra}50`,
                  borderRadius:12, padding:"10px 12px",
                  boxShadow: recebida ? `0 0 0 0 ${C.greenLight}80, 0 4px 14px rgba(0,0,0,0.35)` : "0 4px 14px rgba(0,0,0,0.35)",
                  borderLeft:`4px solid ${corBarra}`,
                  cursor: showAvulsa && avulsaForm.nome ? "not-allowed" : "pointer",
                  opacity: showAvulsa && avulsaForm.nome ? 0.6 : 1,
                  transition:"all .15s",
                  animation: recebida ? "pulseGlowGreen 1.8s ease-in-out infinite" : "none"
                }}
                onClick={() => { if (!(showAvulsa && avulsaForm.nome)) retomarAvulsa(p.id); }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8, marginBottom:4 }}>
                  <div style={{ fontWeight:800, fontSize:13, color:C.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", flex:1 }}>
                    {bandeiraPais && <span style={{ marginRight:4 }}>{bandeiraPais}</span>}
                    {p.avulsaForm.nome || "Sem nome"}
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); if (confirm(`Descartar avulsa de ${p.avulsaForm.nome}?`)) descartarAvulsa(p.id); }}
                    title="Descartar"
                    style={{ width:22, height:22, borderRadius:6, border:"none", background:"transparent", color:C.textMuted, cursor:"pointer", fontSize:14, fontWeight:700, lineHeight:1 }}>×</button>
                </div>
                <div style={{ fontSize:10, color:C.textMuted, marginBottom:6, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                  {p.avulsaForm.telefone || "—"} · {tempoMin}min atrás
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
                  {recebida && (
                    <span style={{ fontSize:10, fontWeight:800, color:C.greenLight, background:`${C.greenLight}20`, padding:"3px 8px", borderRadius:6 }}>
                      ✅ Localização recebida
                    </span>
                  )}
                  {aguardando && !abriu && (
                    <>
                      <div style={{ width:10, height:10, border:`2px solid ${C.border}`, borderTopColor:C.yellow, borderRadius:"50%", animation:"spin .8s linear infinite" }} />
                      <span style={{ fontSize:10, fontWeight:700, color:C.yellow }}>Aguardando cliente</span>
                    </>
                  )}
                  {aguardando && abriu && (
                    <>
                      <div style={{ width:10, height:10, border:`2px solid ${C.border}`, borderTopColor:C.green, borderRadius:"50%", animation:"spin .8s linear infinite" }} />
                      <span style={{ fontSize:10, fontWeight:700, color:C.green }}>👀 Cliente abriu o link</span>
                    </>
                  )}
                  {semLink && (
                    <span style={{ fontSize:10, fontWeight:700, color:C.blue }}>📝 Dados em edição</span>
                  )}
                </div>
                <div style={{ fontSize:10, color:corBarra, fontWeight:700, marginTop:6, textAlign:"right" }}>
                  Clicar para retomar →
                </div>
              </div>
            );
          })}
          {showAvulsa && avulsaForm.nome && (
            <div style={{ fontSize:10, color:C.textMuted, background:C.card, padding:"6px 10px", borderRadius:8, border:`1px dashed ${C.border}`, textAlign:"center" }}>
              Pause a avulsa atual para retomar outra
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════
          LAYOUT PRINCIPAL — iFood clássico
      ═══════════════════════════════════════ */}
      <div style={{ display:"flex", flex:1, minHeight:0, overflow:"hidden" }}>

        {/* ═══ PAINEL ESQUERDO — Lista de pedidos ═══ */}
        <div style={{ width:300, borderRight:`1px solid ${C.border}`, display:"flex", flexDirection:"column", flexShrink:0 }}>

          {/* Header com tabs */}
          <div style={{ padding:"10px 12px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", gap:6 }}>
            <div style={{ padding:"4px 12px", background:C.red, color:"#fff", fontSize:11, fontWeight:700, borderRadius:14, display:"flex", alignItems:"center", gap:4 }}>
              Agora
              {orders.filter(o=>!["entregue","cancelado"].includes(o.status)).length > 0 && (
                <span style={{ background:"#fff", color:C.red, fontSize:9, fontWeight:800, padding:"0 5px", borderRadius:8 }}>
                  {orders.filter(o=>!["entregue","cancelado"].includes(o.status)).length}
                </span>
              )}
            </div>
          </div>

          {/* Busca + Filtros */}
          <div style={{ padding:"8px 10px", borderBottom:`1px solid ${C.border}`, display:"flex", gap:6 }}>
            <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar pedido..."
              style={{ flex:1, padding:"7px 10px", background:C.card, borderRadius:8, border:`1px solid ${C.border}`, color:C.text, fontSize:11, outline:"none", fontFamily:"inherit" }} />
          </div>

          {/* Lista com seções */}
          <div style={{ flex:1, overflowY:"auto", padding:"6px 8px" }} onScroll={markInteracting}>
            {SECTIONS.map(sec => {
              const items = filtrarBusca(sec.items);
              if (items.length === 0 && sec.key !== "pendente") return null;
              return (
                <div key={sec.key} style={{ marginBottom:4 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"6px 4px 4px" }}>
                    <span style={{ fontSize:10, fontWeight:700, color:sec.color, textTransform:"uppercase", letterSpacing:"0.04em" }}>{sec.label}</span>
                    <span style={{ fontSize:10, color:C.textMuted }}>{items.length} pedido{items.length !== 1 ? "s" : ""}</span>
                  </div>
                  {items.length === 0 && sec.key === "pendente" && (
                    <div style={{ textAlign:"center", padding:"20px 10px", color:C.textMuted, fontSize:11 }}>Nenhum pedido novo</div>
                  )}
                  {items.map(o => {
                    const isNovo = o.status === "novo";
                    const min = minutosDesde(o.criado_em);
                    const cor = isNovo ? timerCor(min) : sec.color;
                    const isSel = sel?.id === o.id;
                    const isAvulsa = o.observacao?.includes("[ENTREGA AVULSA]");
                    const naoLidos = (chatNaoLidos[o.id]||0) + (chatNaoLidosEnt[o.id]||0);
                    const stBadge = { aceito:{c:C.blue,l:"ACEITO"}, preparando:{c:C.orange,l:"PREPARO"}, pronto:{c:"#3B82F6",l:"PRONTO"}, buscando_entregador:{c:"#3B82F6",l:"AGUARD"}, em_entrega:{c:C.greenLight,l:"ROTA"}, entregue:{c:"#6B7280",l:"ENTREGUE"}, cancelado:{c:C.red,l:"CANCELADO"} };
                    const badge = stBadge[o.status];

                    return (
                      <div key={o.id} onClick={() => { markInteracting(); setSelected(o); }}
                        style={{
                          background: isSel ? `${C.red}12` : C.card,
                          borderRadius: 10, padding: "10px 10px", marginBottom: 4,
                          borderLeft: `4px solid ${cor}`,
                          border: isSel ? `2px solid ${C.red}` : `1px solid ${C.border}`,
                          cursor: "pointer", transition: "all .15s", position: "relative",
                        }}>
                        {/* Timer bar */}
                        {isNovo && (
                          <div style={{ position:"absolute", top:0, left:0, right:0, height:3, borderRadius:"10px 10px 0 0", overflow:"hidden", background:`${C.border}40` }}>
                            <div style={{ height:"100%", width:`${timerPct(min)}%`, background:cor, transition:"width 1s", borderRadius:3 }} />
                          </div>
                        )}
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
                          <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                            <span style={{ fontWeight:800, fontSize:12, color:C.text }}>#{o.codigo}</span>
                            {naoLidos > 0 && <span style={{ fontSize:8, fontWeight:700, color:"#fff", background:C.red, padding:"0 4px", borderRadius:4 }}>💬{naoLidos}</span>}
                          </div>
                          <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                            {isAvulsa && <span style={{ fontSize:8, fontWeight:700, color:"#8B5CF6", background:"#8B5CF615", padding:"1px 4px", borderRadius:3 }}>AVL</span>}
                            {Number(o.cashback_usado||0) > 0 && <span style={{ fontSize:8, fontWeight:700, color:"#8B5CF6", background:"#8B5CF615", padding:"1px 4px", borderRadius:3 }}>🪙</span>}
                            {isNovo ? (
                              <span style={{ fontSize:10, fontWeight:800, color:cor }}>{min < 1 ? "agora" : `${min} min`}</span>
                            ) : badge && (
                              <span style={{ fontSize:8, fontWeight:700, color:badge.c, background:badge.c+"18", padding:"1px 5px", borderRadius:3 }}>{badge.l}</span>
                            )}
                          </div>
                        </div>
                        <div style={{ fontSize:11, color:C.textSoft, marginBottom:2, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                          {o.cliente_nome || (isAvulsa ? o.observacao?.match(/Cliente: ([^•]+)/)?.[1]?.trim() : `#${o.cliente_id}`)}
                          {" · "}{(o.itens||[]).map(it => `${it.quantidade}x ${it.nome}`).join(", ").slice(0,40) || "..."}
                        </div>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:4 }}>
                          <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                            <span style={{ fontSize:9, fontWeight:700, color:C.textMuted, background:`${C.textMuted}12`, padding:"1px 5px", borderRadius:3 }}>{(o.forma_pagamento||"").toUpperCase()||"—"}</span>
                            <span style={{ fontWeight:800, fontSize:13, color:C.green }}>{fmt(valorReal(o))}</span>
                          </div>
                          {isNovo && (
                            <button onClick={e => { e.stopPropagation(); pararSom(); onAdvance(o.id); setTimeout(()=>onAdvance(o.id),600); imprimirPedido(o); }}
                              style={{ padding:"4px 12px", borderRadius:7, border:"none", color:"#fff", fontSize:10, fontWeight:800, background:C.green, cursor:"pointer" }}>
                              Aceitar
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        {/* ═══ PAINEL DIREITO — Dashboard ou Detalhe ═══ */}
        <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden" }}>

          {/* Topbar */}
          <div style={{ padding:"10px 16px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <span style={{ fontWeight:800, fontSize:17 }}><span style={{ color:C.red }}>Mix</span><span style={{ color:C.green }}>Up</span></span>
              <button onClick={toggleOnline}
                title={forcadoInfo
                  ? `${forcadoInfo.tipo === "online" ? _("forcadoOnlineTooltip") : _("forcadoOfflineTooltip")} ${forcadoInfo.proximoEvento ? forcadoInfo.proximoEvento.toLocaleString("pt-BR", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" }) : ""}`
                  : (lojaOnline ? "Online" : "Offline")}
                style={{ display:"flex", alignItems:"center", gap:5, padding:"4px 10px", borderRadius:16, background:lojaOnline?`${C.green}18`:`${C.red}18`, border:`1px solid ${lojaOnline?C.green+"55":C.red+"55"}`, cursor:"pointer", opacity:togglingOnline?0.5:1 }}>
                <div style={{ width:6, height:6, borderRadius:"50%", background:lojaOnline?C.greenLight:C.red, boxShadow:lojaOnline?`0 0 6px ${C.greenLight}`:"none" }} />
                <span style={{ fontSize:11, fontWeight:700, color:lojaOnline?C.greenLight:C.red }}>{lojaOnline?"Online":"Offline"}</span>
                {forcadoInfo && <span style={{ fontSize:10 }}>🔒</span>}
              </button>
              <button onClick={toggleAutoAceite} style={{ padding:"4px 10px", borderRadius:16, background:autoAceite?"#8B5CF618":C.card, border:`1px solid ${autoAceite?"#8B5CF655":C.border}`, cursor:"pointer" }}>
                <span style={{ fontSize:11, fontWeight:700, color:autoAceite?"#8B5CF6":C.textMuted }}>{autoAceite?"Auto ON":"Auto OFF"}</span>
              </button>
              <button onClick={toggleSilentPrint} onContextMenu={e => { e.preventDefault(); setShowPrintCfg(true); }}
                style={{ padding:"4px 10px", borderRadius:16, background:silentPrint?"#F59E0B18":C.card, border:`1px solid ${silentPrint?"#F59E0B55":C.border}`, cursor:"pointer", display:"flex", alignItems:"center", gap:4 }}>
                <span style={{ fontSize:11 }}>{silentPrint?"🖨":"🖨"}</span>
                <span style={{ fontSize:11, fontWeight:700, color:silentPrint?"#F59E0B":C.textMuted }}>{silentPrint?"Auto Print":"Print OFF"}</span>
              </button>
              <button onClick={() => goTo && goTo("pdv")} title="Ir para o PDV (Caixa)"
                style={{ padding:"4px 10px", borderRadius:16, background:`${C.blue}18`, border:`1px solid ${C.blue}55`, cursor:"pointer", display:"flex", alignItems:"center", gap:4 }}>
                <span style={{ fontSize:11 }}>💰</span>
                <span style={{ fontSize:11, fontWeight:700, color:C.blue }}>PDV</span>
              </button>

              {/* Botão Pausar Loja com dropdown */}
              <div style={{ position:"relative" }}>
                <button onClick={() => setShowPausaMenu(v => !v)} title="Pausar loja temporariamente"
                  style={{ padding:"4px 10px", borderRadius:16, background:pausaInfo?"#F59E0B22":C.card, border:`1px solid ${pausaInfo?"#F59E0B55":C.border}`, cursor:"pointer", display:"flex", alignItems:"center", gap:4 }}>
                  <span style={{ fontSize:11 }}>⏸</span>
                  <span style={{ fontSize:11, fontWeight:700, color:pausaInfo?"#F59E0B":C.textMuted }}>
                    {pausaInfo ? `${_("pausada")} (${formatTempoRestante(pausaInfo.expira)})` : _("pausarBtn")}
                  </span>
                </button>
                {showPausaMenu && (
                  <>
                    {/* clique fora fecha */}
                    <div onClick={() => setShowPausaMenu(false)} style={{ position:"fixed", inset:0, zIndex:9 }} />
                    <div style={{ position:"absolute", top:"100%", left:0, marginTop:4, background:C.card, border:`1px solid ${C.border}`, borderRadius:8, padding:6, boxShadow:"0 4px 12px rgba(0,0,0,0.3)", zIndex:10, minWidth:160 }}>
                      <div style={{ fontSize:10, color:C.textMuted, padding:"4px 8px", fontWeight:700 }}>{_("pausarPor")}</div>
                      <button onClick={() => pausarLoja(30)} style={{ display:"block", width:"100%", textAlign:"left", padding:"8px 12px", background:"transparent", border:"none", color:C.text, cursor:"pointer", fontSize:12, borderRadius:4 }}>{_("pausar30min")}</button>
                      <button onClick={() => pausarLoja(60)} style={{ display:"block", width:"100%", textAlign:"left", padding:"8px 12px", background:"transparent", border:"none", color:C.text, cursor:"pointer", fontSize:12, borderRadius:4 }}>{_("pausar1h")}</button>
                      <button onClick={() => pausarLoja(120)} style={{ display:"block", width:"100%", textAlign:"left", padding:"8px 12px", background:"transparent", border:"none", color:C.text, cursor:"pointer", fontSize:12, borderRadius:4 }}>{_("pausar2h")}</button>
                      {pausaInfo && (
                        <>
                          <div style={{ height:1, background:C.border, margin:"4px 0" }} />
                          <button onClick={() => { setShowPausaMenu(false); cancelarPausa(); }} style={{ display:"block", width:"100%", textAlign:"left", padding:"8px 12px", background:"transparent", border:"none", color:C.red, cursor:"pointer", fontSize:12, fontWeight:700, borderRadius:4 }}>{_("cancelarPausa")}</button>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
            <div style={{ display:"flex", gap:6 }}>
              <button onClick={()=>{ markInteracting(); setShowVendas(true); }} style={{ padding:"4px 10px", borderRadius:8, border:`1px solid ${C.greenLight}40`, background:`${C.greenLight}12`, color:C.greenLight, fontSize:11, fontWeight:700, cursor:"pointer" }}>
                Vendas {fmt(totalVendas)}
              </button>
              <button onClick={()=>{ markInteracting(); setShowComissao(true); }} style={{ padding:"4px 10px", borderRadius:8, border:`1px solid ${C.red}40`, background:`${C.red}12`, color:C.red, fontSize:11, fontWeight:700, cursor:"pointer" }}>
                Comissão {fmt(comissaoValor)}
              </button>
              {saldo !== null && (
                <button onClick={()=>{ markInteracting(); setShowSaldo(true); }} style={{ padding:"4px 10px", borderRadius:8, border:`1px solid ${C.blue}40`, background:`${C.blue}12`, color:C.blue, fontSize:11, fontWeight:700, cursor:"pointer" }}>
                  Saldo {fmt(saldo)}
                </button>
              )}
            </div>
          </div>

          {/* Banner de pausa ativa */}
          {pausaInfo && (
            <div style={{
              padding:"8px 16px",
              background:"#F59E0B22",
              borderBottom:`1px solid #F59E0B55`,
              display:"flex",
              alignItems:"center",
              justifyContent:"space-between",
              flexShrink:0,
              gap:12
            }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <span style={{ fontSize:18 }}>⏸</span>
                <div>
                  <div style={{ fontSize:13, fontWeight:700, color:"#F59E0B" }}>
                    {_("lojaPausada")}
                  </div>
                  <div style={{ fontSize:11, color:C.textMuted }}>
                    {_("voltaAutomaticoEm")} {formatTempoRestante(pausaInfo.expira)} ({pausaInfo.expira.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })})
                  </div>
                </div>
              </div>
              <button onClick={cancelarPausa} disabled={togglingOnline} style={{
                padding:"6px 14px", borderRadius:8,
                border:`1px solid ${C.red}55`, background:`${C.red}18`,
                color:C.red, fontSize:12, fontWeight:700, cursor:"pointer",
                opacity:togglingOnline?0.5:1
              }}>
                {_("cancelarPausaBtn")}
              </button>
            </div>
          )}

          {/* Banner de modo forçado (sem pausa por tempo — vale até próximo evento) */}
          {!pausaInfo && forcadoInfo && (
            <div style={{
              padding:"8px 16px",
              background: forcadoInfo.tipo === "online" ? `${C.green}18` : `${C.red}18`,
              borderBottom:`1px solid ${forcadoInfo.tipo === "online" ? C.green+"55" : C.red+"55"}`,
              display:"flex",
              alignItems:"center",
              justifyContent:"space-between",
              flexShrink:0,
              gap:12
            }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <span style={{ fontSize:18 }}>🔒</span>
                <div>
                  <div style={{ fontSize:13, fontWeight:700, color: forcadoInfo.tipo === "online" ? C.greenLight : C.red }}>
                    {forcadoInfo.tipo === "online" ? _("forcadoOnlineLabel") : _("forcadoOfflineLabel")}
                  </div>
                  <div style={{ fontSize:11, color:C.textMuted }}>
                    {forcadoInfo.proximoEvento
                      ? `${_("voltaHorarioAs")} ${forcadoInfo.proximoEvento.toLocaleString("pt-BR", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" })}`
                      : _("voltaHorarioProximo")}
                  </div>
                </div>
              </div>
              <button onClick={cancelarPausa} disabled={togglingOnline} style={{
                padding:"6px 14px", borderRadius:8,
                border:`1px solid ${C.border}`, background:C.card,
                color:C.text, fontSize:12, fontWeight:700, cursor:"pointer",
                opacity:togglingOnline?0.5:1
              }}>
                {_("voltarAutomatico")}
              </button>
            </div>
          )}

          {/* Conteúdo: Dashboard ou Detalhe */}
          {sel ? (
            /* ═══ DETALHE DO PEDIDO ═══ */
            <div style={{ flex:1, overflowY:"auto", padding:16 }} onScroll={markInteracting}>
              <button onClick={() => setSelected(null)} style={{ display:"flex", alignItems:"center", gap:5, padding:"5px 12px", borderRadius:8, border:`1px solid ${C.border}`, background:C.card, color:C.textSoft, fontSize:12, fontWeight:700, cursor:"pointer", marginBottom:14 }}>
                ← Voltar ao painel
              </button>

              <div style={{ display:"flex", flexDirection:"column", gap:0, alignItems:"stretch", maxWidth:900, margin:"0 auto" }}>
                {/* Topo detalhe — código, progress, status */}
                <div>
                  <div style={{ marginBottom:14 }}>
                    <div style={{ fontWeight:900, fontSize:24 }}>#{sel.codigo}</div>
                    <div style={{ fontSize:11, color:C.textMuted, marginTop:2 }}>{sel.criado_em ? fmtLocalDateTime(sel.criado_em) : ""}</div>
                  </div>

                  {/* Progress bar */}
                  {!["cancelado"].includes(sel.status) && (() => {
                    const pIdx = progressIndex(sel.status);
                    return (
                      <div style={{ display:"flex", alignItems:"center", gap:0, marginBottom:18, padding:"8px 0" }}>
                        {PROGRESS_STEPS.map((step, i) => {
                          const active = i <= pIdx;
                          const current = i === pIdx;
                          return (
                            <div key={step.key} style={{ display:"flex", alignItems:"center", flex: i < PROGRESS_STEPS.length-1 ? 1 : "none" }}>
                              <div style={{ width:current?30:24, height:current?30:24, borderRadius:"50%", background:active?C.red:`${C.border}60`,
                                display:"flex", alignItems:"center", justifyContent:"center", fontSize:current?13:10, color:active?"#fff":C.textMuted,
                                fontWeight:800, transition:"all .3s", flexShrink:0, boxShadow:current?`0 0 10px ${C.red}50`:"none" }}>
                                {active ? step.icon : i+1}
                              </div>
                              {i < PROGRESS_STEPS.length-1 && <div style={{ flex:1, height:2, background:i<pIdx?C.red:`${C.border}40`, margin:"0 2px", borderRadius:2 }} />}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}

                  {/* Status badge */}
                  <span style={{ display:"inline-block", marginBottom:12, fontSize:11, fontWeight:700, color:STATUS[sel.status]?.color, background:STATUS[sel.status]?.bg, padding:"5px 14px", borderRadius:8 }}>{stLabel(sel.status)}</span>

                  {/* Badges */}
                  {sel.observacao?.includes("[RETIRADA NA LOJA]") && <div style={{ padding:"8px 10px", borderRadius:8, background:"#8B5CF610", border:"1px solid #8B5CF630", fontSize:11, color:"#8B5CF6", fontWeight:700, marginBottom:6 }}>🏪 RETIRADA NA LOJA</div>}
                  {sel.observacao?.includes("[PAGO ONLINE]") && <div style={{ padding:"8px 10px", borderRadius:8, background:`${C.green}10`, border:`1px solid ${C.green}30`, fontSize:11, color:C.greenLight, fontWeight:700, marginBottom:6 }}>💳 PAGO ONLINE</div>}
                  {Number(sel.cashback_usado||0) > 0 && <div style={{ padding:"8px 10px", borderRadius:8, background:"#8B5CF610", border:"1px solid #8B5CF630", fontSize:11, color:"#8B5CF6", fontWeight:700, marginBottom:6 }}>🪙 Cashback: {fmt(sel.cashback_usado)}</div>}
                  {/* Botões */}
                  <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:14, alignItems:"center" }}>
                    {!["entregue","cancelado"].includes(sel.status) && (
                      <>
                        <button onClick={() => abrirChat(sel)} style={{ height:42, padding:"0 16px", display:"flex", alignItems:"center", borderRadius:10, border:"1px solid #075E5440", background:"#075E5415", color:"#075E54", fontSize:13, fontWeight:700, cursor:"pointer" }}>
                          💬 Cliente {chatNaoLidos[sel.id]>0&&<span style={{ marginLeft:5, background:C.red, color:"#fff", fontSize:9, padding:"0 4px", borderRadius:6 }}>{chatNaoLidos[sel.id]}</span>}
                        </button>
                        {sel.entregador_id && <button onClick={() => abrirChatEnt(sel)} style={{ height:42, padding:"0 16px", display:"flex", alignItems:"center", borderRadius:10, border:`1px solid ${C.blue}40`, background:`${C.blue}15`, color:C.blue, fontSize:13, fontWeight:700, cursor:"pointer" }}>
                          🛵 Entregador {chatNaoLidosEnt[sel.id]>0&&<span style={{ marginLeft:5, background:C.red, color:"#fff", fontSize:9, padding:"0 4px", borderRadius:6 }}>{chatNaoLidosEnt[sel.id]}</span>}
                        </button>}
                      </>
                    )}
                    <button onClick={() => imprimirPedido(sel)} style={{ height:42, padding:"0 16px", display:"flex", alignItems:"center", borderRadius:10, border:`1px solid ${C.border}`, background:C.card, color:C.textSoft, fontSize:13, fontWeight:700, cursor:"pointer" }}>🖨 Imprimir</button>

                    {sel.status === "novo" && <button onClick={() => { markInteracting(); pararSom(); onAdvance(sel.id); setTimeout(()=>onAdvance(sel.id),600); imprimirPedido(sel); }} style={{ height:42, padding:"0 20px", display:"flex", alignItems:"center", borderRadius:10, border:"none", color:"#fff", fontWeight:800, fontSize:14, background:`linear-gradient(135deg,${C.green},${C.greenLight})`, cursor:"pointer" }}>✓ Aceitar Pedido</button>}

                    {/* Fase 2 — Aceito: 2 botões para chamar entregador */}
                    {(sel.status==="aceito"||sel.status==="preparando") && (
                      <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                        {sel.observacao?.includes("[RETIRADA NA LOJA]") ? (
                          <button onClick={async () => { markInteracting(); try { await put("pedidos", sel.id, { status:"entregue" }); notify("Retirado!"); onRefresh(); } catch { notify("Erro",false); } }}
                            style={{ height:42, padding:"0 20px", display:"flex", alignItems:"center", borderRadius:10, border:"none", color:"#fff", fontWeight:800, fontSize:14, background:`linear-gradient(135deg,${C.green},${C.greenLight})`, cursor:"pointer" }}>
                            ✓ Cliente Retirou
                          </button>
                        ) : (<>
                          <button onClick={async () => { markInteracting();
                            const taxaEnt = Number(sel.taxa_entrega || 0);
                            if (taxaEnt > 0 && Number(saldo || 0) < taxaEnt) { alert("Saldo insuficiente!\n\nSaldo atual: R$ "+Number(saldo||0).toFixed(2)+"\nTaxa de entrega: R$ "+taxaEnt.toFixed(2)+"\n\nAdicione crédito no botão 'Saldo' para solicitar entregador."); return; }
                            try {
                            // 1. Registrar chamada geral (salva fixo_ids pra exclusão)
                            await fetch("https://mixupbr.com.br/api/loja_entregadores/", {
                              method:"POST", headers:{"Content-Type":"application/json"},
                              body: JSON.stringify({ loja_id: lojaId, pedido_id: sel.id, acao: "notificar_todos" })
                            });
                            // 2. Setar status pronto
                            await put("pedidos", sel.id, { status:"pronto" });
                            notify("Chamando todos os entregadores online!"); onRefresh();
                          } catch { notify("Erro",false); } }}
                            style={{ height:42, padding:"0 20px", display:"flex", alignItems:"center", borderRadius:10, border:"none", color:"#fff", fontWeight:800, fontSize:14, background: Number(saldo||0) < Number(sel.taxa_entrega||0) && Number(sel.taxa_entrega||0) > 0 ? C.border : `linear-gradient(135deg,#ea580c,${C.orange})`, cursor:"pointer" }}>
                            {Number(saldo||0) < Number(sel.taxa_entrega||0) && Number(sel.taxa_entrega||0) > 0 ? "⚠ Saldo insuficiente" : "Chamar Entregadores"}
                          </button>
                          <button onClick={async () => { markInteracting();
                            const taxaEnt = Number(sel.taxa_entrega || 0);
                            if (taxaEnt > 0 && Number(saldo || 0) < taxaEnt) { alert("Saldo insuficiente!\n\nSaldo atual: R$ "+Number(saldo||0).toFixed(2)+"\nTaxa de entrega: R$ "+taxaEnt.toFixed(2)+"\n\nAdicione crédito no botão 'Saldo' para solicitar entregador."); return; }
                            try {
                            // 1. Registrar fixos no pedido (salva IDs dos fixos)
                            const resp = await fetch("https://mixupbr.com.br/api/loja_entregadores/", {
                              method:"POST", headers:{"Content-Type":"application/json"},
                              body: JSON.stringify({ loja_id: lojaId, pedido_id: sel.id, acao: "notificar_fixos" })
                            });
                            const data = await resp.json();
                            if (!data.ok && data.erro) { notify(data.erro, false); return; }
                            // 2. Setar status pronto
                            await put("pedidos", sel.id, { status:"pronto" });
                            notify("Chamando " + (data.total||"") + " entregador(es) fixo(s)!"); onRefresh();
                          } catch { notify("Erro",false); } }}
                            style={{ height:42, padding:"0 20px", display:"flex", alignItems:"center", borderRadius:10, border:`2px solid ${Number(saldo||0) < Number(sel.taxa_entrega||0) && Number(sel.taxa_entrega||0) > 0 ? C.border : C.orange}`, color: Number(saldo||0) < Number(sel.taxa_entrega||0) && Number(sel.taxa_entrega||0) > 0 ? C.textMuted : C.orange, fontWeight:800, fontSize:14, background:"transparent", cursor:"pointer" }}>
                            Entregador Fixo
                          </button>
                        </>)}
                      </div>
                    )}

                    {/* Fase 3 — Aguardando entregador (ou já aceito) */}
                    {(sel.status==="pronto"||sel.status==="buscando_entregador") && !sel.observacao?.includes("[RETIRADA NA LOJA]") && (
                      sel.entregador_id ? (
                        <div style={{ width:"100%", padding:10, borderRadius:8, background:`${C.blue}12`, border:`1px solid ${C.blue}30`, display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
                          <div style={{ fontSize:12, color:C.blue, fontWeight:700, textAlign:"left", lineHeight:1.3 }}>
                            🛵 Entregador<br /><span style={{ fontSize:14, fontWeight:800 }}>{sel.entregador_nome || `#${sel.entregador_id}`}</span>
                          </div>
                          {sel.entregador_telefone && (
                            <a href={`https://wa.me/55${String(sel.entregador_telefone).replace(/\D/g,"")}`} target="_blank" rel="noreferrer"
                              style={{ flexShrink:0, padding:"8px 12px", borderRadius:8, background:"#25D366", color:"#fff", fontSize:12, fontWeight:700, textDecoration:"none" }}>
                              WhatsApp
                            </a>
                          )}
                        </div>
                      ) : (
                        <div style={{ width:"100%", padding:10, borderRadius:8, background:`${C.blue}12`, border:`1px solid ${C.blue}30`, textAlign:"center", fontSize:12, color:C.blue, fontWeight:700 }}>🛵 Aguardando entregador aceitar...</div>
                      )
                    )}

                    {/* Fase 4 — Em rota */}
                    {sel.status==="em_entrega" && (
                      sel.entregador_id ? (
                        <div style={{ width:"100%", padding:10, borderRadius:8, background:`${C.green}12`, border:`1px solid ${C.green}30`, display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
                          <div style={{ fontSize:12, color:C.greenLight, fontWeight:700, textAlign:"left", lineHeight:1.3 }}>
                            🛵 Em rota · {sel.entregador_nome || `#${sel.entregador_id}`}
                          </div>
                          {sel.entregador_telefone && (
                            <a href={`https://wa.me/55${String(sel.entregador_telefone).replace(/\D/g,"")}`} target="_blank" rel="noreferrer"
                              style={{ flexShrink:0, padding:"8px 12px", borderRadius:8, background:"#25D366", color:"#fff", fontSize:12, fontWeight:700, textDecoration:"none" }}>
                              WhatsApp
                            </a>
                          )}
                        </div>
                      ) : (
                        <div style={{ width:"100%", padding:10, borderRadius:8, background:`${C.green}12`, border:`1px solid ${C.green}30`, textAlign:"center", fontSize:12, color:C.greenLight, fontWeight:700 }}>🛵 Em rota de entrega</div>
                      )
                    )}

                    {!["entregue","cancelado"].includes(sel.status) && (() => {
                      const prev = { aceito:"novo", preparando:"novo", pronto:"aceito", buscando_entregador:"aceito", em_entrega:"pronto" };
                      return (
                        <div style={{ display:"flex", gap:6 }}>
                          {prev[sel.status] && <button onClick={async () => { markInteracting(); try { await put("pedidos", sel.id, { status:prev[sel.status] }); notify("Voltou"); onRefresh(); } catch { notify("Erro",false); } }} style={{ height:42, padding:"0 16px", display:"flex", alignItems:"center", borderRadius:10, border:`1px solid ${C.blue}40`, background:`${C.blue}12`, color:C.blue, fontWeight:700, fontSize:13, cursor:"pointer" }}>← Voltar</button>}
                          <button onClick={() => { markInteracting(); if (window.confirm("Cancelar #"+sel.codigo+"?")) { onReject(sel.id); setSelected(null); } }} style={{ height:42, padding:"0 16px", display:"flex", alignItems:"center", borderRadius:10, border:`1px solid ${C.red}40`, background:`${C.red}12`, color:C.red, fontWeight:700, fontSize:13, cursor:"pointer" }}>✕ Cancelar</button>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Itens + dados + ações (empilhado) */}
                <div style={{ width:"100%" }}>
                  <div style={{ fontSize:11, fontWeight:800, color:C.textMuted, letterSpacing:"0.08em", marginBottom:10, textTransform:"uppercase" }}>Itens do pedido</div>
                  <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:"6px 16px", marginBottom:12 }}>
                  {(sel.itens||[]).map((it,idx) => {
                    const ads = getAdicionais(it);
                    const obsIt = getObsItem(it);
                    return (
                      <div key={idx} style={{ padding:"10px 0", borderBottom:idx<(sel.itens||[]).length-1?`1px solid ${C.border}40`:"none" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:14 }}>
                          <div style={{ width:40, height:40, borderRadius:10, background:`${C.red}15`, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:16, color:C.red, flexShrink:0 }}>{it.quantidade}x</div>
                          <span style={{ flex:1, fontSize:20, fontWeight:700, lineHeight:1.2 }}>{it.nome}</span>
                          <span style={{ fontWeight:700, fontSize:15 }}>{fmt(it.preco_total)}</span>
                        </div>
                        {ads.length > 0 && (
                          <div style={{ marginLeft:54, marginTop:6 }}>
                            {ads.map((ad, ai) => (
                              <div key={ai} style={{ display:"flex", alignItems:"center", gap:6, padding:"2px 0" }}>
                                <span style={{ fontSize:14, color:C.orange, fontWeight:600 }}>+</span>
                                <span style={{ fontSize:14, color:C.textSoft, flex:1 }}>{ad.nome || ad.adicional_nome || "Adicional"}</span>
                                {Number(ad.preco || ad.valor || 0) > 0 && (
                                  <span style={{ fontSize:13, color:C.textMuted, fontWeight:600 }}>{fmt(ad.preco || ad.valor)}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        {obsIt && (
                          <div style={{ marginLeft:54, marginTop:4, fontSize:14, color:"#8B5CF6", fontStyle:"italic" }}>📝 {obsIt}</div>
                        )}
                      </div>
                    );
                  })}
                  </div>
                  <div style={{ paddingTop:0, marginBottom:14 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}><span style={{ fontSize:12, color:C.textMuted }}>Subtotal</span><span style={{ fontSize:12, fontWeight:600 }}>{fmt(sel.subtotal)}</span></div>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}><span style={{ fontSize:12, color:C.textMuted }}>Taxa</span><span style={{ fontSize:12, fontWeight:600 }}>{fmt(sel.taxa_entrega)}</span></div>
                    {Number(sel.taxa_servico||0) > 0 && (
                      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}><span style={{ fontSize:12, color:C.textMuted }}>Taxa de serviço</span><span style={{ fontSize:12, fontWeight:600 }}>{fmt(sel.taxa_servico)}</span></div>
                    )}
                    {Number(sel.cashback_usado||0) > 0 && (
                      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}><span style={{ fontSize:12, color:"#8B5CF6", fontWeight:600 }}>🪙 Cashback</span><span style={{ fontSize:12, fontWeight:600, color:"#8B5CF6" }}>- {fmt(sel.cashback_usado)}</span></div>
                    )}
                    <div style={{ display:"flex", justifyContent:"space-between", paddingTop:6, borderTop:`2px solid ${C.border}` }}><span style={{ fontWeight:800, fontSize:17 }}>Total</span><span style={{ fontWeight:900, fontSize:22, color:C.green }}>{fmt(valorReal(sel))}</span></div>
                  </div>

                  {/* Dados do pedido (cliente / telefone / pagamento / endereço) */}
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, marginBottom:8 }}>
                    <div style={{ padding:"10px 12px", borderRadius:10, background:C.card, border:`1px solid ${C.border}` }}>
                      <div style={{ fontSize:9, color:C.textMuted }}>Cliente</div>
                      <div style={{ fontSize:13, fontWeight:700, marginTop:2 }}>{sel.cliente_nome || (sel.observacao?.includes("[ENTREGA AVULSA]") ? sel.observacao?.match(/Cliente: ([^•]+)/)?.[1]?.trim()||"Avulso" : `#${sel.cliente_id}`)}</div>
                    </div>
                    <div style={{ padding:"10px 12px", borderRadius:10, background:C.card, border:`1px solid ${C.border}` }}>
                      <div style={{ fontSize:9, color:C.textMuted }}>Telefone</div>
                      <div style={{ fontSize:13, fontWeight:700, marginTop:2 }}>{sel.telefone_entrega || sel.observacao?.match(/Tel: ([^•]+)/)?.[1]?.trim() || "—"}</div>
                    </div>
                    <div style={{ padding:"10px 12px", borderRadius:10, background:C.card, border:`1px solid ${C.border}` }}>
                      <div style={{ fontSize:9, color:C.textMuted }}>Pagamento</div>
                      <div style={{ fontSize:13, fontWeight:700, marginTop:2 }}>{(sel.forma_pagamento||"—").toUpperCase()}</div>
                    </div>
                  </div>
                  <div style={{ padding:"10px 12px", borderRadius:10, background:C.card, border:`1px solid ${C.border}`, marginBottom:10 }}>
                    <span style={{ fontSize:9, color:C.textMuted, display:"block", marginBottom:3 }}>Endereço</span>
                    <strong style={{ fontSize:13 }}>📍 {sel.endereco_entrega || "—"}</strong>
                  </div>

                </div>
              </div>
            </div>
          ) : (
            /* ═══ DASHBOARD — quando nenhum pedido selecionado ═══ */
            <div style={{ flex:1, overflowY:"auto", padding:16 }}>
              {/* Cards de ação */}
              <div style={{ display:"flex", gap:10, marginBottom:18 }}>
                <div onClick={() => { markInteracting(); setShowAvulsa(true); setAvulsaStep(1); fetchLojaModo(); }}
                  style={{ flex:2, background:`linear-gradient(135deg,${C.green},${C.greenLight})`, borderRadius:14, padding:16, cursor:"pointer", transition:"all .2s" }}>
                  <div style={{ fontSize:28, marginBottom:4 }}>📋</div>
                  <div style={{ color:"#fff", fontWeight:700, fontSize:14 }}>Entrega avulsa</div>
                  <div style={{ color:"rgba(255,255,255,0.7)", fontSize:11, marginTop:2 }}>Solicitar entrega de cliente</div>
                </div>
                <div onClick={() => { markInteracting(); setShowVendas(true); }}
                  style={{ flex:1, background:`${C.blue}15`, borderRadius:14, padding:16, cursor:"pointer", border:`1px solid ${C.blue}40` }}>
                  <div style={{ fontSize:28, marginBottom:4 }}>📊</div>
                  <div style={{ color:C.blue, fontWeight:700, fontSize:14 }}>Vendas do dia</div>
                  <div style={{ color:C.textMuted, fontSize:11, marginTop:2 }}>{vendasDone.length} pedidos</div>
                </div>
              </div>

              {/* Stats grid */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:10, marginBottom:18 }}>
                <div style={{ background:C.card, borderRadius:12, padding:14, border:`1px solid ${C.border}` }}>
                  <div style={{ fontSize:11, color:C.textMuted, marginBottom:6 }}>Total vendido</div>
                  <div style={{ fontSize:22, fontWeight:800, color:C.greenLight }}>{fmt(totalVendas)}</div>
                </div>
                <div style={{ background:C.card, borderRadius:12, padding:14, border:`1px solid ${C.border}` }}>
                  <div style={{ fontSize:11, color:C.textMuted, marginBottom:6 }}>Pedidos mês</div>
                  <div style={{ fontSize:22, fontWeight:800, color:C.text }}>{orders.length}</div>
                </div>
                <div style={{ background:C.card, borderRadius:12, padding:14, border:`1px solid ${C.border}` }}>
                  <div style={{ fontSize:11, color:C.textMuted, marginBottom:6 }}>Ticket médio</div>
                  <div style={{ fontSize:22, fontWeight:800, color:C.blue }}>{vendasDone.length > 0 ? fmt(totalVendas/vendasDone.length) : "—"}</div>
                </div>
                <div style={{ background:C.card, borderRadius:12, padding:14, border:`1px solid ${C.border}` }}>
                  <div style={{ fontSize:11, color:C.textMuted, marginBottom:6 }}>Horário</div>
                  <div style={{ fontSize:16, fontWeight:700, color:C.text }}>{loja?.horario_abre||"08:00"} - {loja?.horario_fecha||"23:00"}</div>
                </div>
              </div>

              {/* Breakdown pagamento */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:10, marginBottom:12 }}>
                {[{v:vendaPix,l:"PIX",c:C.green},{v:vendaDinheiro,l:"Dinheiro",c:C.yellow},{v:vendaCartao,l:"Cartão",c:C.red},{v:vendaCash,l:"Cashback Usado",c:"#8B5CF6"}].map((x,i) => (
                  <div key={i} style={{ background:C.card, borderRadius:10, padding:12, border:`1px solid ${C.border}`, textAlign:"center" }}>
                    <div style={{ fontSize:16, fontWeight:800, color:x.c }}>{fmt(x.v)}</div>
                    <div style={{ fontSize:10, color:C.textMuted, marginTop:4 }}>{x.l}</div>
                  </div>
                ))}
              </div>

              {/* Cashback a Receber */}
              {(vendaCash > 0 || cashbackGerado > 0) && (
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:18, padding:14, background:"#8B5CF608", borderRadius:14, border:"1px solid #8B5CF620" }}>
                  <div style={{ textAlign:"center" }}>
                    <div style={{ fontSize:10, color:"#8B5CF6", fontWeight:700 }}>Cashback Recebido</div>
                    <div style={{ fontSize:20, fontWeight:800, color:"#8B5CF6" }}>{fmt(vendaCash)}</div>
                  </div>
                  <div style={{ textAlign:"center" }}>
                    <div style={{ fontSize:10, color:C.orange, fontWeight:700 }}>Cashback a Pagar</div>
                    <div style={{ fontSize:20, fontWeight:800, color:C.orange }}>{fmt(cashbackGerado)}</div>
                  </div>
                </div>
              )}

              {/* Últimos entregues */}
              <div style={{ fontSize:11, fontWeight:800, color:C.textMuted, letterSpacing:"0.08em", marginBottom:8, textTransform:"uppercase" }}>Últimos entregues</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6 }}>
                {orders.filter(o => o.status === "entregue").slice(0, 6).map(o => (
                  <div key={o.id} onClick={() => { markInteracting(); setSelected(o); }}
                    style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 12px", borderRadius:10,
                      background:C.card, border:`1px solid ${C.border}`, cursor:"pointer", fontSize:12 }}>
                    <div>
                      <span style={{ color:C.textMuted }}>#{o.codigo}</span>
                      <div style={{ fontSize:10, color:C.textMuted, marginTop:2 }}>{o.cliente_nome || `#${o.cliente_id}`}</div>
                    </div>
                    <span style={{ fontWeight:700, color:C.greenLight }}>{fmt(valorReal(o))}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ═══ MODALS CHAT ═══ */}
      {showChatEnt && (
        <div onClick={fecharChatEnt} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.55)", zIndex:2000, display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
          <div onClick={e=>e.stopPropagation()} style={{ width:"100%", maxWidth:500, height:"80vh", background:C.card, borderTopLeftRadius:24, borderTopRightRadius:24, display:"flex", flexDirection:"column", overflow:"hidden" }}>
            <div style={{ background:"#1a1a2e", padding:"14px 18px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div><div style={{ color:"#fff", fontWeight:800, fontSize:16 }}>🛵 Pedido #{chatEntPedido?.codigo||chatEntPedido?.id}</div><div style={{ color:"rgba(255,255,255,0.7)", fontSize:12 }}>Entregador #{chatEntPedido?.entregador_id}</div></div>
              <button onClick={fecharChatEnt} style={{ background:"rgba(255,255,255,0.15)", border:"none", color:"#fff", borderRadius:20, width:36, height:36, cursor:"pointer", fontSize:18, fontWeight:700 }}>✕</button>
            </div>
            <div ref={chatEntScrollRef} style={{ flex:1, overflowY:"auto", padding:"12px 16px", background:"#ECE5DD", display:"flex", flexDirection:"column", gap:6 }}>
              {chatEntLoading ? <div style={{ textAlign:"center", marginTop:40, color:"#666" }}>Carregando...</div>
              : chatEntMsgs.length===0 ? <div style={{ textAlign:"center", marginTop:60, color:"#999" }}><div style={{ fontSize:36, marginBottom:8 }}>🛵</div><div>Nenhuma mensagem</div></div>
              : chatEntMsgs.map((m,i) => { const eu = m.remetente_tipo==="loja"; return (
                <div key={m.id||i} style={{ display:"flex", justifyContent:eu?"flex-end":"flex-start" }}>
                  <div style={{ maxWidth:"78%", padding:"8px 12px", borderRadius:16, borderTopRightRadius:eu?4:16, borderTopLeftRadius:eu?16:4, background:eu?"#DCF8C6":"#fff", boxShadow:"0 1px 2px rgba(0,0,0,0.08)" }}>
                    {!eu && <div style={{ fontSize:11, fontWeight:700, color:"#1a1a2e", marginBottom:3 }}>{m.remetente_nome}</div>}
                    <div style={{ fontSize:14, color:"#111", lineHeight:1.4 }}>{m.mensagem}</div>
                    <div style={{ fontSize:10, color:"#999", textAlign:"right", marginTop:3 }}>{fmtLocalTime(m.criado_em)}{eu&&<span style={{ marginLeft:4, color:m.lido?"#34B7F1":"#aaa" }}>{m.lido?"✓✓":"✓"}</span>}</div>
                  </div>
                </div>); })}
            </div>
            <div style={{ display:"flex", gap:10, padding:"12px 14px", background:C.card, borderTop:`1px solid ${C.border}`, alignItems:"flex-end" }}>
              <input value={chatEntTexto} onChange={e=>setChatEntTexto(e.target.value)} onKeyDown={e=>e.key==="Enter"&&!e.shiftKey&&(e.preventDefault(),enviarMsgChatEnt())} placeholder="Mensagem..." style={{ flex:1, padding:"10px 14px", borderRadius:24, border:`1px solid ${C.border}`, fontSize:14, outline:"none", background:C.bg, color:C.text }} />
              <button onClick={enviarMsgChatEnt} disabled={!chatEntTexto.trim()} style={{ width:44, height:44, borderRadius:22, border:"none", background:chatEntTexto.trim()?"#1a1a2e":C.border, color:"#fff", fontSize:18, cursor:"pointer", flexShrink:0 }}>➤</button>
            </div>
          </div>
        </div>
      )}

      {showChat && (
        <div onClick={fecharChat} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.55)", zIndex:2000, display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
          <div onClick={e=>e.stopPropagation()} style={{ width:"100%", maxWidth:500, height:"80vh", background:C.card, borderTopLeftRadius:24, borderTopRightRadius:24, display:"flex", flexDirection:"column", overflow:"hidden" }}>
            <div style={{ background:"#075E54", padding:"14px 18px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div><div style={{ color:"#fff", fontWeight:800, fontSize:16 }}>Chat — Pedido #{chatPedido?.codigo||chatPedido?.id}</div><div style={{ color:"rgba(255,255,255,0.7)", fontSize:12 }}>Cliente #{chatPedido?.cliente_id}</div></div>
              <button onClick={fecharChat} style={{ background:"rgba(255,255,255,0.15)", border:"none", color:"#fff", borderRadius:20, width:36, height:36, cursor:"pointer", fontSize:18, fontWeight:700 }}>✕</button>
            </div>
            <div ref={chatScrollRef} style={{ flex:1, overflowY:"auto", padding:"12px 16px", background:"#ECE5DD", display:"flex", flexDirection:"column", gap:6 }}>
              {chatLoading ? <div style={{ textAlign:"center", marginTop:40, color:"#666" }}>Carregando...</div>
              : chatMsgs.length===0 ? <div style={{ textAlign:"center", marginTop:60, color:"#999" }}><div style={{ fontSize:36, marginBottom:8 }}>💬</div><div>Nenhuma mensagem</div></div>
              : chatMsgs.map((m,i) => { const eu = m.remetente_tipo==="loja"; return (
                <div key={m.id||i} style={{ display:"flex", justifyContent:eu?"flex-end":"flex-start" }}>
                  <div style={{ maxWidth:"78%", padding:"8px 12px", borderRadius:16, borderTopRightRadius:eu?4:16, borderTopLeftRadius:eu?16:4, background:eu?"#DCF8C6":"#fff", boxShadow:"0 1px 2px rgba(0,0,0,0.08)" }}>
                    {!eu && <div style={{ fontSize:11, fontWeight:700, color:"#075E54", marginBottom:3 }}>{m.remetente_nome}</div>}
                    <div style={{ fontSize:14, color:"#111", lineHeight:1.4 }}>{m.mensagem}</div>
                    <div style={{ fontSize:10, color:"#999", textAlign:"right", marginTop:3 }}>{fmtLocalTime(m.criado_em)}{eu&&<span style={{ marginLeft:4, color:m.lido?"#34B7F1":"#aaa" }}>{m.lido?"✓✓":"✓"}</span>}</div>
                  </div>
                </div>); })}
            </div>
            <div style={{ display:"flex", gap:10, padding:"12px 14px", background:C.card, borderTop:`1px solid ${C.border}`, alignItems:"flex-end" }}>
              <input value={chatTexto} onChange={e=>setChatTexto(e.target.value)} onKeyDown={e=>e.key==="Enter"&&!e.shiftKey&&(e.preventDefault(),enviarMsgChat())} placeholder="Mensagem..." style={{ flex:1, padding:"10px 14px", borderRadius:24, border:`1px solid ${C.border}`, fontSize:14, outline:"none", background:C.bg, color:C.text }} />
              <button onClick={enviarMsgChat} disabled={!chatTexto.trim()} style={{ width:44, height:44, borderRadius:22, border:"none", background:chatTexto.trim()?"#075E54":C.border, color:"#fff", fontSize:18, cursor:"pointer", flexShrink:0 }}>➤</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
